import axios from 'axios';
import { validateDeliveryTypePayload } from '../utils/deliveryHelpers';
import { isPublicPath } from '../constants/routes';
import {
  bootstrapApp,
  getActiveFacilityId,
  getUserContext,
  extractFacilityIdFromUrl
} from './appBootstrap';
import { getOrCreateClientId } from '../utils/clientId';
import { apiDay } from '../utils/dateUtils';

// ============================================================================
// API Client Configuration
// ============================================================================

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

console.log('[🚀 API MODULE LOAD] services/api.js loading at:', new Date().toISOString());

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // 15s — prevents indefinite hang on mobile networks
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
  headers: {
    'Content-Type': 'application/json',
  },
});

console.log('[API Client] Initialized with baseURL:', apiClient.defaults.baseURL);
console.log('[API Client] Interceptors will be registered AFTER module initialization');

export { apiClient };

// ============================================================================
// Punch #41 — timeout retry for heavyweight facility endpoints
// ============================================================================
// The species-by-facility (galleries), animals-by-facility, and
// products_with_variations endpoints intermittently exceed the 15s default
// axios timeout on cold cache, which surfaced as console errors plus
// SILENTLY EMPTY grids. Rather than raising the global timeout, retry ONCE
// after 500ms — but only when the failure is specifically a client-side
// timeout (ECONNABORTED / "timeout" message). Real 4xx/5xx errors are
// rethrown immediately. Applied to the three known-heavy call sites only.
const isTimeoutError = (error) =>
  error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '');

const withTimeoutRetry = async (fn) => {
  try {
    return await fn();
  } catch (error) {
    if (!isTimeoutError(error)) throw error;
    // console.warn survives the production terser strip (console.log does not)
    console.warn('[API] Punch #41: request timed out — retrying once after 500ms');
    await new Promise((resolve) => setTimeout(resolve, 500));
    return fn();
  }
};

// PERF 2026-09-07 — per-call timeout for the two facility writes that
// legitimately outlive the 15s global: setting a species cover and
// deleting a species. Both were fixed server-side (the reconcile sweep
// moved to Celery; the delete cascade's signal fan-out suppressed), and
// this is belt-and-braces on top of that, NOT the fix. The global stays
// at 15s — raising it would hide a hang on every other endpoint.
//
// Both calls are idempotent, which is what makes `withTimeoutRetry`
// safe here: re-setting the same cover converges on the same MediaLink
// row, and DELETE on an already-deleted species now returns 204 (see
// facility/views.py delete_species_detail) instead of the 404 that made
// a timed-out-but-committed delete look like two failures.
const HEAVY_WRITE_TIMEOUT_MS = 45000;

// ============================================================================
// JWT Token Utilities
// ============================================================================

const decodeJWT = (token) => {
  if (!token || typeof token !== 'string') return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch (e) {
    console.warn('[Auth] Failed to decode JWT:', e.message);
    return null;
  }
};

export const getAccessTokenExpiry = () => {
  const token = localStorage.getItem('authToken');
  if (!token) return null;
  const payload = decodeJWT(token);
  return payload?.exp || null;
};

export const isAccessTokenExpiringSoon = (bufferSeconds = 60) => {
  const exp = getAccessTokenExpiry();
  if (!exp) return true;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return (exp - nowSeconds) < bufferSeconds;
};

export const getAccessToken = () => localStorage.getItem('authToken');
export const getRefreshToken = () => localStorage.getItem('refreshToken');

export const setTokens = ({ access, refresh }) => {
  if (access) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${access}`;
    localStorage.setItem('authToken', access);
  }
  if (refresh) {
    localStorage.setItem('refreshToken', refresh);
  }
};

export const logoutAndClear = () => {
  delete apiClient.defaults.headers.common['Authorization'];
  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('facilityUserRoles');
  localStorage.removeItem('isEmailVerified');
};

export const setAuthToken = (token) => {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('authToken', token);
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
    localStorage.removeItem('authToken');
  }
};

// ============================================================================
// Request Interceptor: Bootstrap, Auth, Facility ID, CSRF
// ============================================================================

apiClient.interceptors.request.use(
  async (config) => {
    config.headers ||= {};
    const url = config.url || '';

    // Skip bootstrap for flagged requests (e.g., /csrf/, /me/context/)
    if (config._skipBootstrap) {
      return config;
    }

    // Detect facility-scoped requests (support both relative and absolute URLs)
    const needsFacilityHeader =
      url.includes('/api/mediahub/') ||
      url.includes('mediahub/assets/') ||
      url.includes('mediahub/tags/') ||
      url.includes('/facility/');

    // Bootstrap app for any facility-scoped or mediahub request
    if (needsFacilityHeader) {
      await bootstrapApp();

      // Extract facility ID from URL ONLY (no fallback to cached state)
      const m = window.location.pathname.match(/\/my-store\/([^/]+)/);
      let fac = m?.[1];

      // Prod 2026-09-30: an address carrying a facility id that is NOT one
      // of the user's (a stale bookmark / saved value — 71423435… names
      // nothing on prod) was stamped on every request, and the server
      // answered "no role" to the Owner on links, publish, everything.
      // When the user's facilities are known and the address names none of
      // them, stamp the bootstrap's resolved facility (it is validated
      // against the same list) — never a facility the user does not hold.
      const known = getUserContext()?.facilities;
      if (fac && Array.isArray(known) && known.length > 0
          && !known.some((f) => String(f.id) === String(fac))) {
        const active = getActiveFacilityId();
        fac = known.some((f) => String(f.id) === String(active)) ? active : known[0].id;
      }

      if (fac && /^[0-9a-f-]{36}$/i.test(fac)) {
        config.headers['X-Facility-Id'] = fac;

        // Debug logging in development only
        if (process.env.NODE_ENV === 'development') {
          console.log('[X-Facility-Id] source=url, value=', fac);
        }
      } else {
        delete config.headers['X-Facility-Id'];

        if (process.env.NODE_ENV === 'development') {
          console.warn('[X-Facility-Id] No valid facility ID in URL path, header not set');
        }
      }
    }

    // Ensure Authorization header from localStorage for write methods
    const isWrite = config.method && ['post', 'put', 'patch', 'delete'].includes(config.method.toLowerCase());
    if (isWrite) {
      const authToken = localStorage.getItem('authToken');
      console.log('[API INTERCEPTOR] Write request - authToken exists:', !!authToken);
      if (authToken) {
        config.headers['Authorization'] = `Bearer ${authToken}`;
      }
    }

    // Always set X-Requested-With
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    // Add X-Client-UUID for telemetry tracking (anonymous + authenticated)
    try {
      const clientId = getOrCreateClientId();
      if (clientId) {
        config.headers['X-Client-UUID'] = clientId;
      }
    } catch (e) {
      // Silent fail - localStorage may not be available
    }

    // TEMPORARY DEBUG: Log all headers AFTER setting them
    if (url.includes('/facility/') && isWrite) {
      console.log('[WRITE URL]', config.url);
      console.log('[🔍 API DEBUG] Final headers before send:', {
        url: config.url,
        method: config.method,
        'Authorization': config.headers['Authorization'] ? `✅ Bearer ${config.headers['Authorization'].substring(0, 20)}...` : '❌ MISSING',
        'X-Facility-Id': config.headers['X-Facility-Id'] || '❌ MISSING',
        'X-CSRFToken': config.headers['X-CSRFToken'] || config.headers['X-CSRF-Token'] || '❌ MISSING (will be auto-attached)',
        'X-Requested-With': config.headers['X-Requested-With'] || '❌ MISSING',
        withCredentials: config.withCredentials,
        allHeaders: config.headers
      });
    }

    // Proactive JWT refresh if token expiring within 60s
    if (isAccessTokenExpiringSoon(60) && !config.url?.includes('auth/token/refresh')) {
      if (!refreshInProgress) {
        refreshInProgress = true;
        try {
          const newToken = await attemptTokenRefresh();
          refreshInProgress = false;
          onRefreshComplete(newToken);
          config.headers['Authorization'] = `Bearer ${newToken}`;
        } catch (error) {
          refreshInProgress = false;
          onRefreshComplete(null);
        }
      } else {
        const newToken = await new Promise((resolve) => subscribeToRefresh(resolve));
        if (newToken) {
          config.headers['Authorization'] = `Bearer ${newToken}`;
        }
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================================================
// Response Interceptor: 401 Retry with Token Refresh, 403 CSRF Retry
// ============================================================================

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // Handle 401 with token refresh and retry once
    if (status === 401 && !originalRequest._retryAttempted) {
      const isTokenInvalid =
        error.response?.data?.code === 'token_not_valid' ||
        error.response?.data?.detail?.includes('Token is expired') ||
        error.response?.data?.message?.includes('Token is expired');

      if (isTokenInvalid) {
        originalRequest._retryAttempted = true;

        if (refreshInProgress) {
          const newToken = await new Promise((resolve) => subscribeToRefresh(resolve));
          if (newToken) {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return apiClient.request(originalRequest);
          }
        }

        refreshInProgress = true;
        try {
          const newToken = await attemptTokenRefresh();
          refreshInProgress = false;
          onRefreshComplete(newToken);
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return apiClient.request(originalRequest);
        } catch (refreshError) {
          refreshInProgress = false;
          onRefreshComplete(null);
          console.error('[Auth] Token refresh failed, logging out');
          logoutAndClear();
          const currentPath = window.location.pathname;
          if (!isPublicPath(currentPath)) {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        }
      }
    }

    // Handle 403 CSRF error with re-bootstrap and retry once
    if (status === 403 && !originalRequest._csrfRetryAttempted) {
      const isCsrfError =
        error.response?.data?.detail?.toLowerCase().includes('csrf') ||
        error.response?.data?.message?.toLowerCase().includes('csrf');

      if (isCsrfError) {
        console.warn('[API] 403 CSRF error, re-bootstrapping...');
        originalRequest._csrfRetryAttempted = true;
        await bootstrapApp(true); // Force re-bootstrap
        return apiClient.request(originalRequest);
      }
    }

    // Handle 429 Too Many Requests — log and do NOT retry (prevents self-DDoS)
    if (status === 429) {
      const retryAfter = error.response?.headers?.['retry-after'];
      console.warn(`[API] 429 Rate Limited on ${originalRequest.url}` +
        (retryAfter ? ` (retry-after: ${retryAfter}s)` : ''));
      // Do not retry — let callers handle gracefully
      return Promise.reject(error);
    }

    // Handle CORS / network errors (status is undefined when browser blocks the response)
    if (!error.response && error.message?.includes('Network Error')) {
      console.warn('[API] Network/CORS error — API may be rate-limited at edge');
      // Do not retry — prevents self-DDoS when edge rate limiter strips CORS headers
      return Promise.reject(error);
    }

    // Final logout for repeated 401s
    if (status === 401 && originalRequest._retryAttempted) {
      console.warn('[Auth] 401 after refresh retry, logging out');
      logoutAndClear();
      const currentPath = window.location.pathname;
      if (!isPublicPath(currentPath)) {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

console.log('[✅ API INTERCEPTORS] Request and response interceptors registered successfully');
console.log('[✅ API INTERCEPTORS] Request interceptor count:', apiClient.interceptors.request.handlers.length);
console.log('[✅ API INTERCEPTORS] Response interceptor count:', apiClient.interceptors.response.handlers.length);

// ============================================================================
// Brand Template ID Management
// ============================================================================

let currentTemplateId = process.env.REACT_APP_BRAND_TEMPLATE_ID || 'feeling_froggy';

// Function to update template ID (called from BrandingContext)
export const updateApiTemplateId = (templateId) => {
  currentTemplateId = templateId;
};

// ============================================================================
// JWT Refresh State Management
// ============================================================================

let refreshInProgress = false;
let refreshSubscribers = [];

const subscribeToRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

const onRefreshComplete = (newToken) => {
  refreshSubscribers.forEach(callback => callback(newToken));
  refreshSubscribers = [];
};

const attemptTokenRefresh = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    console.info('[Auth] Refreshing access token...');
    const response = await apiClient.post('auth/token/refresh/', {
      refresh: refreshToken,
    });

    if (response.data.access) {
      const { access, refresh: newRefresh } = response.data;
      setTokens({ access, refresh: newRefresh || refreshToken });
      console.info('[Auth] Access token refreshed successfully');
      return access;
    }
    throw new Error('No access token in refresh response');
  } catch (error) {
    console.error('[Auth] Token refresh failed:', error.response?.status, error.response?.data);
    throw error;
  }
};

export const login = async (email, password) => {
    console.log("REACT_APP_API_URL:", process.env.REACT_APP_API_URL);
    console.log("API Login Payload:", { email, password });
    const response = await apiClient.post("auth/login/", { email, password });
    return response.data;
};

export const signup = async (userData) => {
  const response = await apiClient.post('auth/registration/', userData);
  return response.data;
};

export const logout = async () => {
  await apiClient.post('auth/logout/');
  setAuthToken(null);
};


// export const googleLogin = async (code, redirectUri) => {
//   const response = await apiClient.post('/auth/social/google/', {
//     code,
//     provider: 'google',
//     redirect_uri: redirectUri,
//   });
//   if (response.data.token) setAuthToken(response.data.token);
//   return response.data;
// };
export const googleLogin = async (code, redirectUri) => {
  const response = await apiClient.post('auth/social/google/', {
      code,
      provider: 'google',
      redirect_uri: redirectUri, // ✅ REQUIRED for auth-code flow
  });
  return response.data;
};

// export const googleLogin = async (accessToken) => {
//   const response = await apiClient.post('auth/social/google/', { access_token: accessToken });
//   if (response.data.token) setAuthToken(response.data.token);
//   return response.data;
// };

// PASSWORD RESET API
export const requestPasswordReset = async (email) => {
  const response = await apiClient.post('auth/password/reset/', { email });
  return response.data;
};

export const resetPassword = async (uid, token, newPassword1, newPassword2) => {
  const response = await apiClient.post('auth/password/reset/confirm/', {
    uid,
    token,
    new_password1: newPassword1,
    new_password2: newPassword2,
  });
  return response.data;
};

// RESEND EMAIL VERIFICATION
export const resendEmailVerification = async (email) => {
  // New endpoint uses authenticated user, doesn't need email parameter
  const response = await apiClient.post('auth/resend-email/', {});
  return response.data;
};

// GET CURRENT USER PROFILE (uses dj-rest-auth user endpoint)
export const getCurrentUser = async () => {
  const response = await apiClient.get('auth/user/');
  return response.data;
};


export const apiRefreshToken = async (refreshToken) => {
    const response = await axios.post('auth/refresh/', { refresh: refreshToken });
    return response.data;
};

// Refresh the JWT token
export const refreshAuthToken = async () => {
    const refreshToken = localStorage.getItem('refreshToken'); // Ensure refresh token is stored
    if (!refreshToken) {
        console.log('err: Refresh token not available')
      throw new Error('Refresh token not available');
    }
  
    const response = await apiClient.post('auth/token/refresh/', {
      refresh: refreshToken,
    });
  
    if (response.data.access) {
      setAuthToken(response.data.access); // Update the access token
      localStorage.setItem('refreshToken', response.data.refresh); // Optionally update the refresh token
    }
  
    return response.data;
};

// Get all animal types
export const getAnimalTypes = async () => {
    try {
      const response = await apiClient.get('facility/animal-types/');
      return response.data; // [{ id: 0, name: 'FROG' }, { id: 1, name: 'PLANT' }, ...]
    } catch (error) {
      console.error('Error fetching animal types:', error);
      throw error;
    }
};

// PRODUCT API
/**
 * Distinct genus names per species type, for the navbar dropdowns.
 *
 * I-03 S3-01.7 PERF — the navbar used to build the Frogs and Geckos
 * menus with TWO getProducts() calls (speciesType 0 and 3, isForSale
 * true). Those are the two /store/products/ requests that fired on
 * EVERY page in the app, the Cuttings page included, each returning a
 * fully serialized catalog so the menu could read one string per row.
 * `store/genera/` answers the same question in one cheap query.
 *
 * Returns { "0": [...], "3": [...] } — every requested type is present.
 */
export const getGenera = async (speciesTypes = [0, 3]) => {
    try {
        const response = await apiClient.get('store/genera/', {
            params: { speciesTypes: speciesTypes.join(',') },
        });
        return response.data?.genera || {};
    } catch (error) {
        console.warn('getGenera failed:', error?.message || error);
        throw error;
    }
};

export const getProducts = async (filters = {}) => {
    try {
        console.log('getProducts called with filters:', filters); // Log the input filters

        const params = {};
        // speciesType 0 is Frog — a truthiness check silently dropped it,
        // so /frogs was actually requesting ALL for-sale products. Harmless
        // while the catalog is frog-only; wrong the day the first gecko or
        // plant product goes live.
        if (filters?.speciesType !== undefined && filters?.speciesType !== null) {
            params.speciesType = filters.speciesType;
            console.log('Filter applied - speciesType:', params.speciesType); // Log the speciesType filter
        }
        if (filters?.facility) {
            params.facility = filters.facility;
            console.log('Filter applied - facility:', params.facility); // Log the facility filter
        }
        if (filters?.isForSale !== undefined) {
            params.isForSale = filters.isForSale;
            console.log('Filter applied - isForSale:', params.isForSale); // Log the isForSale filter
        }
        // Homepage "Currently Available" rail (Gap 4): opt-in server-side
        // ordering (marketing-featured animals first, then newest arrivals)
        // and a row cap so the homepage doesn't pull the full catalog.
        if (filters?.featuredFirst) {
            params.featuredFirst = true;
        }
        if (filters?.limit) {
            params.limit = filters.limit;
        }

        console.log('Sending GET request to /store/products/ with params:', params); // Log the request details

        // Storefront product endpoint intermittently exceeds the 15s
        // default axios timeout (observed 2026-07-02 — 2.4s in one
        // call, 30s+ in another). When it times out, callers on the
        // customer-facing pages (FrogsPage / GeckosPage / PlantsPage /
        // FrogGenusPage) end up showing "No frogs available" which is
        // indistinguishable from actually being sold out. Bump this
        // specific call to 30s so cold-start or intermittent slowness
        // doesn't drive customers off. The pages ALSO now distinguish
        // error state from empty state so a real timeout shows a
        // "having trouble" message instead of "sold out".
        const response = await apiClient.get('store/products/', {
            params,
            timeout: 30000,
        });

        console.log('Response received from /store/products/:', response.data); // Log the response data

        return response.data;
    } catch (error) {
        // Downgraded from console.error to console.warn on 2026-07-15 during
        // S12 triage — this endpoint is known-intermittent (30s timeout under
        // cold-start) and callers already handle the error via loadError state.
        // console.error was flooding telemetry on every failed load.
        console.warn('getProducts failed:', error?.message || error);
        throw error;
    }
};

// ✅ Get homepage data (store config, featured promotions, SEO intro)
export const getHomepageData = async () => {
  try {
    const response = await apiClient.get('store/homepage/');
    return response.data;
  } catch (error) {
    console.error('Error fetching homepage data:', error);
    throw error;
  }
};

// ✅ Get active public promotions (the promo landing pages). Same
// payload shape the homepage featured cards consume: id/name/slug/
// subtitle/short_description/hero_image_url.
export const getActivePromos = async () => {
  try {
    const response = await apiClient.get('store/promos/');
    return response.data;
  } catch (error) {
    console.error('Error fetching promotions:', error);
    throw error;
  }
};

// ✅ Get promo page data by slug
export const getPromoBySlug = async (slug) => {
  try {
    const response = await apiClient.get(`store/promos/${slug}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching promo data:', error);
    throw error;
  }
};

// ✅ New detailed product fetch
export const getProductById = async (productId) => {
  try {
    const response = await apiClient.get(`store/products/${productId}/`);
    return response.data;
  } catch (error) {
    throw new Error('Failed to fetch product');
  }
};

// GET products by genus (case-insensitive)
export const getProductsByGenus = async (genus, speciesType = null) => {
  try {
    const params = speciesType !== null ? { speciesType } : {};
    const response = await apiClient.get(`store/products/by_genus/${genus}/`, { params });
    return response.data;
  } catch (error) {
    throw new Error('Failed to fetch products by genus');
  }
};


// Restock Notifications. Pass a variationId for a variation-level
// subscription; omit it for PRODUCT-LEVEL (fires when ANY variation
// relists — used by the sold-out PDP empty state). Axios/JSON drop the
// undefined key, so the request is clean either way.
export const checkRestockNotifyAll = async (productId) => {
  const response = await apiClient.get(`store/products/${productId}/restock-notify/`);
  return response.data; // { subscriptions: { variationId: true, ... }, product_level: bool }
};

export const subscribeRestockNotify = async (productId, variationId) => {
  const response = await apiClient.post(`store/products/${productId}/restock-notify/`, { variation_id: variationId });
  return response.data;
};

export const unsubscribeRestockNotify = async (productId, variationId) => {
  await apiClient.delete(`store/products/${productId}/restock-notify/`, { params: { variation_id: variationId } });
};

// I-02b.2 — flip a VARIATION between 'stock' and 'individual' sales
// modes (moved off Product: mixed products sell juvenile stock next to
// individually-listed adults)
export const setVariationSalesMode = async (variationId, salesMode) => {
  const response = await apiClient.post(`store/variations/${variationId}/sales-mode/`, { sales_mode: salesMode });
  return response.data;
};

// I-02b.2 — management roster of a variation's linked animals (every
// linked animal + listing_status, incl. ones the storefront hides)
export const getVariationLinkedAnimals = async (variationId) => {
  const response = await apiClient.get(`store/variations/${variationId}/linked-animals/`);
  return response.data;
};

// I-02d Slice 4 — sellable lots (multi-animal units at one lot price).
// I-02d.3: lots ride ANY variation and list alongside its own cards.
// GET allows Staff; writes are Owner/Manager. Lot payloads carry derived
// state (is_purchasable, is_sold, not_purchasable_reasons) — never
// re-derive client-side.
export const getVariationLots = async (variationId) => {
  const response = await apiClient.get(`store/variations/${variationId}/lots/`);
  return response.data;
};

export const createVariationLot = async (variationId, { lotType, title, price, animalIds, quantity }) => {
  // I-02d.2 — pool groups (lotType 'pool_group', STOCK-mode variations)
  // send quantity and NO animal_ids (the backend 400s if both arrive);
  // pinned lots (pair/group) send animal_ids as before.
  const payload = {
    lot_type: lotType,
    title,
    price,
  };
  if (lotType === 'pool_group') {
    payload.quantity = quantity;
  } else {
    payload.animal_ids = animalIds;
  }
  const response = await apiClient.post(`store/variations/${variationId}/lots/`, payload);
  return response.data; // the created lot payload
};

// PATCH title/price/status ('retired'/'active'; re-activate can 400
// with per-member eligibility errors). Returns the fresh lot payload.
export const patchLot = async (lotId, patch) => {
  const response = await apiClient.patch(`store/lots/${lotId}/`, patch);
  return response.data;
};

// Member changes 409 while the lot has live reservations / cart lines.
export const addLotMember = async (lotId, animalId) => {
  const response = await apiClient.post(`store/lots/${lotId}/members/`, { animal_id: animalId });
  return response.data; // the fresh lot payload
};

export const removeLotMember = async (lotId, animalId) => {
  const response = await apiClient.delete(`store/lots/${lotId}/members/${animalId}/`);
  return response.data; // { removed, lot }
};

// Picker feed for lot building — same-species animals not deceased/
// sold/on-hold/holdback/breeder/in another active lot. I-02d.3: LISTED
// animals are offered too, each carrying `listed_in` (the listing names
// it leaves when the lot is created).
export const getLotEligibleAnimals = async (variationId) => {
  const response = await apiClient.get(`store/variations/${variationId}/lot-eligible-animals/`);
  return response.data;
};

// I-02d.3 — retired lot: put its members back on the shelf (re-links each
// to the lot's variation and recounts; the lot stays retired).
export const returnLotMembersToSale = async (lotId) => {
  const response = await apiClient.post(`store/lots/${lotId}/return-members/`);
  return response.data; // { relinked, skipped, lot }
};

export const archiveProduct = async (productId) => {
  const response = await apiClient.post(`store/products/${productId}/archive/`);
  return response.data;
};

export const unarchiveProduct = async (productId) => {
  const response = await apiClient.post(`store/products/${productId}/unarchive/`);
  return response.data;
};

export const getProductsByFacility = async (facilityId, filters = {}) => {
  const response = await apiClient.get(`facility/facilities/${facilityId}/products/`, { params: filters });
  return response.data;
};

// CART API
export const getCart = async () => {
  const response = await apiClient.get('store/cart/');
  console.log('Get cart respone:' + response)
  return response.data;
};

export const addToCart = async (variationId, quantity, animalId = null, lotId = null) => {
  // I-02b: optional animalId pins the cart line to a specific animal
  // (individual-mode products; quantity is forced to 1 server-side).
  // I-02d: optional lotId pins the line to a sellable lot (lot-mode
  // products). Mutually exclusive with animalId — the backend 400s if
  // both are sent.
  const payload = { product_variation_id: variationId, quantity };
  if (animalId) payload.animal_id = animalId;
  if (lotId) payload.lot_id = lotId;
  const response = await apiClient.post('store/cart/add/', payload);
  return response.data;
};

export const updateCartItem = async (itemId, quantity) => {
  // Only send quantity, no facility_id
  const response = await apiClient.put(`store/cart/update/${itemId}/`, {
    quantity,
  });
  return response.data;
};

export const removeFromCart = async (itemId) => {
  const response = await apiClient.delete(`store/cart/remove/${itemId}/`);
  return response.status === 204;
};

export const clearCart = async () => {
  const response = await apiClient.delete('store/cart/clear/');
  return response.status === 204;
};

export const mergeCart = async (cartItems, promoCode = null) => {
  // CRITICAL FIX: Include promo_code in merge payload to persist through login
  const payload = {
    items: cartItems,
  };

  if (promoCode) {
    payload.promo_code = promoCode;
    console.log('[MERGE][FRONTEND] Including promo code in merge:', promoCode);
  }

  console.log('[MERGE][FRONTEND] Sending mergeCart payload:', payload);
  const response = await apiClient.post('store/cart/merge/', payload);
  console.log('[MERGE][FRONTEND] Response from mergeCart:', response.data);
  return response.data;
};

// Phase C: Apply promo code to cart
export const applyPromoCode = async (promoCode, items = null) => {
  console.log('[PROMO] Applying promo code:', promoCode, 'with items:', items);
  const payload = {
    promo_code: promoCode,
  };

  // For anonymous users, include items array for preview
  if (items) {
    payload.items = items;
  }

  const response = await apiClient.post('store/cart/apply-promo/', payload);
  console.log('[PROMO] Promo code response:', response.data);
  return response.data;
};

// Phase C: Remove promo code from cart (authenticated users only)
export const removePromoCode = async (promoCode = '') => {
  console.log('[PROMO] Removing promo code:', promoCode || 'ALL');
  const payload = promoCode ? { promo_code: promoCode } : {};
  const response = await apiClient.post('store/cart/remove-promo/', payload);
  console.log('[PROMO] Remove promo response:', response.data);
  return response.data;
};

// TELEMETRY & PREVIEW (Option A - stateless anon carts)
export const snapshotAnonCart = async (clientUuid, items) => {
  /**
   * Send cart snapshot for anonymous telemetry.
   * Non-blocking - don't throw errors to user.
   */
  try {
    const response = await apiClient.post('store/telemetry/cart-snapshot/', {
      client_uuid: clientUuid,
      items: items.map(item => ({
        variation_id: item.product_variation.id,
        quantity: item.quantity,
        price: item.product_variation.latest_price?.price || '0.00'
      }))
    });
    console.log('[TELEMETRY] Snapshot recorded:', response.data.snapshot_id);
    return response.data;
  } catch (error) {
    console.warn('[TELEMETRY] Failed to snapshot cart (non-blocking):', error);
    return null;
  }
};

// adv-review N7 (2026-08-07): `previewPromotions` lived here and was
// imported by nothing — one of the "three payload builders" #58 kept in
// sync was unreachable. The two live callers of store/promotions/preview/
// are in CartPage.js (guest subtotal + guest promo-code apply); they send
// lot_id and animal_id and are covered by the parity harness. Deleted
// rather than maintained, so there is no third shape to drift.

// ORDER API
export const placeOrder = async () => {
  const response = await apiClient.post('store/orders/place/');
  return response.data;
};

export const getOrders = async () => {
  const response = await apiClient.get('store/orders/');
  return response.data;
};

export const getOrderById = async (orderId) => {
  const response = await apiClient.get(`store/orders/${orderId}/`);
  return response.data;
};

// Get order with enriched data (same structure as create order)
export const getOrder = async (orderId) => {
  const response = await apiClient.get(`store/orders/${orderId}/`);
  return response.data;
};

// Fetch order (alias for getOrder for consistency)
export const fetchOrder = async (orderId) => {
  const response = await apiClient.get(`store/orders/${orderId}/`);
  return response.data;
};

// Fetch order confirmation (specialized endpoint for post-payment display)
export const fetchOrderConfirmation = async (orderId) => {
  const response = await apiClient.get(`store/orders/${orderId}/confirmation/`);
  return response.data;
};

// Create order with quote - takes cart data and address info to generate shipping quote
export const createOrderWithQuote = async (payload) => {
  try {
    console.log('[API] Creating order with quote:', payload);
    const response = await apiClient.post('store/get-or-create-order-with-quote/', payload);
    console.log('[API] Order with quote response:', response.data);
    return response.data;
  } catch (error) {
    console.error('[API] Error creating order with quote:', error.response?.data || error.message);
    throw error;
  }
};

// Save shipping quote to order before payment
export const saveShippingQuote = async (orderId, quoteData) => {
  try {
    console.log('[API] Saving shipping quote for order:', orderId, quoteData);
    
    // SANITIZED PAYLOAD - Remove forbidden shipping fields, keep only quote data
    const sanitizedPayload = { ...quoteData };
    
    // Remove forbidden fields that must NOT be sent by client (only delivery_type enum is authoritative)
    delete sanitizedPayload.delivery_type_code;
    delete sanitizedPayload.delivery_type_label;
    delete sanitizedPayload.delivery_type_display;
    delete sanitizedPayload.is_residential;
    // Keep hub_id if delivery_type is HUB - it's needed for hub delivery
    if (sanitizedPayload.delivery_type !== 'HUB') {
      delete sanitizedPayload.hub_id;
    }
    delete sanitizedPayload.hub_address;
    delete sanitizedPayload.hub_name;
    delete sanitizedPayload.shipping_method;
    delete sanitizedPayload.shipping_method_label;
    delete sanitizedPayload.box_size;
    delete sanitizedPayload.box_size_label;
    
    // Validate delivery type consistency
    const validatedPayload = validateDeliveryTypePayload(sanitizedPayload);
    
    // Log for debugging
    console.debug("[CONFIRM PAYLOAD] save-shipping-quote:", validatedPayload);
    
    const response = await apiClient.post(`store/orders/${orderId}/save-shipping-quote/`, validatedPayload);
    console.log('[API] Shipping quote saved:', response.data);
    return response.data;
  } catch (error) {
    console.error('[API] Error saving shipping quote:', error.response?.data || error.message);
    throw error;
  }
};

// Clear cart after payment (server-side, idempotent)
export const clearCartAfterPayment = async (orderId) => {
  try {
    console.log('[API] Clearing cart for order:', orderId);
    const response = await apiClient.post(`store/orders/${orderId}/clear-cart/`);
    console.log('[API] Cart clear result:', response.data);
    return response.data;
  } catch (error) {
    console.error('[API] Error clearing cart:', error.response?.data || error.message);
    throw error;
  }
};

// Validate shipping quote (validate-only, no auto-generation)
export const validateShippingQuote = async (orderId) => {
  try {
    console.log('[API] Validating shipping quote for order:', orderId);
    const response = await apiClient.post(`store/orders/${orderId}/validate-shipping-quote/`);
    console.log('[API] Quote validation result:', response.data);
    return response.data;
  } catch (error) {
    console.error('[API] Quote validation error:', error.response?.data || error.message);
    throw error;
  }
};


// ===========================
// SHIPPING API METHODS
// ===========================

// Facility Shipping Address APIs

export const getShippingQuote = async (orderId) => {
  console.log('Requesting shipping quote for orderId:', orderId);
  const response = await apiClient.post(`shipping/quote/${orderId}/`);
  console.log('Shipping quote response:', response.data);
  return response.data;
};

export const trackShipment = async (trackingNumber) => {
  console.log('Tracking shipment with tracking number:', trackingNumber);
  const response = await apiClient.post('shipping/track/', { tracking_number: trackingNumber });
  console.log('Track shipment response:', response.data);
  return response.data;
};

export const getFacilityShippingCalendarDayListCreate = async (facilityId) => {
  console.log('Fetching shipping calendar for facilityId:', facilityId);
  const response = await apiClient.get(`shipping/facility/${facilityId}/shipping-calendar/`);
  console.log('Shipping calendar data:', response.data);
  return response.data;
};

// Toggle a single shipping calendar day for a facility.
export const toggleShippingCalendarDay = async (facilityId, dateStr, isOpen) => {
  try {
    const payload = {
      facility: facilityId,
      date: dateStr,
      is_open: isOpen,
    };
    console.log('Toggling shipping calendar day with payload:', payload);
    const response = await apiClient.post(
      `/shipping/facility/${facilityId}/shipping-calendar/`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error toggling shipping calendar day:", error?.response?.data || error.message || error);
    throw error;
  }
};


export const updateFacilityShippingAddressDetail = async (facilityId, addressId, updatedData) => {
  try {
    console.log('Updating shipping address with Facility ID:', facilityId, 'Address ID:', addressId, 'Data:', updatedData);
    // If updatedData contains facility_id, convert to facility: { id: ... }
    let payload = { ...updatedData };
    if (payload.facility_id !== undefined) {
      payload.facility = { id: payload.facility_id };
      // Optionally, remove facility_id if not needed
      // delete payload.facility_id;
    }
    const response = await apiClient.put(`shipping/facility/${facilityId}/shipping-addresses/${addressId}/`, payload);
    console.log('Updated shipping address response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating shipping address:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Delete a facility shipping address by facility ID and address ID.
 * @param {string} facilityId - Facility UUID.
 * @param {string} addressId - Address UUID.
 * @returns {Promise<any>} Response data from the server.
 */
export const deleteFacilityShippingAddress = async (facilityId, addressId) => {
  const response = await apiClient.delete(`shipping/facility/${facilityId}/shipping-addresses/${addressId}/delete/`);
  return response.data;
};

// Activate a facility shipping address (sets is_active: true via separate endpoint)
export const activateFacilityShippingAddress = async (facilityId, addressId) => {
  try {
    const response = await apiClient.post(
      `shipping/facility/shipping-addresses/${addressId}/activate/`,
      { facility_id: facilityId }
    );
    return response.data;
  } catch (error) {
    console.error("Error activating shipping address:", error.response?.data || error.message);
    throw error;
  }
};

// Get list of facility shipping addresses or create a new one
export const getFacilityShippingAddressListCreate = async (facilityId) => {
  try {
    console.log('Fetching facility shipping addresses for facilityId:', facilityId);
    const response = await apiClient.get(`shipping/facility/${facilityId}/shipping-addresses/`);
    console.log('Facility shipping addresses:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching facility shipping addresses:', error.response?.data || error.message);
    throw error;
  }
};

// Create a new shipping address
export const createFacilityShippingAddress = async (facilityId, data) => {
  try {
    console.log('Creating shipping address for facilityId:', facilityId, 'Data:', data);
    const payload = {
      ...data,
      facility: facilityId,
    };
    const response = await apiClient.post(`shipping/facility/${facilityId}/shipping-addresses/`, payload);
    console.log('Created shipping address response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating shipping address:', error.response?.data || error.message);
    throw error;
  }
};

export const getFacilityShippingDiscounts = async (facilityId) => {
  const response = await apiClient.get(`shipping/facility/${facilityId}/shipping-discounts/`);
  return response.data;
};

export const createFacilityShippingDiscount = async (facilityId, payload) => {
  const response = await apiClient.post(`shipping/facility/${facilityId}/shipping-discounts/`, payload);
  return response.data;
};

// // ======================================================
// // Shipping Calendar API
// // ======================================================

// // Note: The deleteShippingCalendarBatch function uses a DELETE request to match the backend API,
// // even though batch deletes are sometimes handled via POST for complex payloads.
// /**
//  * Delete a batch of shipping calendar days for a facility.
//  * Uses DELETE to match backend API (not POST).
//  * @param {string} facilityId
//  * @param {Array} dateList
//  * @returns {Promise<any>}
//  */
// export async function deleteShippingCalendarBatch(facilityId, dateList) {
//   return apiClient.delete(`/shipping/facility/${facilityId}/shipping-calendar/batch/`, { data: dateList });
// }

/**
 * Get default shipping days for a facility.
 * @param {string} facilityId
 * @returns {Promise<any>}
 */
export async function getDefaultShippingDays(facilityId) {
  return apiClient.get(`shipping/facility/${facilityId}/default-shipping-days/`);
}

/**
 * Update default shipping days for a facility.
 * @param {string} facilityId
 * @param {Object} payload
 * @returns {Promise<any>}
 */
export async function updateDefaultShippingDays(facilityId, payload) {
  return apiClient.put(`shipping/facility/${facilityId}/default-shipping-days/`, payload);
}

// Get default shipping days for a facility (legacy)
export const getFacilityDefaultShippingDays = async (facilityId) => {
  const response = await apiClient.get(`shipping/facility/${facilityId}/default-shipping-days/`);
  return response.data;
};

// Update default shipping days for a facility (legacy)
export const updateFacilityDefaultShippingDays = async (facilityId, payload) => {
  const response = await apiClient.put(`shipping/facility/${facilityId}/default-shipping-days/`, {
    ...payload,
    facility: facilityId
  });
  return response.data;
};

// #37 storefront search — public grouped search (products / care guides /
// listed individual animals). Min 2 chars enforced server-side (400).
export const searchStorefront = async (q) => {
  const response = await apiClient.get('store/search/', { params: { q } });
  return response.data;
};

// Weather AI guidance (owner-editable directive for the Phase 2 weather
// judgment tier). Applies only to borderline (±2°F) calls and flip timing;
// hard temperature bounds are enforced in backend code regardless.
export const getWeatherAiGuidance = async (facilityId) => {
  const response = await apiClient.get(`shipping/facility/${facilityId}/weather-ai-guidance/`);
  return response.data;
};

export const updateWeatherAiGuidance = async (facilityId, directive) => {
  const response = await apiClient.patch(`shipping/facility/${facilityId}/weather-ai-guidance/`, {
    weather_judgment_directive: directive,
  });
  return response.data;
};

// Weather gate decisions ledger (read-only operator panel)
export const getWeatherDecisions = async (facilityId, { limit = 25, orderId = null } = {}) => {
  const params = { limit };
  if (orderId) params.order_id = orderId;
  const response = await apiClient.get(
    `shipping/facility/${facilityId}/weather-decisions/`, { params });
  return response.data;
};


// Toggle a single shipping calendar day for a facility.
export async function toggleFacilityShippingCalendarDay(facilityId, date) {
  const response = await apiClient.post(
    `shipping/facility/${facilityId}/shipping-calendar/`,
    {
      facility: facilityId,
      date,
    }
  );
  return response.data;
}

export const getFacilityShippingDiscountListCreate = async (facilityId) => {
  console.log('Fetching shipping discounts for facilityId:', facilityId);
  const response = await apiClient.get(`shipping/facility/${facilityId}/shipping-discounts/`);
  console.log('Shipping discounts data:', response.data);
  return response.data;
};

// Delete a shipping calendar day by its ID.
export const deleteFacilityShippingCalendarDay = async (calendarDayId) => {
  try {
    console.log('Deleting shipping calendar day with ID:', calendarDayId);
    const response = await apiClient.delete(`shipping/facility/shipping-calendar/${calendarDayId}/`);
    console.log('Delete shipping calendar day response status:', response.status);
    return response.status === 204;
  } catch (error) {
    console.error('Error deleting shipping calendar day:', error.response?.data || error.message);
    throw error;
  }
};

// Update a shipping calendar day (new UUID-based endpoint)
export const updateFacilityShippingCalendarDay = async (facilityId, calendarDayId, calendarDayData) => {
  try {
    console.log('Updating shipping calendar day for facilityId:', facilityId, 'Calendar Day ID:', calendarDayId, 'Data:', calendarDayData);
    const response = await apiClient.put(`shipping/facility/${facilityId}/shipping-calendar/${calendarDayId}/`, calendarDayData);
    console.log('Updated shipping calendar day response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating shipping calendar day:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get the shipping calendar range for a facility.
 * @param {string} facilityId
 * @param {string} start - Start date (YYYY-MM-DD)
 * @param {string} end - End date (YYYY-MM-DD)
 * @returns {Promise<any>}
 */
export async function getFacilityShippingCalendarRange(facilityId, start, end) {
  const response = await apiClient.get(`shipping/facility/${facilityId}/shipping-calendar-range/`, {
    params: { start, end }
  });
  return response.data;
}

/**
 * Batch delete shipping calendar days for a facility (POST).
 * @param {string} facilityId
 * @param {Object} payload
 * @returns {Promise<any>}
 */
export async function batchDeleteFacilityShippingCalendarDays(facilityId, payload) {
  return apiClient.post(`shipping/facility/${facilityId}/shipping-calendar/batch-delete/`, payload);
}

/**
 * Batch delete shipping calendar days for a facility (POST, API client export).
 * @param {string} facilityId
 * @param {Object} payload
 * @returns {Promise<any>}
 */
export const facilityShippingCalendarDayBatchDelete = (facilityId, payload) =>
  apiClient.post(`shipping/facility/${facilityId}/shipping-calendar/batch-delete/`, payload);
export const getFacilities = async () => {
    try {
        const response = await apiClient.get('facility/facilities/list/'); // Replace with the correct API endpoint
        return response.data;
    } catch (error) {
        console.error('Error fetching facilities:', error);
        throw error;
    }
};

export const getFacilityById = async (facilityId) => {
    try {
        const response = await apiClient.get(`facility/facilities/${facilityId}/`); // Confirm the endpoint
        console.log('Facility API Response:', response.data); // Debugging log
        return response.data;
    } catch (error) {
        console.error('Error in getFacilityById:', error);
        throw error;
    }
};

/**
 * Update facility frog room notes (for AI navigation context)
 * Manager+ only.
 * @param {string} facilityId - Facility UUID
 * @param {string} frogRoomNotes - The room layout description text
 * @returns {Promise<Object>} Updated facility data
 */
export const updateFacilityRoomNotes = async (facilityId, frogRoomNotes) => {
    try {
        const response = await apiClient.put(`facility/facilities/${facilityId}/edit/`, {
            frog_room_notes: frogRoomNotes
        });
        return response.data;
    } catch (error) {
        console.error('Error updating facility room notes:', error);
        throw error;
    }
};

export const getSpeciesByFacility = async (facilityId) => {
    try {
      // Punch #41: heavyweight endpoint (species + galleries) — retry once on timeout
      const response = await withTimeoutRetry(() => apiClient.get(`facility/species/${facilityId}/`));
      return response.data;
    } catch (error) {
      console.error("Error fetching species:", error);
      throw error;
    }
};

export const getSpeciesDetail = async (speciesId) => {
    try {
        const response = await apiClient.get(`facility/detail/${speciesId}/`);
        return response.data;
    } catch (error) {
        console.error("Error fetching species:", error);
        throw error;
    }
};

export const getAnimalsByFacility = async (facilityId, filters = {}) => {
    try {
        // Build query parameters from filters
        const params = new URLSearchParams();
        
        // Add each filter if it has a value
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                params.append(key, value);
            }
        });
        
        const queryString = params.toString();
        const url = `facility/facilities/${facilityId}/animals/${queryString ? `?${queryString}` : ''}`;
        
        console.log("Fetching animals with URL:", url);
        // Punch #41: heavyweight endpoint (full animals grid) — retry once on timeout
        const response = await withTimeoutRetry(() => apiClient.get(url));
        console.log("Raw API Response:", response);
        console.log("Animals fetched from API:", response.data);
        return response.data;
    } catch (error) {
        console.error("Error in getAnimalsByFacility:", error || error.message);
        throw error;
    }
};

export const getMorphsBySpecies = async (speciesId) => {
  try {
      const response = await apiClient.get(`facility/morphs/${speciesId}/`);
      console.log("Morphs fetched from API:", response.data); // Add log to view fetched data
      return response.data;
  } catch (error) {
      console.error("Error fetching morphs:", error);
      throw error;
  }
};


/**
 * Every genetic trait of ONE FACILITY.
 *
 * ⚠️ **IT SENDS THE FACILITY ID, AND ALWAYS DID HAVE TO** (Cowork run 22,
 * finding 7). `facility/genetic-traits/` has REQUIRED `?facility_id=` since
 * it was scoped — `views.get_genetic_traits` answers 400 "facility_id query
 * parameter is required for facility-scoped traits" without it — and this
 * wrapper took no argument and sent none, so EVERY call was a guaranteed
 * 400, logged twice (here and again in the caller's own catch). That is the
 * "Error fetching genetic traits: 400" Cowork read off the console while
 * the Add Animal and Manage Morphs modals were open.
 *
 * With no id it makes no request at all and answers `[]`: a call that
 * cannot be scoped is a caller bug, and a 400 the console prints twice is
 * the least useful way to say so.
 */
export const getGeneticTraits = async (facilityId) => {
  const scope = facilityId || extractFacilityIdFromUrl();
  if (!scope) {
      console.warn("getGeneticTraits called with no facility id — returning []");
      return [];
  }
  try {
      const response = await apiClient.get('facility/genetic-traits/', {
          params: { facility_id: scope },
      });
      const data = response?.data ?? [];
      return Array.isArray(data) ? data : [];
  } catch (error) {
      console.error("Error fetching genetic traits:", error);
      throw error;
  }
};

export const getTraitsBySpecies = async (speciesId) => {
  try {
      const response = await apiClient.get(`facility/traits/?species_id=${speciesId}`);
      const data = response?.data ?? {};
      // Backend shape is an object: { primary: [...], secondary: [...] }
      const primary = Array.isArray(data.primary) ? data.primary : [];
      const secondary = Array.isArray(data.secondary) ? data.secondary : [];
      return { primary, secondary };
  } catch (error) {
      console.error("Error fetching traits by species:", error);
      throw error;
  }
};

export const createTrait = async (traitData) => {
  try {
      const facilityId = extractFacilityIdFromUrl();
      if (!facilityId) throw new Error('No facility ID in route');
      const response = await apiClient.post(`facility/facilities/${facilityId}/traits/`, traitData);
      return response.data;
  } catch (error) {
      console.error("Error creating trait:", error);
      throw error;
  }
};

export const createAnimal = async (animalData) => {
  try {
      const facilityId = extractFacilityIdFromUrl();
      if (!facilityId) throw new Error('No facility ID in route');
      const response = await apiClient.post(`facility/facilities/${facilityId}/animals/`, animalData);
      return response.data;
  } catch (error) {
      console.error('Error creating animal:', error);
      throw error;
  }
};

export const updateAnimal = async (animalId, data) => {
  const facilityId = extractFacilityIdFromUrl();
  if (!facilityId) throw new Error('No facility ID in route');
  // PATCH (partial semantics) — grid quick-toggles send single-field
  // bodies; the endpoint treats PUT/PATCH identically but PATCH is
  // honest about intent. (Cowork I-02b sweep Bug 1.)
  const response = await apiClient.patch(`facility/facilities/${facilityId}/animals/${animalId}/`, data);
  return response.data;
};


export const getClutchesByFacility = async (facilityId, filterParams = {}) => {
  // Backend now uses /clutch/ for GET list (animal_groups is POST create only)
  const params = new URLSearchParams();

  // Add filter parameters
  Object.keys(filterParams).forEach(key => {
    if (filterParams[key] !== null && filterParams[key] !== undefined && filterParams[key] !== '') {
      params.append(key, filterParams[key]);
    }
  });

  const queryString = params.toString();
  const url = queryString
    ? `facility/facilities/${facilityId}/clutch/?${queryString}`
    : `facility/facilities/${facilityId}/clutch/`;

  return await apiClient.get(url);
};

export const createClutch = async (data) => {
  // Clutches are refered to as animal_groups in the back-end
  const facilityId = extractFacilityIdFromUrl();
  if (!facilityId) throw new Error('No facility ID in route');
  return await apiClient.post(`facility/facilities/${facilityId}/animal_groups/`, data);
};

export const splitClutch = async (animalGroupId, payload) => {
  try {
    const facilityId = extractFacilityIdFromUrl();
    if (!facilityId) throw new Error('No facility ID in route');
    console.log("Payload:", JSON.stringify(payload)); // Log the payload for debugging
    const response = await apiClient.post(
      `facility/facilities/${facilityId}/animal_groups/${animalGroupId}/split/`,
      payload
    );
    console.log("Split clutch response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error splitting clutch:", error);
    throw error;
  }
};

/**
 * ONE clutch's row as the Clutches grid lists it (the detail read — the same
 * `AnimalGroupSerializer` and context the list uses). The grid reads it
 * after a LOG that filled one of the clutch's dates (Fable design
 * 2026-10-28), so the row's derived stage and age follow the new date.
 */
export const getClutchDetail = async (facilityId, clutchId) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/animal_groups/${clutchId}/`);
  return response.data;
};

export const updateClutch = async (clutchId, updatedClutch) => {
  try {
    const facilityId = extractFacilityIdFromUrl();
    if (!facilityId) throw new Error('No facility ID in route');
    // PATCH (partial semantics) — I-03 R5 · S3-05.2. The Clutches grid's
    // row is the editor now, so every body is ONE key: a name, a count, a
    // date, a flag pair, a placement. `animal_group_detail_nested` routes
    // PUT and PATCH to the same `update_animal_group`, which has always
    // run `partial=True`, so nothing about the answer changes — but PUT
    // over a one-key body was a lie about intent, and the day the route
    // tells PUT and PATCH apart it becomes a data-loss bug. (The same
    // change `updateAnimal` made in the I-02b sweep, for the same reason.)
    const response = await apiClient.patch(`facility/facilities/${facilityId}/animal_groups/${clutchId}/`, updatedClutch);
    return response.data;
  } catch (error) {
    console.error('Error updating clutch:', error);
    throw error;
  }
};

// ── I-03 R4 · S3-04.2 — PAIRINGS AND THE TREE ────────────────────────
//
// ⚠️ EVERY ONE OF THESE IS PINNED AT THE WIRE, not at its own argument
// (`services/__tests__/lineageApiWire.test.js`). `runRoutine` shipped
// DROPPING a key between the caller and the network while its RTL suite —
// which asserted the object handed to a MOCKED wrapper — passed
// throughout; the bodies below carry `sire`, `dam`, `members` and an
// explicit `null`, and every one of those is a key whose loss answers
// 200 and writes the wrong thing.
//
// The facility id is taken EXPLICITLY rather than sniffed out of the URL:
// the Lineage tab is reachable from the Animals page and the Pairings
// page, and `deleteRack`'s precedent is the one to follow where a caller
// already holds the id.

/** `GET facilities/<fid>/pairings/` — paginated; `{open, species, page}`. */
export const getPairings = async (facilityId, params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value);
    }
  });
  const qs = query.toString();
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/pairings/${qs ? `?${qs}` : ''}`);
  return response.data;
};

/**
 * `GET facilities/<fid>/pairings/<id>/` — ONE pairing, whatever its status.
 *
 * What `?pairing=<id>` on the Pairings page reads. The list is filtered
 * (open first, and the default filter hides an ENDED pairing) AND
 * paginated, so a deep link from a clutch cannot be answered out of the
 * page the operator happens to be on — it asks for the row.
 */
export const getPairing = async (facilityId, pairingId) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/pairings/${pairingId}/`);
  return response.data;
};

/**
 * `POST facilities/<fid>/pairings/`.
 *
 * The body goes over the wire AS GIVEN — a `pair` names `sire` / `dam`,
 * a `group` names `members: [{animal, role, confidence}]`, and sending
 * both is the server's own 400. A wrapper that reshaped either would be
 * the `runRoutine` failure with a lineage row behind it.
 *
 * A parent below subadult, with no date to read a stage from, or unsexed
 * as sire/dam is ASKED about first (Q-young-unsexed-parent, 2026-10-29):
 * 409 `confirm_member_change` with `consequences: {sentence, warnings,
 * key}`, nothing written; the same body plus `consequences_seen: key`
 * writes it as chosen.
 */
export const createPairing = async (facilityId, body) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/pairings/`, body);
  return response.data;
};

/** `PATCH facilities/<fid>/pairings/<id>/` — one key at a time. */
export const updatePairing = async (facilityId, pairingId, body) => {
  const response = await apiClient.patch(
    `facility/facilities/${facilityId}/pairings/${pairingId}/`, body);
  return response.data;
};

/**
 * `PATCH facilities/<fid>/pairings/<id>/?dry_run=1` — what a member change
 * WOULD do, and nothing written (R5 B3.3, Q-pairing-members-are-correctable).
 *
 * The body is the write's own, AS GIVEN: `{member_change: {action: 'add',
 * animal, role, confidence?} | {action: 'remove', member} | {action:
 * 'replace', member, animal} | {action: 'set_role', member, role} |
 * {action: 'set_confidence', member, confidence}, members_seen}`. Answers
 * `{dry_run, action, summary, kind_before, kind_after, confirm,
 * consequences: {clutch_count, clutches, more, parents, links_before,
 * links_after, links_change, declared_clutch_count, split_hatchling_count,
 * sentence, key}}` — `confirm` is whether the keeper is asked, the
 * sentence is what the confirm holds word for word, and the `key` is what
 * the write sends back as `consequences_seen`. (The old `members/<mid>/`
 * door still exists server-side and is the same `set_confidence` change;
 * the page no longer calls it.)
 */
export const previewPairingMemberChange = async (facilityId, pairingId, body) => {
  const response = await apiClient.patch(
    `facility/facilities/${facilityId}/pairings/${pairingId}/?dry_run=1`, body);
  return response.data;
};

/**
 * `POST facilities/<fid>/pairings/<id>/members/undo/` `{token}` — undo ONE
 * card member change (R5 B3.3). Answers the pairing row plus
 * `undo: {readback, note}`; 409 `changed_since` names what moved.
 */
export const undoPairingMemberChange = async (facilityId, pairingId, token) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/pairings/${pairingId}/members/undo/`,
    { token });
  return response.data;
};

/**
 * `GET facilities/<fid>/animal_groups/<id>/lineage/` — a clutch's OWN
 * parents (Cowork run 23, finding 4): `{clutch, parents, pairing}`.
 */
export const getClutchLineage = async (facilityId, groupId) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/animal_groups/${groupId}/lineage/`);
  return response.data;
};

/**
 * `DELETE facilities/<fid>/pairings/<id>/` — Owner/Manager.
 *
 * ⚠️ IT NEVER REFUSES OVER A CLUTCH. The 409 `pairing_in_use` is gone
 * (review round 1, BLOCK): the clutches keep the parents the pairing
 * recorded, and the response says so in `note` with `keeps_links` as a
 * real count.
 */
export const deletePairing = async (facilityId, pairingId) => {
  const response = await apiClient.delete(
    `facility/facilities/${facilityId}/pairings/${pairingId}/`);
  return response.data;
};

/**
 * `GET facilities/<fid>/localities/?species=<id>` — a species' lines.
 *
 * The roster the Provenance section's `origin_locality` select offers.
 * The species is REQUIRED: a line means nothing without it, and a
 * facility-wide list would invite the cross-species pick the serializer
 * refuses.
 */
export const getLocalities = async (facilityId, speciesId) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/localities/?species=${speciesId}`);
  return response.data?.results || [];
};

/** `GET facilities/<fid>/animals/<id>/lineage/` — the tree, depth 3. */
export const getAnimalLineage = async (facilityId, animalId, depth) => {
  const qs = depth ? `?depth=${depth}` : '';
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/animals/${animalId}/lineage/${qs}`);
  return response.data;
};

/**
 * `POST facilities/<fid>/animals/<id>/parents/` — the declared links.
 *
 * ⚠️ AN EXPLICIT `null` IS THE INSTRUCTION TO CLEAR, and an ABSENT key
 * leaves that role alone. Both spellings reach the wire untouched: a
 * wrapper that dropped a null key would answer 200 and change nothing,
 * which is the 2026-09-06 `enclosures_id` bug.
 */
export const setAnimalParents = async (facilityId, animalId, body) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/animals/${animalId}/parents/`, body);
  return response.data;
};

/** `DELETE …/animals/<id>/parents/` — the declared links, nothing else. */
export const clearAnimalParents = async (facilityId, animalId) => {
  const response = await apiClient.delete(
    `facility/facilities/${facilityId}/animals/${animalId}/parents/`);
  return response.data;
};

/**
 * `POST facilities/<fid>/animal_groups/<id>/parents/` — a clutch's own.
 *
 * `cascade: true` also re-derives the clutch's already-split hatchlings'
 * `split` links. The modal WARNS before sending it, because it is a write
 * to rows the operator did not open.
 */
export const setClutchParents = async (facilityId, groupId, body) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/animal_groups/${groupId}/parents/`,
    body);
  return response.data;
};

// ── I-03 R4 · S3-04.3 — THE GENETICS ENGINE'S FOUR READ DOORS ────────
//
// ⚠️ PINNED AT THE WIRE like every wrapper above them
// (`services/__tests__/lineageApiWire.test.js`). Three of these are GETs
// whose only payload is a query string, and the fourth is a POST whose
// BODY is the whole question — a dropped `genotype` key there is a
// prediction over two normal animals, answered confidently, which is the
// `runRoutine` failure with an arithmetic answer behind it.

/**
 * `GET facilities/<fid>/pairings/<id>/expected/` — what it would throw.
 *
 * Fetched ON EXPAND, never with the list: a prediction is a recursive CTE
 * plus a genetics read plus a morph roster PER ROW, and the Pairings page
 * draws twenty-five rows.
 */
/**
 * `GET facilities/<fid>/pairings/<id>/photos/` — a breeding pairing's
 * cover and gallery, `{pairing_id, cover, gallery, count}` (Q-trios-and-
 * pairing-photos, 2026-10-14). Facility-side only; any facility role reads.
 */
export const getPairingPhotos = async (facilityId, pairingId) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/pairings/${pairingId}/photos/`);
  return response.data;
};

export const getPairingExpected = async (facilityId, pairingId, eggs) => {
  const qs = eggs ? `?eggs=${eggs}` : '';
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/pairings/${pairingId}/expected/${qs}`);
  return response.data;
};

/**
 * `GET facilities/<fid>/animal_groups/<id>/expected/` — a clutch's own.
 *
 * `eggs` defaults SERVER-SIDE to the clutch's own count, so the caller
 * sends nothing and gets the clutch-scaled line for free. A clutch with
 * no pairing answers `{pairing: null}` with a note — not a 404.
 */
export const getClutchExpected = async (facilityId, groupId, eggs) => {
  const qs = eggs ? `?eggs=${eggs}` : '';
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/animal_groups/${groupId}`
    + `/expected/${qs}`);
  return response.data;
};

/** `GET facilities/<fid>/animals/<id>/genetics/derived/` — possible hets. */
export const getAnimalDerivedGenetics = async (facilityId, animalId) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/animals/${animalId}`
    + '/genetics/derived/');
  return response.data;
};

/**
 * `GET facilities/<fid>/genetics/predict/` — the calculator's vocabulary.
 *
 * With no species: the facility's species that have a Mendelian trait to
 * predict. With one: that species' per-trait options, LABELLED FROM THE
 * DATA (`super_form_name` is where "Super Snow" lives).
 */
export const getGeneticsVocabulary = async (facilityId, speciesId) => {
  const qs = speciesId ? `?species=${speciesId}` : '';
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/genetics/predict/${qs}`);
  return response.data;
};

/**
 * `POST facilities/<fid>/genetics/predict/` — the calculator.
 *
 * ⚠️ THE BODY GOES OVER THE WIRE AS GIVEN. A side is `{animal: <id>}` OR
 * `{genotype: {trait_id: 'normal'|'het'|'visual'|'super'}}`, and sending
 * both is the server's own 400. A wrapper that reshaped either — or that
 * dropped the `genotype` map because it looked empty — would answer 200
 * with a prediction over two normal animals.
 */
export const predictGenetics = async (facilityId, body) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/genetics/predict/`, body);
  return response.data;
};

/**
 * Destroy a clutch. Refuses with 409 when the clutch is not empty
 * (`group_not_empty`, count > 0) or is still placed on a shelf / in a
 * tank (`group_placed`) — the same "nothing living inside" rule the
 * racks Delete follows. Takes the facility id explicitly, like
 * `deleteRack`, rather than sniffing it out of the URL.
 */
export const deleteClutch = async (facilityId, clutchId) => {
  await apiClient.delete(
    `facility/facilities/${facilityId}/animal_groups/${clutchId}/`);
};

// S3-02.3: both readers used to swallow the error and return [] — a
// failed load rendered as "no racks", which is a lie the operator would
// act on. useRackTree is now their only caller and shows the failure, so
// they rethrow.
//
// S3-02.5: `{ archived: true }` asks for the ARCHIVED side of the room
// instead of the live one — never both, because a mixed list is how an
// archived rack ends up drawn on the map again.
export const getRacksAndEnclosuresByFacility = async (facilityId, { archived = false } = {}) => {
  try {
    const response = await apiClient.get(
      `facility/racks/racksandenclosures/${facilityId}/${archived ? '?archived=1' : ''}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching racks and enclosures:", error);
    throw error;
  }
};

export const getEnclosures = async (facilityId, { archived = false } = {}) => {
  try {
    const response = await apiClient.get(
      `facility/facilities/${facilityId}/enclosures/${archived ? '?archived=1' : ''}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching enclosures:", error);
    throw error;
  }
};

// ===========================================================================
// S3-02.3 — R2 rack / shelf / enclosure wrappers.
//
// Every one is facility-scoped by an EXPLICIT argument (never
// extractFacilityIdFromUrl — the label print route and the shared
// EnclosurePicker are reachable from URLs that carry no facility id) and
// NONE of them swallows an error: the racks page renders the backend's
// 409 bodies (code + occupants), so the rejection has to reach the caller
// intact. The legacy createRackAPI / updateRack / createEnclosureAPI /
// updateEnclosureAPI wrappers were deleted with the old rack UI — they
// were the only rack_slot writers left in src/.
// ===========================================================================

/** Normalizes an axios error into `{ status, code, message, detail }`. */
export const rackApiError = (error) => {
  const body = error?.response?.data || {};
  return {
    status: error?.response?.status ?? 0,
    code: body.code || body.error_code || null,
    message: body.error || body.detail || error?.message || 'Request failed',
    detail: body,
  };
};

export const createRackV2 = async (facilityId, payload) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/racks/v2/`, payload);
  return response.data;
};

export const updateRackV2 = async (facilityId, rackId, payload) => {
  const response = await apiClient.patch(
    `facility/facilities/${facilityId}/racks/${rackId}/v2/`, payload);
  return response.data;
};

export const deleteRack = async (facilityId, rackId) => {
  await apiClient.delete(`facility/facilities/${facilityId}/racks/${rackId}/`);
};

// --- S3-02.5 soft archive -------------------------------------------
//
// Reversible, and NOT delete: `deleteRack` above still destroys. The
// backend refuses to archive a TANK that has anything living in it (409
// `tank_occupied`); archiving a RACK is never refused — its tanks come
// off the shelves and stand in the room instead, unless the optional
// `archiveEmptyTanks` box archives the empty ones with it.

/** Counts for the confirm modal: tanks on this rack, and empty ones. */
export const getRackArchiveState = async (facilityId, rackId) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/racks/${rackId}/archive/`);
  return response.data;
};

export const archiveRack = async (
  facilityId, rackId, { reason = '', archiveEmptyTanks = false } = {},
) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/racks/${rackId}/archive/`,
    { reason, archive_empty_tanks: archiveEmptyTanks });
  return response.data;
};

export const restoreRack = async (facilityId, rackId) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/racks/${rackId}/restore/`, {});
  return response.data;
};

/**
 * What is INSIDE one tank, and what of it blocks the archive.
 *
 * The confirm dialog reads this so it can say "Inside: 1 clutch (6) —
 * deceased, does not block" BEFORE the operator commits (operator
 * ruling 2026-09-08: archive is ONE decision). Same list the 409
 * carries, so a refusal can never say something the confirm did not.
 */
export const getEnclosureArchiveState = async (facilityId, enclosureId) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/enclosures/${enclosureId}/archive/`);
  return response.data;
};

/**
 * DOES THIS FACILITY STILL HAVE THE PLACE? (FABLE_QUEUE "Filed by the Opus
 * driver 2026-11-02 ~00:30 — the sticky chip outlives its place"; the
 * legacy park batch, item 6.) The dock's sticky chip kept a tank a
 * `cleanup_qadelete` had removed. A rack or a tank is looked up by id
 * through its archive-state read (any facility role; 404 when the row is
 * not this facility's any more):
 *   true  — the row is there;
 *   false — a 404: the facility does not know it (the ONLY answer that
 *           drops a place);
 *   null  — anything else (no lookup for a room, a 403, the network):
 *           unknown, and an unknown place is KEPT.
 */
export const placeIsKnown = async (facilityId, place) => {
  const kind = place && place.kind;
  const id = place && place.id;
  const path = { rack: 'racks', enclosure: 'enclosures' }[kind];
  if (!facilityId || !id || !path) return null;
  try {
    await apiClient.get(
      `facility/facilities/${facilityId}/${path}/${id}/archive/`);
    return true;
  } catch (error) {
    return (error && error.response && error.response.status === 404)
      ? false : null;
  }
};

export const archiveEnclosure = async (
  facilityId, enclosureId, { reason = '', setAsideClutches = false } = {},
) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/enclosures/${enclosureId}/archive/`,
    { reason, set_aside_clutches: setAsideClutches });
  return response.data;
};

/**
 * The bulk sweep's PREVIEW — the honest N behind the button's label.
 *
 * `roomId` is REQUIRED from the UI: the button sits in one room's
 * Elsewhere zone and counts that room's cards, so the count and the
 * action have to be the same set (operator ruling, 2026-09-08).
 */
export const previewArchiveEmptyEnclosures = async (facilityId, roomId) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/enclosures/archive-empty/`,
    roomId ? { params: { room: roomId } } : undefined);
  return response.data;
};

/** Archive every unplaced tank in one room with nothing living in it. */
export const archiveEmptyEnclosures = async (
  facilityId, { reason = '', roomId = null } = {},
) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/enclosures/archive-empty/`,
    { reason, ...(roomId ? { room: roomId } : {}) });
  return response.data;
};

export const restoreEnclosure = async (facilityId, enclosureId) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/enclosures/${enclosureId}/restore/`, {});
  return response.data;
};

export const listShelves = async (facilityId, rackId) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/racks/${rackId}/shelves/`);
  return response.data;
};

export const createShelf = async (facilityId, rackId, payload) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/racks/${rackId}/shelves/`, payload);
  return response.data;
};

export const updateShelf = async (facilityId, shelfId, payload) => {
  const response = await apiClient.patch(
    `facility/facilities/${facilityId}/shelves/${shelfId}/`, payload);
  return response.data;
};

export const deleteShelf = async (facilityId, shelfId) => {
  await apiClient.delete(`facility/facilities/${facilityId}/shelves/${shelfId}/`);
};

/**
 * "Name tanks by position…" — the per-rack helper (ruling 2026-09-14).
 *
 * `dryRun: true` (the default) WRITES NOTHING and answers with the list
 * the confirm dialog prints: `{renames: [{id, from, to, shelf_no, face,
 * position_no}], left_alone: [{id, from, would_be}], collisions:
 * [{to, other_id, other_rack}], applied: false}`.
 *
 * ⚠️ **THE APPLY SENDS THE LIST THAT WAS ON SCREEN.** It used to send
 * only the flags and let the server re-run the plan, so a tank moved
 * while the dialog sat open had a DIFFERENT set of renames applied than
 * the one the operator had just read and approved. The server now writes
 * exactly `renames` and answers 409 `plan_stale` if any of it has moved
 * or been renamed since.
 *
 * `includeCustom` defaults OFF: a tank called "Bruce's tank" is listed as
 * left alone and is never renamed unless the operator ticks the box.
 * `applyAnyway` defaults OFF too, and is the other box: a rename whose
 * target another live tank already answers to is LEFT OUT without it.
 * Owner/Manager only — the endpoint refuses Staff with a 403.
 */
export const nameTanksByPosition = async (
  facilityId, rackId,
  { dryRun = true, includeCustom = false, applyAnyway = false,
    renames = null } = {},
) => {
  const body = {
    dry_run: dryRun,
    include_custom: includeCustom,
    apply_anyway: applyAnyway,
  };
  if (!dryRun) body.renames = renames || [];
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/racks/${rackId}/name-by-position/`,
    body);
  return response.data;
};

export const updateEnclosureV2 = async (facilityId, enclosureId, payload) => {
  const response = await apiClient.patch(
    `facility/facilities/${facilityId}/enclosures/${enclosureId}/v2/`, payload);
  return response.data;
};

/**
 * THE placement write. `shelf: null` makes the enclosure freestanding.
 * Throws on 409 position_occupied / 400 face_not_allowed — the caller
 * reverts its optimistic update and shows the body.
 */
export const moveEnclosure = async (facilityId, enclosureId, { shelf, positionNo }) => {
  const body = { shelf: shelf ?? null };
  if (positionNo !== undefined && positionNo !== null && positionNo !== '') {
    body.position_no = positionNo;
  }
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/enclosures/${enclosureId}/move/`, body);
  return response.data;
};

export const deleteEnclosure = async (facilityId, enclosureId) => {
  await apiClient.delete(
    `facility/facilities/${facilityId}/enclosures/${enclosureId}/`);
};

export const createEnclosureV2 = async (facilityId, payload) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/enclosures/`, { ...payload, facility_id: facilityId });
  return response.data;
};

// ===========================================================================
// S3-03.2 — the EVENT SPINE wrappers (I-03 R3).
//
// Same rules as the rack wrappers above: the facility id is an EXPLICIT
// argument (never sniffed out of the URL — the Log control is mounted in
// a tank drawer, an animal row and a phone sheet, and only one of those
// routes carries a facility id), and NOTHING swallows an error. The log
// form renders the backend's 400/409 bodies verbatim — `warnings`,
// `duplicate_lay`, `type_disabled` and `not_applicable` all have to reach
// the caller intact.
//
// `apiClient` has REACT_APP_API_URL as its baseURL, so these are never a
// relative fetch to the React origin (beta serves the API from
// api-beta.feelingfroggy.llc, a different host).
// ===========================================================================

/** The facility's event catalog. `enabled` true => only what a Log menu offers. */
export const listEventTypes = async (facilityId, { enabled = true, appliesTo } = {}) => {
  const params = new URLSearchParams();
  if (enabled !== null && enabled !== undefined) params.append('enabled', enabled ? '1' : '0');
  if (appliesTo) params.append('applies_to', appliesTo);
  const query = params.toString();
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/event-types/${query ? `?${query}` : ''}`);
  return response.data;
};

/**
 * Log ONE event. `payload` is the POST body the spine documents:
 * { type, occurred_at?, subject_animal?|subject_group?, locus_enclosure?,
 *   locus_rack?, values?, notes?, force? }.
 *
 * The 201 body is the event PLUS `warnings` (an out-of-range reading is
 * stored and reported, never refused — operator ruling 2026-09-07) and,
 * for a lay logged on an animal, `created_clutch_id` / `created_clutch_name`.
 */
export const logEvent = async (facilityId, payload) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/events/`, payload);
  return response.data;
};

/**
 * Log a WEIGH-IN for one animal (operator request 2026-10-07).
 *
 * An animal's weight is DERIVED from its newest weigh-in / checkup event
 * (`facility/services/animal_weight.py`) and is never a field on the
 * animal, so the grid row's "Log weight" writes THIS — one `weigh_in`
 * event — and never PATCHes the row. The body is exactly what the spine
 * reads: no locus (it is captured from where the animal lives), and no
 * `occurred_at` for TODAY (the server stamps now).
 *
 * `day` (optional, `YYYY-MM-DD`) is the day the animal was weighed
 * (Q-animal-weight-over-time, 2026-10-14). A PAST day is sent as that
 * day's NOON with no offset — `log_event` reads a naive stamp in
 * `settings.TIME_ZONE`, the zone `weighed_at` is rendered in, so the
 * weigh-in lands on the day the keeper chose and never a day off (the
 * day-over-a-timestamp rule). Today — `apiDay()`, today in that same zone
 * — or no day sends no stamp at all: noon today may still be in the
 * future, which the spine refuses. A future day is sent as asked and the
 * server refuses it with its sentence.
 */
export const logWeighIn = async (facilityId, animalId, grams, { day } = {}) => {
  const body = {
    type: 'weigh_in',
    subject_animal: String(animalId),
    values: { weight_g: grams },
  };
  if (day && day !== apiDay()) body.occurred_at = `${day}T12:00:00`;
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/events/`, body);
  return response.data;
};

/**
 * THE ANIMAL'S WEIGHT OVER TIME — the panel's trend and short list
 * (Q-animal-weight-over-time). Newest first; `before` is the opaque
 * keyset cursor a previous page answered as `next_before`. The body
 * carries `current_weight_g` / `weighed_at` beside `results`, the same
 * reading the row's own fields are.
 */
export const getAnimalWeights = async (facilityId, animalId, { limit, before } = {}) => {
  const params = new URLSearchParams();
  if (limit) params.append('limit', String(limit));
  if (before) params.append('before', before);
  const query = params.toString();
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/animals/${animalId}/weights/${query ? `?${query}` : ''}`);
  return response.data;
};

/** One animal's row as the grid lists it (the detail read). */
export const getAnimalDetail = async (facilityId, animalId) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/animals/${animalId}/`);
  return response.data;
};

export const listEvents = async (facilityId, params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, value);
    }
  });
  const query = search.toString();
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/events/${query ? `?${query}` : ''}`);
  return response.data;
};

/** Soft void. Nothing is ever hard-deleted from history by this API. */
export const voidEvent = async (facilityId, eventId, reason = '') => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/events/${eventId}/void/`, { reason });
  return response.data;
};

/** Attach already-uploaded media assets to an event (per-event photos). */
export const addEventPhotos = async (facilityId, eventId, assetIds) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/events/${eventId}/photos/`,
    { asset_ids: (assetIds || []).map(String) });
  return response.data;
};

// --- the two projections -------------------------------------------
//
// `before` is the OPAQUE keyset cursor a previous page returned as
// `next_before` — pass it back verbatim, never an offset. The timeline
// merges two tables written to independently, so an offset would skip or
// repeat rows the moment anything was logged mid-page.

// ⚠️ `includeVoided` IS SENT ONLY WHEN TRUE, never as `0`. The server
// reads it through `_truthy`, so unknown means off — and a param that is
// always present is a param a reader has to decide about. It widens the
// SPINE source only: a voided event comes back with `is_voided` and its
// `void_reason` so the row can say why it is struck out (Cowork run 20,
// the Feeding History rule applied to the timeline).
const timelineParams = ({ limit, before, since, until, includeVoided } = {}) => {
  const params = new URLSearchParams();
  if (limit) params.append('limit', String(limit));
  if (before) params.append('before', before);
  if (since) params.append('since', since);
  if (until) params.append('until', until);
  if (includeVoided) params.append('include_voided', '1');
  const query = params.toString();
  return query ? `?${query}` : '';
};

export const getAnimalTimeline = async (facilityId, animalId, options = {}) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/animals/${animalId}/timeline/${timelineParams(options)}`);
  return response.data;
};

// Cowork 2026-10-29 — a CLUTCH's own Activity list (its lay, hatch, out of
// water, feedings). The animal timeline's implementation for a group
// subject; same entry shape, same keyset cursor, same voided toggle.
export const getClutchTimeline = async (facilityId, clutchId, options = {}) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/animal_groups/${clutchId}/timeline/${timelineParams(options)}`);
  return response.data;
};

export const getEnclosureTimeline = async (facilityId, enclosureId, options = {}) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/enclosures/${enclosureId}/timeline/${timelineParams(options)}`);
  return response.data;
};

export const getRackTimeline = async (facilityId, rackId, options = {}) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/racks/${rackId}/timeline/${timelineParams(options)}`);
  return response.data;
};

/**
 * Interval stats for the prediction chips. With no `typeKey` the backend
 * answers for its shed + lay defaults. `likely_next` is null below three
 * events — the chip must not render a prediction the backend declined to
 * make.
 */
export const getAnimalEventStats = async (facilityId, animalId, typeKey) => {
  const query = typeKey ? `?type=${encodeURIComponent(typeKey)}` : '';
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/animals/${animalId}/event-stats/${query}`);
  return response.data;
};

// ===========================================================================
// S3-03.3 — ENCLOSURE ROUTINES (the one-tap bundle).
//
// Same rules as the event wrappers above: explicit facility id, nothing
// swallowed. A run's 201 body carries `warnings` (an out-of-range
// reading is stored and reported, never refused) and `targets`, so a
// rack run can say what it wrote and where.
// ===========================================================================

/**
 * The routines a surface should OFFER. `offeredFor` is the target kind
 * you are standing on — 'rack' gets the rack routines AND the tank
 * routines it can fan out over; 'enclosure' gets only the tank ones.
 */
export const listRoutines = async (
  facilityId, { enabled = true, offeredFor } = {},
) => {
  const params = new URLSearchParams();
  if (enabled !== null && enabled !== undefined) params.append('enabled', enabled ? '1' : '0');
  if (offeredFor) params.append('for', offeredFor);
  const query = params.toString();
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/routines/${query ? `?${query}` : ''}`);
  return response.data;
};

/**
 * Run one routine. `target` is `{kind: 'rack'|'enclosure', id}`;
 * `overrides` is a LIST with one entry per step, positional; `targets`
 * is the PER-TANK list `[{enclosure, values_by_step}]`.
 *
 * ⚠️ `targets` WAS MISSING FROM THIS DESTRUCTURE UNTIL S3-03.4, and the
 * bug was invisible from both ends. `RunRoutineModal` built the per-tank
 * payload correctly and the backend accepted it correctly; the wrapper
 * in between silently dropped the key, so a rack run posted
 * `{rack, overrides: [{},{},{}], occurred_at}` and every reading step
 * was recorded as BLANK — which the server reads as "nobody looked at
 * that tank" and reports as `skipped_steps`, exactly the truthful
 * behaviour that made the loss look like a decision. Found live on beta
 * by the harness.
 *
 * The RTL test could not catch it: it asserted the argument the modal
 * handed to a mocked `runRoutine`, which was always right. The guard now
 * asserts the WIRE BODY through a mocked `apiClient.post`
 * (`services/__tests__/eventsApiWire.test.js`) — the only place a
 * dropped key is visible.
 */
export const runRoutine = async (
  facilityId, routineId, { target, overrides, targets, occurredAt } = {},
) => {
  const body = {};
  if (target?.kind === 'rack') body.rack = String(target.id);
  else if (target?.kind === 'enclosure') body.enclosure = String(target.id);
  if (overrides) body.overrides = overrides;
  if (Array.isArray(targets) && targets.length) body.targets = targets;
  if (occurredAt) body.occurred_at = occurredAt;
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/routines/${routineId}/run/`, body);
  return response.data;
};

/** The facility's event rules — the "because" behind rule-created tasks. */
export const listEventRules = async (facilityId, { enabled } = {}) => {
  const params = new URLSearchParams();
  if (enabled !== undefined && enabled !== null) {
    params.append('enabled', enabled ? '1' : '0');
  }
  const query = params.toString();
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/event-rules/${query ? `?${query}` : ''}`);
  return response.data;
};

export const updateEventRule = async (facilityId, ruleId, patch) => {
  const response = await apiClient.patch(
    `facility/facilities/${facilityId}/event-rules/${ruleId}/`, patch);
  return response.data;
};

// ===========================================================================
// S3-03.4 — the CATALOG ADMIN writes (the Events settings page).
//
// All Owner/Manager on the backend. Nothing is swallowed here either: the
// settings page renders the 409 bodies verbatim, and those bodies are the
// whole operator surface for `system_type_locked`, `type_in_use`,
// `routine_in_use`, `system_routine_undeletable` and
// `system_rule_undeletable`. A wrapper that turned one of those into
// "Request failed" would delete the only explanation the operator gets.
// ===========================================================================

/** The WHOLE catalog, disabled rows included — the settings list. */
/**
 * The WHOLE catalog, disabled rows included — the settings page's fetch.
 *
 * `dependents: true` adds `dependent_rules` / `dependent_templates` to
 * every row: the event rules that watch the type and the task templates
 * whose card it completes. Cowork run 21 — turning a type off never
 * refuses, but it silently stops a rule firing and a card closing
 * itself, so the page says who needs it BEFORE the switch flips. Opt-in,
 * so every Log menu's fetch is byte identical.
 */
export const listAllEventTypes = async (facilityId, { dependents } = {}) => {
  const query = dependents ? '?dependents=1' : '';
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/event-types/${query}`);
  return response.data;
};

export const createEventType = async (facilityId, payload) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/event-types/`, payload);
  return response.data;
};

export const updateEventType = async (facilityId, typeId, patch) => {
  const response = await apiClient.patch(
    `facility/facilities/${facilityId}/event-types/${typeId}/`, patch);
  return response.data;
};

export const deleteEventType = async (facilityId, typeId) => {
  await apiClient.delete(
    `facility/facilities/${facilityId}/event-types/${typeId}/`);
};

export const createRoutine = async (facilityId, payload) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/routines/`, payload);
  return response.data;
};

export const updateRoutine = async (facilityId, routineId, patch) => {
  const response = await apiClient.patch(
    `facility/facilities/${facilityId}/routines/${routineId}/`, patch);
  return response.data;
};

export const deleteRoutine = async (facilityId, routineId) => {
  await apiClient.delete(
    `facility/facilities/${facilityId}/routines/${routineId}/`);
};

export const createEventRule = async (facilityId, payload) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/event-rules/`, payload);
  return response.data;
};

export const deleteEventRule = async (facilityId, ruleId) => {
  await apiClient.delete(
    `facility/facilities/${facilityId}/event-rules/${ruleId}/`);
};

/**
 * The tank drawer's compact "Readings" line — the LAST reading logged AT
 * this tank, with its type's field schema so each number renders with
 * its unit and its expected range. `{reading: null}` when there is none;
 * that is a state, not an error.
 */
export const getEnclosureReadings = async (facilityId, enclosureId) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/enclosures/${enclosureId}/readings/`);
  return response.data;
};

// ===========================================================================
// R-OBLIGATE OB-1 (2026-10-31) — SITES on a tank (canisters, axils, pools).
//
// A site is a numbered spot inside one tank where a dam deposits a tadpole.
// The BOARD (each site's `sentence` / `label` / `state`) is derived on the
// server and displayed verbatim — never re-derived here. Any role reads;
// Owner/Manager write (the server answers `can_edit`, and 403s Staff).
// A retire is soft (`active: false`), never a delete; restore undoes it.
// ===========================================================================

/** `{enclosure_id, enclosure_name, can_edit, sites: [Site]}`. */
export const getEnclosureSites = async (facilityId, enclosureId, { includeRetired = false } = {}) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/enclosures/${enclosureId}/sites/`
    + (includeRetired ? '?include_retired=1' : ''));
  return response.data;
};

/** "Add N sites" — appends the next numbers. `kind` is optional. */
export const addEnclosureSites = async (facilityId, enclosureId, count, kind) => {
  const body = { count: Number(count) };
  if (kind) body.kind = kind;
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/enclosures/${enclosureId}/sites/`, body);
  return response.data;
};

/** `patch`: any of `{kind, panel, height, note}`. Answers the Site. */
export const updateTankSite = async (facilityId, siteId, patch) => {
  const response = await apiClient.patch(
    `facility/facilities/${facilityId}/sites/${siteId}/`, patch);
  return response.data;
};

/** Soft retire. 409 `site_occupied` while a tadpole is still in it. */
export const retireTankSite = async (facilityId, siteId) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/sites/${siteId}/retire/`, {});
  return response.data;
};

export const restoreTankSite = async (facilityId, siteId) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/sites/${siteId}/restore/`, {});
  return response.data;
};

/**
 * OB-2 item 3 — "Log a tadpole here": the SERVER's which-clutch list for a
 * deposit at this site (`rows`, numbered, newest first, "A lay I didn't
 * see" LAST with id `found`) and the found clutch's parents (`parents`:
 * `{verdict, rows}` — `ask` means the keeper must choose).
 */
export const getSiteDepositCandidates = async (facilityId, enclosureId, siteId) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/enclosures/${enclosureId}/sites/${siteId}/deposit-candidates/`);
  return response.data;
};

/**
 * OB-2 item 3 — the deposit at a site. `choice` is `{clutch: <row id>}` or
 * `{found: true, found_parents?: <parents row id>}`; only those keys go on
 * the wire (the server refuses a clutch its list does not offer).
 */
export const logSiteDeposit = async (facilityId, enclosureId, siteId, choice = {}) => {
  const body = {};
  if (choice.found) {
    body.found = true;
    if (choice.found_parents) body.found_parents = String(choice.found_parents);
  } else {
    body.clutch = String(choice.clutch || '');
  }
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/enclosures/${enclosureId}/sites/${siteId}/deposit/`, body);
  return response.data;
};

/** `{can_edit, boards: {enclosure_id: [Site]}}` — only tanks with sites. */
export const getSiteBoards = async (facilityId, enclosureIds = []) => {
  const ids = (enclosureIds || []).map(String).filter(Boolean);
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/site-boards/`
    + (ids.length ? `?enclosure_ids=${ids.map(encodeURIComponent).join(',')}` : ''));
  return response.data;
};

// ===========================================================================
// I-03 R6 · S3-06.4 — THINGS (supplies & equipment, find/things).
//
// A Thing is anything in the room that is not an animal, a clutch, a tank
// or a rack: springtail cultures, the fogger, spare heat tape. Any
// facility role may read AND write — the backend does not gate this on
// Owner/Manager, and neither does the UI (adding a tub of springtails is
// daily work, like the ungated "Add freestanding tank").
//
// Same rules as every other wrapper on this page: facility id explicit,
// nothing swallowed. DELETE archives — it never hard-deletes.
// ===========================================================================

/** `params`: any of `room`, `rack`, `enclosure`, `kind`, `include_archived`. */
export const getThings = async (facilityId, params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, value);
    }
  });
  const query = search.toString();
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/things/${query ? `?${query}` : ''}`);
  return response.data;
};

/** `payload`: `{name, kind?, room?, rack?, enclosure?, spot?, notes?}`. */
export const createThing = async (facilityId, payload) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/things/`, payload);
  return response.data;
};

/** `patch`: any of `{name, kind, spot, notes, room, rack, enclosure, archived}`. */
export const updateThing = async (facilityId, thingId, patch) => {
  const response = await apiClient.patch(
    `facility/facilities/${facilityId}/things/${thingId}/`, patch);
  return response.data;
};

/** Soft archive — never a hard delete. Returns the archived Thing. */
export const archiveThing = async (facilityId, thingId) => {
  const response = await apiClient.delete(
    `facility/facilities/${facilityId}/things/${thingId}/`);
  return response.data;
};


/** Places a clutch's egg cup on an incubator shelf. */
export const placeAnimalGroup = async (facilityId, groupId, { shelf, positionNo }) => {
  const body = { shelf: shelf ?? null };
  if (positionNo !== undefined && positionNo !== null && positionNo !== '') {
    body.position_no = positionNo;
  }
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/animal-groups/${groupId}/place/`, body);
  return response.data;
};

// --- S3-02.3b rooms ------------------------------------------------
//
// A room is WHERE things are, one level: one main room per facility plus
// support rooms positioned relative to it. `moveRackToRoom` /
// `moveEnclosureToRoom` are the only writers of the room FK (both
// serializers hold it read-only server-side), and each sets the room and
// the wall text in ONE request so a drag between rooms cannot half-land.

export const listRooms = async (facilityId) => {
  const response = await apiClient.get(`facility/facilities/${facilityId}/rooms/`);
  return response.data;
};

export const createRoom = async (facilityId, payload) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/rooms/`, payload);
  return response.data;
};

export const updateRoom = async (facilityId, roomId, payload) => {
  const response = await apiClient.patch(
    `facility/facilities/${facilityId}/rooms/${roomId}/`, payload);
  return response.data;
};

export const deleteRoom = async (facilityId, roomId) => {
  await apiClient.delete(`facility/facilities/${facilityId}/rooms/${roomId}/`);
};

/** Move a rack into a room, optionally rewriting its wall text too. */
export const moveRackToRoom = async (facilityId, rackId, { room, location }) => {
  const body = { room: room ?? null };
  if (location !== undefined) body.location = location;
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/racks/${rackId}/room/`, body);
  return response.data;
};

/**
 * Move a FREESTANDING tank into a room. The server refuses (400
 * enclosure_on_shelf) for a tank sitting on a shelf — a racked tank is
 * in its rack's room, and the rack is the thing that moves.
 */
export const moveEnclosureToRoom = async (
  facilityId, enclosureId, { room, location },
) => {
  const body = { room: room ?? null };
  if (location !== undefined) body.location = location;
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/enclosures/${enclosureId}/room/`, body);
  return response.data;
};

export const listTags = async (facilityId) => {
  const response = await apiClient.get(`facility/facilities/${facilityId}/tags/`);
  return response.data;
};

export const createTag = async (facilityId, name) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/tags/`, { name });
  return response.data;
};

export const deleteTag = async (facilityId, tagId) => {
  await apiClient.delete(`facility/facilities/${facilityId}/tags/${tagId}/`);
};

/**
 * S3-02.3 — set the cover photo of a rack (or the frog room itself).
 * Neither model has a cover_asset FK, so "cover" is a role='cover'
 * MediaLink: drop any existing cover link, then create the new one.
 * `targetType` is 'rack' or 'facility'.
 */
export const setRackCoverAsset = async (facilityId, targetType, targetId, assetId) => {
  let existing = [];
  try {
    const data = await getObjectAssets(targetType, targetId);
    existing = (data?.links || []).filter((l) => l.role === 'cover');
  } catch (error) {
    console.warn('[racks] could not read existing cover links:', error?.message);
  }
  for (const link of existing) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await mediaUnlinkAsset({ linkId: link.id });
    } catch (error) {
      console.warn('[racks] could not remove old cover link:', error?.message);
    }
  }
  return mediaLinkAsset({
    assetId,
    objectType: targetType,
    objectId: targetId,
    role: 'cover',
    position: 0,
    facilityId,
  });
};

export const markAnimalsForSale = async (facilityId, animalIds) => {
  try {
    // Derive from route if not provided
    const resolvedFacilityId = facilityId || extractFacilityIdFromUrl();
    if (!resolvedFacilityId) throw new Error('No facility ID in route');
    const response = await apiClient.put(`facility/facilities/${resolvedFacilityId}/animals/mark_as_products/`, {
      facility_id: resolvedFacilityId,  // Ensure `facility_id` is passed correctly
      animal_ids: animalIds,    // Ensure the array is correctly formatted
    });
    return response.data;
  } catch (error) {
    console.error('Error marking animals for sale:', error);
    throw error;
  }
};

// THE PREVIEW OF "MARK FOR SALE" (Cowork run 35, G2): the SAME endpoint
// and the SAME body with `?dry_run=1` — the server runs the write's own
// resolution and creates nothing, answering per animal the product and
// variation it would be linked to (or why it cannot be). The grid's confirm
// reads this; the write that follows is `markAnimalsForSale`, unchanged.
export const previewMarkAnimalsForSale = async (facilityId, animalIds) => {
  const resolvedFacilityId = facilityId || extractFacilityIdFromUrl();
  if (!resolvedFacilityId) throw new Error('No facility ID in route');
  const response = await apiClient.put(
    `facility/facilities/${resolvedFacilityId}/animals/mark_as_products/?dry_run=1`,
    { facility_id: resolvedFacilityId, animal_ids: animalIds },
  );
  return response.data;
};

// DEPRECATED (P-05c bypass lockdown): backend endpoint preserved for
// remediation scripting only — Owner-only on the server, logs WARNING on use.
// UI button removed from AnimalsGrid. Do NOT add new call sites.
// Use createManualOrder with is_historical=True instead.
export const bulkMarkAnimalsSold = async (facilityId, animalIds) => {
  const response = await apiClient.post(`facility/facilities/${facilityId}/animals/bulk-mark-sold/`, {
    animal_ids: animalIds,
  });
  return response.data;
};


//Store APIs

export const createProductFromSpecies = async (speciesId, facilityId) => {
  // Derive from route if not provided
  const resolvedFacilityId = facilityId || extractFacilityIdFromUrl();
  if (!resolvedFacilityId) throw new Error('No facility ID in route');
  const response = await apiClient.post(`facility/facilities/${resolvedFacilityId}/species/create_product/`, {
    species_id: speciesId,
    facility_id: resolvedFacilityId
  });
  return response.data;
};


export const getProductsWithVariations = async (facilityId, { rethrow = false } = {}) => {
  try {
    // Cowork run 3 (2026-08-31) measured the COLD call at 26.9s against the
    // 15s global timeout (warm: ~1.3s) — the first attempt AND the Punch #41
    // retry both burned out while the request was still in flight, so the
    // operator watched a 30-40s spinner on the first visit of a session (and
    // the abandoned first query kept running server-side, doubling load).
    // ONE attempt at 45s replaces retry-at-15s: it clears the measured cold
    // case by 67%, so a timeout here now means a real fault — and the page
    // surfaces its own Retry button (ManageProducts loadFailed), which is a
    // better failure affordance than a second silent 45s wait.
    const response = await apiClient.get(
      `store/products/products_with_variations/facility/${facilityId}/`,
      { timeout: 45000 }
    );

    return response.data; // Returns the list of products with variations
  } catch (error) {
    console.error("Error fetching products with variations:", error);
    // Punch #41-family: legacy behavior swallows the error and returns []
    // — indistinguishable from "no products", which left the manual-order
    // wizard with a silently empty variation dropdown. Callers that need
    // to show an error/Retry pass { rethrow: true }.
    if (rethrow) throw error;
    return [];
  }
};


// src/services/api.js
export const addOrUpdatePrice = async (variationId, priceData, facilityId) => {
  try {
      // ✅ Include facility_id in the payload
      const payloadWithFacilityId = {
          ...priceData,
          facility_id: facilityId,
      };

      console.log("API Request to add or update price:", variationId, JSON.stringify(payloadWithFacilityId, null, 2));

      // Use POST for new prices, PUT for existing ones
      const method = priceData.id ? 'put' : 'post';
      const response = await apiClient[method](`/store/variations/${variationId}/prices/`, payloadWithFacilityId);

      console.log("API Response:", response.data);
      return response.data;
  } catch (error) {
      console.error("Error adding or updating price:", error.response?.data || error.message);
      throw error;
  }
};

export const getVariationPrices = async (variationId, facilityId) => {
  try {
      const response = await apiClient.get(`store/variations/${variationId}/get-prices/`, {
          params: { facility_id: facilityId }
      });

      console.log("✅ Backend Response from getVariationPrices:", response.data); // 🔥 Debug Log
      return response.data;
  } catch (error) {
      console.error("❌ Failed to fetch variation prices:", error.response?.data || error.message);
      throw error;
  }
};


export const editPriceEndDate = async (priceId, endDatePayload) => {
  try {
      const response = await apiClient.put(`store/prices/${priceId}/`, endDatePayload);
      return response.data;
  } catch (error) {
      console.error("Failed to update end date:", error.response?.data || error.message);
      throw error;
  }
};

// ✅ Delete Price
export const deletePrice = async (variationId, priceId, facilityId) => {
  try {
      console.log(`🗑️ Deleting Price: ${priceId} for Variation: ${variationId}`);
      const response = await apiClient.delete(`store/variations/${variationId}/prices/${priceId}/delete/`, {
          data: { facility_id: facilityId }
      });
      return response.data;
  } catch (error) {
      console.error("❌ Error deleting price:", error.response?.data || error.message);
      throw error;
  }
};



;

// Batch toggle shipping calendar days for a facility (replaced with new API function).
/**
 * Batch toggle shipping calendar days for a facility.
 * @param {string} facilityId
 * @param {Array<{ date: string, is_open: boolean }>} days
 * @returns {Promise<any>}
 */
export async function batchToggleShippingCalendarDays(facilityId, days) {
  // `days` is an array of objects: [{ date: 'YYYY-MM-DD', is_open: true }, ...]
  try {
    const payload = {
      dates: days.map((day) => ({
        date: day.date,
        is_open: day.is_open,
      })),
    };
    console.log("Payload being sent to batchToggleShippingCalendarDays:", payload);
    const response = await apiClient.post(
      `shipping/facility/${facilityId}/shipping-calendar/batch-toggle/`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error toggling shipping calendar days:", error);
    throw error;
  }
}

// Facility Shipping Box Sizes

/**
 * Fetch all box sizes for a facility.
 * @param {string} facilityId
 */
export async function getFacilityShippingBoxSizes(facilityId) {
  const response = await apiClient.get(`shipping/facility/${facilityId}/shipping-box-sizes/`);
  return response.data;
}

/**
 * Add a new box size to a facility.
 * @param {string} facilityId
 * @param {object} data - { length_in: number, width_in: number, height_in: number, liner_thickness_in: number }
 */
export async function addFacilityShippingBoxSize(facilityId, data) {
  const response = await apiClient.post(`shipping/facility/${facilityId}/shipping-box-sizes/`, data);
  return response.data;
}

/**
 * Delete a facility box size.
 * @param {string} id
 */
export async function deleteFacilityShippingBoxSize(facilityId, id) {
  const response = await apiClient.delete(`shipping/facility/${facilityId}/shipping-box-sizes/${id}/`);
  return response.data;
}

/**
 * Deactivate a facility box size.
 * @param {string} facilityId
 * @param {string} id
 */
export async function deactivateFacilityShippingBoxSize(facilityId, id) {
  console.log("🛑 deactivateFacilityShippingBoxSize called with facilityId:", facilityId);
  const response = await apiClient.post(`shipping/facility/${facilityId}/shipping-box-sizes/${id}/deactivate/`);
  return response.data;
}

/**
 * Update a specific shipping box size by ID.
 * @param {string} id
 * @param {Object} data
 * @param {string} facilityId
 * @returns {Promise<any>}
 */
export async function updateShippingBoxSize(id, data, facilityId) {
  try {
    console.log("🚚 updateShippingBoxSize called with facilityId:", facilityId);
    console.log("updateShippingBoxSize payload . . . ")
    const payload = {
      ...data,
      facility_id: facilityId,
    };
    const response = await apiClient.put(`shipping/facility/${facilityId}/shipping-box-sizes/${id}/`, payload);
    return response.data;
  } catch (error) {
    console.error("Error updating shipping box size:", error);
    throw error;
  }
}

// === Shipping Container APIs ===

export const getShippingContainers = async (facilityId) => {
  const response = await apiClient.get(`shipping/shipping_containers/facility/${facilityId}/`);
  return response.data;
};

export const createShippingContainer = async (facilityId, data) => {
  const response = await apiClient.post(`shipping/shipping_containers/facility/${facilityId}/`, data);
  return response.data;
};

export const updateShippingContainer = async (facilityId, id, data) => {
  const response = await apiClient.put(`shipping/shipping_containers/facility/${facilityId}/${id}/`, data);
  return response.data;
};

export const deleteShippingContainer = async (facilityId, id) => {
  const response = await apiClient.delete(`shipping/shipping_containers/facility/${facilityId}/${id}/`);
  return response.data;
};

export const deactivateShippingContainer = async (facilityId, id) => {
  const response = await apiClient.post(`shipping/shipping_containers/facility/${facilityId}/${id}/deactivate/`);
  return response.data;
};

// Variation Shipping Profile APIs
/**
 * Fetch a variation shipping profile by variation ID.
 * @param {string|number} variationId
 * @returns {Promise<any>}
 */
export const getVariationShippingProfileByVariation = async (variationId, facility_id) => {
  console.log("🔍 Fetching VariationShippingProfile - variationId:", variationId, "facility_id:", facility_id);
  const response = await apiClient.get(`shipping/variation_shipping_profiles/facility/${facility_id}/by-variation/${variationId}/`);
  return response.data;
};

/**
 * Update a variation shipping profile by profile ID.
 * @param {string|number} profileId
 * @param {object} payload
 * @returns {Promise<any>}
 */
export const updateVariationShippingProfile = async (profileId, payload, facility_id) => {
  console.log("✏️ Updating VariationShippingProfile - profileId:", profileId, "facility_id:", facility_id, "Payload:", payload);
  const response = await apiClient.put(`shipping/variation_shipping_profiles/facility/${facility_id}/${profileId}/`, payload);
  return response.data;
};


// Estimate shipping box for a cart
/**
 * Estimate the shipping box for a given cart.
 * @param {string} cartId - The cart ID.
 * @param {object} payload - The payload to send for estimation.
 * @returns {Promise<any>} The estimated shipping box response.
 */
export const estimateShippingBox = async (cartId, payload) => {
  try {
    console.log("📦 Estimating shipping box for cart:", cartId);

    // Validation and logging for required fields
    if (!payload.destination_zip) {
      console.error("❌ estimateShippingBox: Missing required field: destination_zip");
      throw new Error("Missing required field: destination_zip");
    }
    if (payload.residential === undefined || payload.residential === null) {
      console.warn("⚠️ estimateShippingBox: 'residential' not provided, defaulting to true");
      payload.residential = true;
    }
    if (!payload.address_id && !payload.address_line_1 && !payload.zip) {
      console.error("❌ estimateShippingBox: Must include address_id or full address or zip");
      throw new Error("Must include address_id or full address or zip");
    }

    console.log("Payload being sent:", payload);

    // Use GET request with query parameters (no body)
    const response = await apiClient.get(
      `shipping/cart/${cartId}/estimate-shipping-box/`,
      {
        params: {
          destination_zip: payload.destination_zip,
          residential: payload.residential,
          zip: payload.zip,
        },
      }
    );

    console.log("📦 Shipping box estimate response:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error estimating shipping box:", error.response?.data || error.message || error);
    throw error;
  }
};

/**
 * Estimate shipping box from product variations.
 * Accepts an array of variation IDs or objects with variation and quantity.
 * @param {Array} variationItems - Array of variation IDs or {variation, quantity} objects.
 * @param {string} zipCode - Destination ZIP code.
 * @param {boolean} [isResidential=true] - Whether the destination is residential.
 * @returns {Promise<any>} The estimated shipping box response.
 */
export const estimateShippingBoxFromVariations = async (variationItems, zipCode, isResidential = true) => {
  const variation_ids = [];
  const quantity_map = {};
  const lot_ids = [];

  variationItems.forEach((item) => {
    const variationId = item.variation;
    const quantity = item.quantity;

    // I-02d: lot lines ship every member, not quantity=1 — send the lot
    // id and let the server resolve the member count (server-
    // authoritative box sizing; the client never computes it).
    if (item.lot) {
      lot_ids.push(item.lot);
      return;
    }

    if (variationId) {
      variation_ids.push(variationId);
      quantity_map[variationId] = quantity;
    }
  });

  const payload = {
    variation_ids,
    quantity_map,
    lot_ids,
    zip: zipCode,
    // destination_zip: zipCode,
    // residential: isResidential,
  };

  return await apiClient
    .post(`/shipping/variations/estimate-shipping-box/`, payload)
    .then((res) => res.data);
};


/**
 * Get a list of FedEx Express hubs that support HOLD_AT_LOCATION for a given ZIP code.
 * @param {string} zip - ZIP code to look up.
 * @returns {Promise<any>} List of FedEx hubs.
 */
export const getFedexHubListByZip = async (zip) => {
  try {
    console.log("[API] Requesting FedEx hubs for ZIP:", zip);
    const response = await apiClient.get(`shipping/fedex/hub_list_by_zip/`, {
      params: { zip },
    });
    console.log("[API] Received FedEx hubs:", response.data);
    return response.data;
  } catch (error) {
    console.error("[API] Error fetching FedEx hub list:", error.response?.data || error.message);
    throw error;
  }
};

// ======================================================
// Payments API
// ======================================================

/**
 * Create a reservation for an order (stub implementation)
 * @param {string} orderId - Order ID
 * @returns {Promise<any>} Reservation data
 */
export const createOrderReservation = async (orderId, options = {}) => {
  try {
    console.log("[API] Creating reservation for order:", orderId);
    const config = {
      ...options,
      headers: {
        ...options.headers
      }
    };
    const response = await apiClient.post(`store/orders/${orderId}/reserve/`, {}, config);
    console.log("[API] Reservation created:", response.data);
    return response.data;
  } catch (error) {
    console.error("[API] Error creating reservation:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Release order reservation (for payment failures)
 * @param {string} orderId - Order ID
 * @param {object} options - Request options (headers, etc)
 * @returns {Promise<any>} Release response
 */
export const releaseOrderReservation = async (orderId, options = {}) => {
  try {
    console.log("[API] Releasing reservation for order:", orderId);
    const config = {
      ...options,
      headers: {
        ...options.headers
      }
    };
    const response = await apiClient.post(`store/orders/${orderId}/release/`, {}, config);
    console.log("[API] Reservation released:", response.data);
    return response.data;
  } catch (error) {
    console.error("[API] Error releasing reservation:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Create a Stripe payment intent for an order
 * @param {string} orderId - Order ID
 * @returns {Promise<any>} Payment intent data with client_secret
 */
export const createPaymentIntent = async (orderId) => {
  try {
    console.log("[API] Creating payment intent for order:", orderId);
    const response = await apiClient.post(`payments/intents/`, {
      order_id: orderId
    });
    console.log("[API] Payment intent created:", response.data);
    return response.data;
  } catch (error) {
    console.error("[API] Error creating payment intent:", error.response?.data || error.message);
    throw error;
  }
};

export const reconcilePaymentIntent = async (paymentIntentId) => {
  try {
    console.log("[API] Reconciling payment intent:", paymentIntentId);
    const response = await apiClient.get(`payments/intents/${paymentIntentId}/reconcile/`);
    console.log("[API] Payment intent reconciled:", response.data);
    return response.data;
  } catch (error) {
    console.error("[API] Error reconciling payment intent:", error.response?.data || error.message);
    throw error;
  }
};

export const getOrderPaymentIntent = async (orderId) => {
  try {
    console.log("[API] Getting order payment intent:", orderId);
    const response = await apiClient.get(`payments/orders/${orderId}/payment-intent/`);
    console.log("[API] Order payment intent retrieved:", response.data);
    return response.data;
  } catch (error) {
    console.error("[API] Error getting order payment intent:", error.response?.data || error.message);
    throw error;
  }
};

// Update delivery type (sanctioned endpoint for intentional changes)
export const updateDeliveryType = async (orderId, deliveryType, hubId = null) => {
  try {
    console.log('[API] Updating delivery type for order:', orderId, deliveryType, hubId);
    const payload = {
      order_id: orderId,
      delivery_type: deliveryType, // 'HOME' or 'HUB' - simplified field
    };
    
    if (deliveryType === 'HUB' && hubId) {
      payload.hub_id = hubId;
    }
    
    const response = await apiClient.post('store/update-delivery-type/', payload);
    console.log('[API] Delivery type updated:', response.data);
    return response.data;
  } catch (error) {
    console.error('[API] Error updating delivery type:', error.response?.data || error.message);
    throw error;
  }
};

export const ensureFreshPaymentIntent = async (orderId) => {
  try {
    console.log("[API] Ensuring fresh payment intent for order:", orderId);
    
    // SANITIZED PAYLOAD - Only order_id, no shipping fields
    const payload = { order_id: orderId };
    
    // Log for debugging
    console.debug("[CONFIRM PAYLOAD] ensure-fresh-intent:", payload);
    
    const response = await apiClient.post(`payments/ensure-fresh-intent/`, payload);
    console.log("[API] Fresh payment intent ensured:", response.data);
    return response.data;
  } catch (error) {
    console.error("[API] Error ensuring fresh payment intent:", error.response?.data || error.message);
    throw error;
  }
};

// Customer Orders API
export const fetchMyOrders = async (page = 1, pageSize = 10) => {
  try {
    console.log(`[API] Fetching my orders - page: ${page}, pageSize: ${pageSize}`);
    const response = await apiClient.get(`store/my-orders/`, {
      params: {
        page,
        page_size: pageSize
      }
    });
    console.log("[API] My orders fetched:", response.data);
    return response.data;
  } catch (error) {
    console.error("[API] Error fetching my orders:", error.response?.data || error.message);
    throw error;
  }
};

export const fetchMyOrderDetail = async (orderId) => {
  try {
    console.log("[API] Fetching my order detail:", orderId);
    const response = await apiClient.get(`store/my-orders/${orderId}/`);
    console.log("[API] My order detail fetched:", response.data);
    return response.data;
  } catch (error) {
    console.error("[API] Error fetching my order detail:", error.response?.data || error.message);
    throw error;
  }
};


// ===== SHIPPING MANAGEMENT =====

/**
 * Get shipments for facility management with filtering and pagination
 */
export const getShipments = async (facilityId, params = {}) => {
  try {
    console.log("[API] Fetching facility shipments:", { facilityId, params });
    const response = await apiClient.get(`store/facilities/${facilityId}/shipping/shipments/`, { params });
    console.log("[API] Facility shipments fetched:", response.data);
    return response.data;
  } catch (error) {
    console.error("[API] Error fetching facility shipments:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Advance order status through shipping lifecycle
 */
export const advanceOrderStatus = async (facilityId, orderId, action, data = {}) => {
  try {
    console.log("[API] Advancing order status:", { facilityId, orderId, action, data });
    const payload = { action, ...data };
    const response = await apiClient.post(`store/facilities/${facilityId}/shipping/orders/${orderId}/advance-status/`, payload);
    console.log("[API] Order status advanced:", response.data);
    return response.data;
  } catch (error) {
    console.error("[API] Error advancing order status:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update shipping information for an order
 */
export const updateOrderShipping = async (facilityId, orderId, data) => {
  try {
    console.log("[API] Updating order shipping:", { facilityId, orderId, data });
    const response = await apiClient.put(`store/facilities/${facilityId}/shipping/orders/${orderId}/shipping/`, data);
    console.log("[API] Order shipping updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("[API] Error updating order shipping:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Send label created email for an order
 */
export const sendLabelCreatedEmail = async (facilityId, orderId, trackingNumber = null) => {
  try {
    console.log("[API] Sending label created email:", { facilityId, orderId, trackingNumber });
    const payload = trackingNumber ? { tracking_number: trackingNumber } : {};
    const response = await apiClient.post(`store/facilities/${facilityId}/shipping/orders/${orderId}/email-label-created/`, payload);
    console.log("[API] Label created email sent:", response.data);
    return response.data;
  } catch (error) {
    console.error("[API] Error sending label created email:", error.response?.data || error.message);
    throw error;
  }
};

// Toggle email muting for an order
export const toggleOrderEmailMute = async (facilityId, orderId, muted, reason = '') => {
  const response = await apiClient.post(`store/facilities/${facilityId}/shipping/orders/${orderId}/mute-emails/`, {
    muted, reason
  });
  return response.data;
};

// Manual order creation (off-platform sales)
export const createManualOrder = async (facilityId, data) => {
  // Stripe-link orders perform two Stripe API calls server-side (Price + PaymentLink)
  // which can exceed the default 15s client timeout. Allow up to 60s for manual
  // orders generally — safe because the backend wraps the whole create in a single
  // @transaction.atomic block.
  const response = await apiClient.post(
    `store/facilities/${facilityId}/orders/manual/`,
    data,
    { timeout: 60000 }
  );
  return response.data;
};

export const getAvailableAnimalsForAssignment = async (facilityId, params = {}) => {
  const response = await apiClient.get(`store/facilities/${facilityId}/orders/manual/available-animals/`, { params });
  return response.data;
};

// Orphan animals: sold-but-not-linked animals for retroactive/historical orders
export const getOrphanAnimalsForAssignment = async (facilityId, params = {}) => {
  const response = await apiClient.get(`store/facilities/${facilityId}/orders/manual/orphan-animals/`, { params });
  return response.data;
};

// Cancel a manual order and release its animals back to inventory.
// Punch #64: cancel any UNPAID order (customer checkout leftovers and
// manual holds alike). Owner/Manager only; paid orders refuse server-side.
export const cancelUnpaidOrder = async (facilityId, orderId, reason = '') => {
  const response = await apiClient.post(
    `store/facilities/${facilityId}/orders/${orderId}/cancel/`,
    { reason }
  );
  return response.data;
};

export const cancelManualOrder = async (facilityId, orderId, reason = '') => {
  const response = await apiClient.post(
    `store/facilities/${facilityId}/orders/manual/${orderId}/cancel/`,
    { reason }
  );
  return response.data;
};

// Mark a hold_external or stripe_link manual order as paid (external payment arrived).
export const markHoldPaid = async (facilityId, orderId) => {
  const response = await apiClient.post(
    `store/facilities/${facilityId}/orders/manual/${orderId}/mark-paid/`,
    {}
  );
  return response.data;
};

// Refund — get info (history + remaining refundable). Owner-only on backend.
export const getRefundInfo = async (facilityId, orderId) => {
  const response = await apiClient.get(
    `store/facilities/${facilityId}/orders/${orderId}/refund/info/`
  );
  return response.data;
};

// Refund — AI draft email (no side effects). Owner-only on backend.
export const draftRefundEmail = async (facilityId, orderId, { amount, reason, animal_dispositions = {} }) => {
  const response = await apiClient.post(
    `store/facilities/${facilityId}/orders/${orderId}/refund/draft-email/`,
    { amount, reason, animal_dispositions }
  );
  return response.data;
};

// Refund — process refund (Stripe API + animal dispositions + email). Owner-only.
export const processRefund = async (facilityId, orderId, payload) => {
  const response = await apiClient.post(
    `store/facilities/${facilityId}/orders/${orderId}/refund/`,
    payload
  );
  return response.data;
};

// Retry Stripe PaymentLink creation for a stripe_link order.
export const retryStripePaymentLink = async (facilityId, orderId) => {
  const response = await apiClient.post(
    `store/facilities/${facilityId}/orders/manual/${orderId}/stripe-link/`,
    {}
  );
  return response.data;
};

// Look up existing customer (User account) by email for manual-order linking.
// Returns { matches: [{id, email, name}] }, up to 5 entries.
export const manualOrderCustomerLookup = async (facilityId, email) => {
  const response = await apiClient.get(
    `store/facilities/${facilityId}/orders/manual/customer-lookup/`,
    { params: { email } }
  );
  return response.data;
};

// Get a FedEx shipping quote for a manual order before creation.
// Body: { box_size_id, destination: {address_line1?, city?, state?, postal_code}, residential? weight_lbs? }
// Returns: { rate, service, discount_applied?, raw }
export const getManualOrderShippingQuote = async (facilityId, payload) => {
  const response = await apiClient.post(
    `store/facilities/${facilityId}/orders/manual/shipping-quote/`,
    payload,
    { timeout: 30000 }
  );
  return response.data;
};

// Get packing snapshot for an order
export const getPackingSnapshot = async (facilityId, orderId, mode = 'customer') => {
  try {
    console.log("[API] Fetching packing snapshot:", { facilityId, orderId, mode });
    const response = await apiClient.get(`store/facilities/${facilityId}/shipping/orders/${orderId}/packing-snapshot/?mode=${mode}`);
    console.log("[API] Packing snapshot loaded successfully");
    return response.data;
  } catch (error) {
    console.error("[API] Error fetching packing snapshot:", error.response?.data || error.message);
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      throw new Error('Authentication required - please log in');
    }
    
    // Check if we got HTML instead of JSON (indicates proxy/routing issue)
    const contentType = error.response?.headers?.['content-type'] || '';
    if (contentType.includes('text/html')) {
      throw new Error('Configuration error: Got SPA HTML instead of API response');
    }
    
    throw error;
  }
};

export const getOpsCurrentQuote = async (facilityId, orderId) => {
  try {
    console.log("[API] Fetching ops current quote:", { facilityId, orderId });
    const response = await apiClient.get(`store/facilities/${facilityId}/shipping/orders/${orderId}/ops-current-quote/`);
    console.log("[API] Ops quote loaded successfully");
    return response.data;
  } catch (error) {
    console.error("[API] Error fetching ops quote:", error.response?.data || error.message);
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      throw new Error('Authentication required - please log in');
    }
    
    // Check if we got HTML instead of JSON (indicates proxy/routing issue)
    const contentType = error.response?.headers?.['content-type'] || '';
    if (contentType.includes('text/html')) {
      throw new Error('Configuration error: Got SPA HTML instead of API response');
    }
    
    throw error;
  }
};

export const getPackingEstimateForOrder = async (facilityId, orderId) => {
  try {
    console.log("[API] Fetching packing estimate for order:", orderId, "facility:", facilityId);
    const response = await apiClient.get(`shipping/facility/${facilityId}/orders/${orderId}/packing-estimate/`);
    console.log("[API] Packing estimate loaded successfully");
    return response.data;
  } catch (error) {
    // Serialise: axios error bodies logged raw print as "[object Object]"
    // in the drawer console, which said nothing.
    console.error("[API] Error fetching packing estimate:",
      JSON.stringify(error.response?.data) || error.message);
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      throw new Error('Authentication required - please log in');
    }
    
    // Check if we got HTML instead of JSON (indicates proxy/routing issue)
    const contentType = error.response?.headers?.['content-type'] || '';
    if (contentType.includes('text/html')) {
      throw new Error('Configuration error: Got SPA HTML instead of API response');
    }
    
    // Not an error if estimate unavailable - just return null
    if (error.response?.status === 404 || error.response?.status === 400) {
      return null;
    }
    
    throw error;
  }
};

// Species Management API Functions

export const createSpecies = async (facilityId, speciesData) => {
  try {
    const response = await apiClient.post(`facility/species/${facilityId}/`, speciesData);
    return response.data;
  } catch (error) {
    console.error('Error creating species:', error);
    throw error;
  }
};

export const updateSpecies = async (speciesId, speciesData) => {
  try {
    const response = await apiClient.put(`facility/species/detail/${speciesId}/update/`, speciesData);
    return response.data;
  } catch (error) {
    console.error('Error updating species:', error);
    throw error;
  }
};

// I-03 S3-01.8 — the species publish gate. A species starts as a DRAFT
// and works fully on the facility side; publishing is a deliberate click
// that puts it on the public site. The backend refuses with a 409 whose
// body carries { reason, reason_label, detail } when the species has no
// cover photo or no care guide — callers should surface `detail`.
export const publishSpecies = async (speciesId) => {
  try {
    const response = await apiClient.post(`facility/species/${speciesId}/publish/`);
    return response.data;
  } catch (error) {
    console.error('Error publishing species:', error);
    throw error;
  }
};

export const unpublishSpecies = async (speciesId) => {
  try {
    const response = await apiClient.post(`facility/species/${speciesId}/unpublish/`);
    return response.data;
  } catch (error) {
    console.error('Error unpublishing species:', error);
    throw error;
  }
};

// I-03 close 1c: "not sold here" (Owner/Manager). Body is exactly
// {not_sold_here: <bool>}; turning it on takes the species' listings down
// server-side. Returns the species payload plus `delisted` (variation ids).
export const setSpeciesSelling = async (speciesId, notSoldHere) => {
  const response = await apiClient.post(
    `facility/species/${speciesId}/selling/`,
    { not_sold_here: Boolean(notSoldHere) },
  );
  return response.data;
};

// R-CLUTCH-LEDGER slice 2b (item 6a): "the parents raise the tadpoles"
// (Owner/Manager). Body is exactly {parent_raised_tadpoles: <bool>}; answers
// the species payload. A parent-raised clutch gets no tadpole feeding.
export const setSpeciesParentRaised = async (speciesId, parentRaised) => {
  const response = await apiClient.post(
    `facility/species/${speciesId}/parent-raised/`,
    { parent_raised_tadpoles: Boolean(parentRaised) },
  );
  return response.data;
};

export const deleteSpecies = async (speciesId, options = {}) => {
  // S-24.13 — when `force=true`, send `force=1` so the backend
  // proceeds past the operator-confirmed-link curation warning (409).
  // Default behavior is unchanged for callers that don't opt in.
  // PERF 2026-09-07 — 45s + one retry (see HEAVY_WRITE_TIMEOUT_MS).
  // Deleting a photographed species used to outlive the 15s global,
  // COMMIT anyway, and then 404 the operator's retry. The server no
  // longer takes that long and now answers 204 when the species is
  // already gone, so the retry converges instead of contradicting.
  try {
    const config = {
      timeout: HEAVY_WRITE_TIMEOUT_MS,
      ...(options.force ? { params: { force: 1 } } : {}),
    };
    const response = await withTimeoutRetry(() => apiClient.delete(
      `facility/species/detail/${speciesId}/delete/`,
      config,
    ));
    return response.data;
  } catch (error) {
    console.error('Error deleting species:', error);
    throw error;
  }
};

// Morph Management API Functions - All facility-scoped

// Deprecated - DO NOT USE - Removed to prevent 403 errors
// export const getAllMorphs = async () => { ... }
// Use getSpeciesMorphs(facilityId, speciesId) instead

export const getSpeciesMorphs = async (facilityId, speciesId) => {
  if (!facilityId || !speciesId) {
    throw new Error('Both facilityId and speciesId are required');
  }
  try {
    const response = await apiClient.get(`facility/morphs/${speciesId}/?facility_id=${facilityId}`);
    console.log("Species morphs fetched from API:", response.data);
    
    const data = response?.data ?? {};
    // Backend shape is an object: { assigned: [...], available: [...] }
    const assigned = Array.isArray(data.assigned) ? data.assigned : [];
    const available = Array.isArray(data.available) ? data.available : [];
    
    console.log("Normalized morphs response:", { assigned: assigned.length, available: available.length });
    return { assigned, available };
  } catch (error) {
    console.error("Error fetching species morphs:", error);
    throw error;
  }
};

export const createMorph = async (facilityId, morphData) => {
  if (!facilityId) {
    throw new Error('facilityId is required for morph creation');
  }
  try {
    const response = await apiClient.post(`facility/morphs/?facility_id=${facilityId}`, morphData);
    return response.data;
  } catch (error) {
    console.error('Error creating morph:', error);
    throw error;
  }
};

export const updateMorph = async (facilityId, morphId, morphData) => {
  if (!facilityId || !morphId) {
    throw new Error('Both facilityId and morphId are required');
  }
  try {
    const response = await apiClient.patch(`facility/morphs/${morphId}/update/?facility_id=${facilityId}`, morphData);
    return response.data;
  } catch (error) {
    console.error('Error updating morph:', error);
    throw error;
  }
};

export const assignMorphsToSpecies = async (facilityId, speciesId, assignedMorphIds) => {
  if (!facilityId || !speciesId) {
    throw new Error('Both facilityId and speciesId are required');
  }
  try {
    console.log(`[API] Assigning morphs to species ${speciesId}:`, assignedMorphIds);
    const response = await apiClient.put(`facility/species/${speciesId}/morphs/assign/?facility_id=${facilityId}`, {
      assigned_ids: assignedMorphIds
    });
    console.log(`[API] Assignment response:`, response.data);
    return response.data;
  } catch (error) {
    console.error('Error assigning morphs to species:', error);
    throw error;
  }
};

// ==========================================
// MEDIAHUB ERROR UTILITIES
// ==========================================

/**
 * Validates facility context for MediaHub operations
 * @param {number|string} facilityId - The facility ID to validate
 * @param {string} operation - The operation being performed (for error context)
 * @throws {Error} - If facility context is invalid
 */
const validateFacilityContext = (facilityId, operation = 'MediaHub operation') => {
  if (!facilityId || (typeof facilityId !== 'number' && typeof facilityId !== 'string')) {
    throw new Error(`Facility context is required for ${operation}. Please select a facility and try again.`);
  }
};

/**
 * Enhanced error mapping for MediaHub API responses
 * @param {Error} error - The error object from API call
 * @param {string} context - Context for the error (e.g., 'uploading media', 'creating tag')
 * @returns {Error} - Enhanced error with user-friendly message
 */
// Helper to check if an error is a cancelation
const isCanceledError = (error) => {
  return error.name === 'CanceledError' || 
         error.name === 'AbortError' ||
         error.code === 'ERR_CANCELED' || 
         error.message === 'canceled' ||
         error.message === 'Request aborted' ||
         (typeof axios !== 'undefined' && axios.isCancel && axios.isCancel(error));
};

const enhanceMediaError = (error, context = 'performing operation') => {
  // Handle cancelled requests silently
  if (isCanceledError(error)) {
    // Re-throw as is to be caught by the component
    throw error;
  }
  
  const status = error?.response?.status;
  const serverMessage = error?.response?.data?.message || error?.response?.data?.detail || error?.response?.data?.error;

  // Adversarial review MED-1 (2026-05-27): DRF returns 400s with
  // field-level errors as `{"asset_id": ["This image's license..."]}`
  // — none of message/detail/error are populated. Flatten the field
  // errors so operator-facing messages surface the actual rejection
  // reason (e.g. the license-guard explanation) instead of falling
  // through to the generic "Invalid request while linking asset".
  const flattenDRFFieldErrors = (data) => {
    if (!data || typeof data !== 'object') return null;
    const parts = [];
    for (const [key, value] of Object.entries(data)) {
      if (key === 'message' || key === 'detail' || key === 'error' ||
          key === 'error_type' || key === 'field_errors') {
        continue;
      }
      if (Array.isArray(value)) {
        parts.push(...value.map(String));
      } else if (typeof value === 'string') {
        parts.push(value);
      }
    }
    return parts.length ? parts.join(' ') : null;
  };
  const fieldMessage = status === 400
    ? flattenDRFFieldErrors(error?.response?.data)
    : null;

  let message;

  switch (status) {
    case 400:
      message = serverMessage || fieldMessage || `Invalid request while ${context}`;
      break;
    case 403:
      message = `Access denied: insufficient permissions for ${context}`;
      break;
    case 404:
      message = serverMessage || `Resource not found while ${context}`;
      break;
    case 409:
      message = serverMessage || `Conflict: resource already exists while ${context}`;
      break;
    case 413:
      message = `File too large while ${context}. Please choose a smaller file.`;
      break;
    case 422:
      message = serverMessage || `Invalid data while ${context}`;
      break;
    case 429:
      message = `Rate limit exceeded while ${context}. Please wait and try again.`;
      break;
    case 500:
      message = `Server error while ${context}. Please try again later.`;
      break;
    case 502:
    case 503:
    case 504:
      message = `Service temporarily unavailable while ${context}. Please try again later.`;
      break;
    default:
      message = serverMessage || `Failed to complete ${context}`;
  }
  
  const enhancedError = new Error(message);
  enhancedError.originalError = error;
  enhancedError.status = status;
  enhancedError.context = context;
  
  return enhancedError;
};

// ==========================================
// MEDIAHUB API FUNCTIONS
// ==========================================

/**
 * List media assets with filtering and pagination
 * @param {Object} params - Query parameters
 * @param {string} params.q - Search term
 * @param {Array} params.tags - Array of tag names
 * @param {number} params.page - Page number (1-based)
 * @param {number} params.pageSize - Items per page
 * @param {boolean} params.unlinkedOnly - Show only unlinked assets
 * @param {string} params.dateFrom - Start date filter
 * @param {string} params.dateTo - End date filter
 * @param {number|string} params.facility - Facility ID (required)
 * @returns {Promise<Object>} Assets list with pagination: {items, page, total, hasNext, hasPrev}
 */
export const mediaListAssets = async ({
  q,
  tags = [],
  page = 1,
  pageSize = 24,
  unlinkedOnly = false,
  dateFrom,
  dateTo,
  facility,
  // External-source opt-in (P-08-Images). CSV of source keys to
  // include alongside the facility's own uploads, e.g.
  // 'uploaded,inaturalist,smithsonian_open_access'. Undefined =
  // legacy default (facility's own uploads only). Cowork QA Run 39
  // 2026-05-27 caught the bug where this param was missing from
  // the destructure, so the checkbox state was never reaching the
  // wire.
  sources,
  // S-24.16 — Media Library "Show unusable" toggle. Default-off; when
  // enabled, the backend returns both usable and unusable assets so
  // operator can find a flagged photo and restore it via the ⋮ menu.
  // Same destructure trap as `sources`: without it here, the caller's
  // param silently drops on the floor.
  include_unusable,
} = {}, options = {}) => {
  // Validate facility context
  validateFacilityContext(facility, 'loading media assets');

  try {
    const params = new URLSearchParams();

    // Add facility context first
    params.append('facility', facility);

    // Add pagination
    if (page) params.append('page', page.toString());
    if (pageSize) params.append('pageSize', pageSize.toString());

    // Add search term
    if (q) params.append('q', q);

    // Add tag filters
    if (tags.length > 0) {
      tags.forEach(tag => params.append('tags', tag));
    }

    // Add date filters
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);

    // Add unlinked filter
    if (unlinkedOnly) params.append('unlinkedOnly', 'true');

    // Add external-source filter (only when checkbox is opted in)
    if (sources) params.append('sources', sources);

    // S-24.16 — forward the include_unusable flag through to the
    // backend so the "Show unusable" toggle actually reaches the wire.
    if (include_unusable) params.append('include_unusable', 'true');

    const response = await apiClient.get(`mediahub/assets/?${params.toString()}`, options);
    
    // The backend now returns the expected format
    const data = response.data;
    return {
      items: data.items || [],
      page: data.page || page,
      total: data.total || 0,
      hasNext: data.hasNext || false,
      hasPrev: data.hasPrev || false
    };
  } catch (error) {
    // Don't log canceled requests
    if (!isCanceledError(error)) {
      console.error('Error listing media assets:', error);
    }
    throw enhanceMediaError(error, 'loading media assets');
  }
};

/**
 * Get all assets linked to a specific object
 * @param {Object} params - Query parameters
 * @param {string} params.type - Object type ('species', 'product_variant', etc.)
 * @param {string} params.id - Object ID
 * @returns {Promise<Object>} Object assets: {target_model, target_id, links}
 */
export const mediaGetObjectAssets = async ({ type, id }, options = {}) => {
  if (!type || !id) {
    throw new Error('Both type and id are required');
  }
  
  try {
    const params = new URLSearchParams({
      target_model: type,
      target_id: id
    });

    const response = await apiClient.get(`mediahub/objects/assets/?${params.toString()}`, options);
    return response.data;
  } catch (error) {
    console.error('Error getting object assets:', error);
    throw enhanceMediaError(error, 'loading object assets');
  }
};

/**
 * Prepare upload for a media asset (for presigned URL pattern if implemented)
 * Currently returns upload URL for direct upload
 * @param {Object} params - Upload preparation parameters
 * @param {string} params.fileName - Original file name
 * @param {string} params.contentType - File MIME type
 * @param {string} params.facilityId - Facility ID
 * @returns {Promise<Object>} Upload preparation: {uploadUrl, assetTempId, fields}
 */
export const mediaPrepareUpload = async ({ fileName, contentType, facilityId }) => {
  try {
    // For now, return direct upload URL since MediaHub uses direct upload
    // This can be extended later for presigned URLs
    return {
      uploadUrl: `${API_BASE_URL}/mediahub/assets/upload/`,
      assetTempId: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      fields: {
        facility: facilityId,
        content_type: contentType,
        original_name: fileName
      }
    };
  } catch (error) {
    console.error('Error preparing upload:', error);
    throw new Error('Failed to prepare upload');
  }
};

/**
 * Finalize upload and create asset record
 * @param {Object} params - Finalization parameters
 * @param {string} params.assetTempId - Temporary asset ID from preparation
 * @param {Object} params.meta - Asset metadata
 * @param {File} params.file - File to upload
 * @param {string} params.facilityId - Facility ID
 * @returns {Promise<Object>} Created asset
 */
export const mediaFinalizeUpload = async ({ assetTempId, meta = {}, file, facilityId }, options = {}) => {
  // Validate facility context
  validateFacilityContext(facilityId, 'uploading files');

  try {
    const formData = new FormData();
    formData.append('file', file);

    // Add metadata
    if (meta.caption) formData.append('caption', meta.caption);
    if (meta.is_public !== undefined) formData.append('is_public', meta.is_public ? '1' : '0');

    // Handle tags - support both new format (meta.tags) and legacy (meta.tag_names)
    const tagsToSend = meta.tags || meta.tag_names || [];
    console.log('[mediaFinalizeUpload] Tags to send:', tagsToSend);
    if (Array.isArray(tagsToSend) && tagsToSend.length > 0) {
      // Send as JSON string to avoid file copy issues in backend
      formData.append('tags', JSON.stringify(tagsToSend));
    }

    // Use facility-scoped URL pattern (standardized across all mediahub endpoints)
    const response = await apiClient.post(`mediahub/assets/facility/${facilityId}/upload/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      ...options
    });

    return response.data.asset || response.data;
  } catch (error) {
    console.error('Error finalizing upload:', error);
    throw enhanceMediaError(error, 'uploading file');
  }
};

/**
 * Link an asset to an object
 * @param {Object} params - Link parameters
 * @param {string} params.assetId - Asset ID
 * @param {string} params.objectType - Object type ('species', 'product_variant', etc.)
 * @param {string} params.objectId - Object ID
 * @param {string} params.role - Link role ('primary', 'gallery', etc.)
 * @param {number} params.position - Position in sequence
 * @param {string} params.facilityId - Facility ID
 * @returns {Promise<Object>} Created link
 */
export const mediaLinkAsset = async ({ 
  assetId, 
  objectType, 
  objectId, 
  role = 'gallery', 
  position = 0, 
  facilityId 
}, options = {}) => {
  if (!assetId || !objectType || !objectId) {
    throw new Error('assetId, objectType, and objectId are required');
  }
  
  try {
    const response = await apiClient.post('mediahub/links/create/', {
      asset_id: assetId,
      target_model: objectType,
      target_id: objectId,
      role,
      position,
      facility_id: facilityId
    }, options);
    
    // D21 (2026-07-29): surface already_linked to callers; keep `link`
    // spread for backwards compatibility.
    const payload = response.data || {};
    return { ...(payload.link || {}), already_linked: !!payload.already_linked };
  } catch (error) {
    console.error('Error linking asset:', error);
    throw enhanceMediaError(error, 'linking asset');
  }
};

/**
 * Unlink an asset from an object
 * @param {Object} params - Unlink parameters
 * @param {string} params.linkId - Link ID to delete
 * @returns {Promise<void>}
 */
export const mediaUnlinkAsset = async ({ linkId }, options = {}) => {
  if (!linkId) {
    throw new Error('linkId is required');
  }
  
  try {
    await apiClient.delete(`mediahub/links/${linkId}/`, options);
  } catch (error) {
    console.error('Error unlinking asset:', error);
    throw enhanceMediaError(error, 'unlinking asset');
  }
};

/**
 * Update asset metadata
 * @param {Object} params - Update parameters
 * @param {string} params.assetId - Asset ID
 * @param {string} params.name - New asset name/caption
 * @param {Array} params.tags - Array of tag names
 * @returns {Promise<Object>} Updated asset
 */
export const mediaUpdateAsset = async ({ assetId, name, tags = [] }, options = {}) => {
  if (!assetId) {
    throw new Error('assetId is required');
  }
  
  try {
    const updateData = {};
    if (name !== undefined) updateData.caption = name;
    if (tags.length > 0) updateData.tags = tags;
    
    const response = await apiClient.patch(`mediahub/assets/${assetId}/`, updateData, options);
    return response.data;
  } catch (error) {
    console.error('Error updating asset:', error);
    throw enhanceMediaError(error, 'updating asset');
  }
};

/**
 * Delete an asset (soft-delete if supported)
 * @param {Object} params - Delete parameters
 * @param {string} params.assetId - Asset ID
 * @returns {Promise<void>}
 */
export const mediaDeleteAsset = async ({ assetId }, options = {}) => {
  if (!assetId) {
    throw new Error('assetId is required');
  }
  
  try {
    await apiClient.delete(`mediahub/assets/${assetId}/`, options);
  } catch (error) {
    console.error('Error deleting asset:', error);
    throw enhanceMediaError(error, 'deleting asset');
  }
};

/**
 * Bulk delete multiple assets
 * @param {Object} params
 * @param {string} params.facilityId - Facility ID
 * @param {string[]} params.assetIds - Array of asset IDs to delete
 * @returns {Promise<{deleted: number, failures: Array}>}
 */
export const mediaBulkDeleteAssets = async ({ facilityId, assetIds }, options = {}) => {
  validateFacilityContext(facilityId, 'bulk deleting assets');
  if (!assetIds || assetIds.length === 0) {
    throw new Error('assetIds is required');
  }

  try {
    const response = await apiClient.post(
      `mediahub/assets/facility/${facilityId}/bulk/delete/`,
      { asset_ids: assetIds },
      options
    );
    return response.data;
  } catch (error) {
    console.error('Error bulk deleting assets:', error);
    throw enhanceMediaError(error, 'bulk deleting assets');
  }
};

/**
 * Generate poster thumbnails for video assets missing them (synchronous)
 * @param {Object} params
 * @param {string} params.facilityId - Facility ID
 * @param {string[]} [params.assetIds] - Optional specific asset IDs
 * @returns {Promise<{processed: number, created: number, failures: Array}>}
 */
export const mediaGeneratePosters = async ({ facilityId, assetIds }, options = {}) => {
  validateFacilityContext(facilityId, 'generating posters');

  try {
    const body = assetIds ? { asset_ids: assetIds } : {};
    const response = await apiClient.post(
      `mediahub/assets/facility/${facilityId}/generate-posters/`,
      body,
      options
    );
    return response.data;
  } catch (error) {
    console.error('Error generating posters:', error);
    throw enhanceMediaError(error, 'generating posters');
  }
};

/**
 * List available tags with optional search
 * @param {Object} params - Query parameters
 * @param {string} params.q - Search term for tag names
 * @param {number|string} params.facility - Facility ID (required)
 * @returns {Promise<Array>} Array of tags
 */
export const mediaListTags = async ({ q = '', facility, limit } = {}, options = {}) => {
  // Validate facility context
  validateFacilityContext(facility, 'loading tags');

  try {
    const params = new URLSearchParams();
    params.append('facility', facility);
    // Run 17 A4: the server reads `q` (and honors `limit`); this wrapper
    // sent `search`, which the view silently ignored — tag suggestions
    // were never filtered and the limit callers passed was dropped.
    if (q) params.append('q', q);
    if (limit) params.append('limit', String(limit));
    
    const response = await apiClient.get(`mediahub/tags/?${params.toString()}`, options);
    return response.data.results || response.data;
  } catch (error) {
    // Don't log canceled requests
    if (!isCanceledError(error)) {
      console.error('Error listing tags:', error);
    }
    throw enhanceMediaError(error, 'loading tags');
  }
};

/**
 * Create a new tag
 * @param {Object} params - Creation parameters
 * @param {string} params.name - Tag name
 * @returns {Promise<Object>} Created tag
 */
export const mediaCreateTag = async ({ name }, options = {}) => {
  if (!name) {
    throw new Error('Tag name is required');
  }
  
  try {
    const response = await apiClient.post('mediahub/tags/', { name }, options);
    return response.data;
  } catch (error) {
    console.error('Error creating tag:', error);
    throw enhanceMediaError(error, 'creating tag');
  }
};

/**
 * Update asset tags
 * @param {string} assetId - Asset ID 
 * @param {Array<string>} tags - Array of tag names
 * @param {Object} options - Request options (signal, etc.)
 * @returns {Promise<Object>} Updated asset
 */
export const mediaUpdateAssetTags = async (assetId, tags = [], options = {}) => {
  if (!assetId) {
    throw new Error('assetId is required');
  }
  
  // Ensure tags is an array
  const tagNames = Array.isArray(tags) ? tags : [];
  
  console.log('[mediaUpdateAssetTags] Updating tags for asset:', assetId, 'with tags:', tagNames);
  
  try {
    const response = await apiClient.patch(`mediahub/assets/${assetId}/`, {
      tags: tagNames
    }, options);
    
    console.log('[mediaUpdateAssetTags] Response:', response.data);
    return response.data;
  } catch (error) {
    // Don't log canceled requests
    if (!isCanceledError(error)) {
      console.error('[mediaUpdateAssetTags] Error updating asset tags:', error);
      console.error('[mediaUpdateAssetTags] Error response:', error.response?.data);
    }
    throw enhanceMediaError(error, 'updating asset tags');
  }
};

/**
 * Bulk add tags to multiple assets
 * @param {string} facilityId - Facility ID
 * @param {Array<string>} assetIds - Array of asset IDs (max 200)
 * @param {Array<string>} tags - Array of tag names to add
 * @param {Object} options - Request options (signal, etc.)
 * @returns {Promise<Object>} { success: number, failures: Array, tags_added: Array }
 */
export const mediaBulkAddTags = async (facilityId, assetIds, tags, options = {}) => {
  if (!facilityId) {
    throw new Error('facilityId is required');
  }

  if (!Array.isArray(assetIds) || assetIds.length === 0) {
    throw new Error('assetIds must be a non-empty array');
  }

  if (!Array.isArray(tags) || tags.length === 0) {
    throw new Error('tags must be a non-empty array');
  }

  if (assetIds.length > 200) {
    throw new Error('Maximum 200 assets per request');
  }

  console.log('[mediaBulkAddTags] Adding tags to assets:', assetIds.length, 'assets, tags:', tags, 'facility:', facilityId);

  try {
    const response = await apiClient.post(`mediahub/assets/facility/${facilityId}/bulk/add-tags/`, {
      asset_ids: assetIds,
      tags,
      facility_id: facilityId  // Belt-and-suspenders: include in body too
    }, options);

    console.log('[mediaBulkAddTags] Response:', response.data);
    return response.data;
  } catch (error) {
    if (!isCanceledError(error)) {
      console.error('[mediaBulkAddTags] Error bulk adding tags:', error);
      console.error('[mediaBulkAddTags] Error response:', error.response?.data);
    }
    throw enhanceMediaError(error, 'bulk adding tags');
  }
};

/**
 * Bulk remove tags from multiple assets
 * @param {string} facilityId - Facility ID
 * @param {Array<string>} assetIds - Array of asset IDs (max 200)
 * @param {Array<string>} tags - Array of tag names to remove
 * @param {Object} options - Request options (signal, etc.)
 * @returns {Promise<Object>} { success: number, failures: Array, tags_removed: Array }
 */
export const mediaBulkRemoveTags = async (facilityId, assetIds, tags, options = {}) => {
  if (!facilityId) {
    throw new Error('facilityId is required');
  }

  if (!Array.isArray(assetIds) || assetIds.length === 0) {
    throw new Error('assetIds must be a non-empty array');
  }

  if (!Array.isArray(tags) || tags.length === 0) {
    throw new Error('tags must be a non-empty array');
  }

  if (assetIds.length > 200) {
    throw new Error('Maximum 200 assets per request');
  }

  console.log('[mediaBulkRemoveTags] Removing tags from assets:', assetIds.length, 'assets, tags:', tags, 'facility:', facilityId);

  try {
    const response = await apiClient.post(`mediahub/assets/facility/${facilityId}/bulk/remove-tags/`, {
      asset_ids: assetIds,
      tags,
      facility_id: facilityId  // Belt-and-suspenders: include in body too
    }, options);

    console.log('[mediaBulkRemoveTags] Response:', response.data);
    return response.data;
  } catch (error) {
    if (!isCanceledError(error)) {
      console.error('[mediaBulkRemoveTags] Error bulk removing tags:', error);
      console.error('[mediaBulkRemoveTags] Error response:', error.response?.data);
    }
    throw enhanceMediaError(error, 'bulk removing tags');
  }
};

/**
 * Rename a tag atomically across all assets
 * @param {string} facilityId - Facility ID
 * @param {string} fromName - Current tag name
 * @param {string} toName - New tag name
 * @param {Object} options - Request options (signal, etc.)
 * @returns {Promise<Object>} { message, from_name, to_name, assets_updated }
 */
export const mediaRenameTag = async (facilityId, fromName, toName, options = {}) => {
  if (!facilityId || !fromName || !toName) {
    throw new Error('facilityId, fromName, and toName are required');
  }

  console.log('[mediaRenameTag] Renaming tag:', fromName, '→', toName, 'facility:', facilityId);

  try {
    const response = await apiClient.post(`mediahub/tags/facility/${facilityId}/rename/`, {
      facility: facilityId,
      facility_id: facilityId,  // Belt-and-suspenders: include in body too
      from_name: fromName,
      to_name: toName
    }, options);

    console.log('[mediaRenameTag] Response:', response.data);
    return response.data;
  } catch (error) {
    if (!isCanceledError(error)) {
      console.error('[mediaRenameTag] Error renaming tag:', error);
      console.error('[mediaRenameTag] Error response:', error.response?.data);
    }
    throw enhanceMediaError(error, 'renaming tag');
  }
};

/**
 * Delete a tag (optionally replace with another tag if in use)
 * @param {string} facilityId - Facility ID
 * @param {string} tagName - Tag name to delete
 * @param {string} replaceWith - Optional replacement tag name (required if tag is in use)
 * @param {Object} options - Request options (signal, etc.)
 * @returns {Promise<Object>} { message, name, assets_updated, replaced_with? }
 */
export const mediaDeleteTag = async (facilityId, tagName, replaceWith = null, options = {}) => {
  if (!facilityId || !tagName) {
    throw new Error('facilityId and tagName are required');
  }

  console.log('[mediaDeleteTag] Deleting tag:', tagName, replaceWith ? `(replace with: ${replaceWith})` : '(no replacement)', 'facility:', facilityId);

  try {
    const payload = {
      facility: facilityId,
      facility_id: facilityId,  // Belt-and-suspenders: include in body too
      name: tagName
    };

    if (replaceWith) {
      payload.replace_with = replaceWith;
    }

    const response = await apiClient.post(`mediahub/tags/facility/${facilityId}/delete/`, payload, options);

    console.log('[mediaDeleteTag] Response:', response.data);
    return response.data;
  } catch (error) {
    if (!isCanceledError(error)) {
      console.error('[mediaDeleteTag] Error deleting tag:', error);
      console.error('[mediaDeleteTag] Error response:', error.response?.data);
    }
    throw enhanceMediaError(error, 'deleting tag');
  }
};

// Robust facility assets fetcher
export async function fetchFacilityAssets(facilityId, { pageSize = 24, ordering = '-created_at' } = {}) {
  const params = new URLSearchParams();
  params.set('facility', facilityId);
  params.set('page_size', String(pageSize));
  params.set('ordering', ordering);

  const endpoint = `mediahub/assets/?${params.toString()}`;

  try {
    console.log('[fetchFacilityAssets] Making request to:', endpoint);
    console.log('[fetchFacilityAssets] Full URL will be:', `${apiClient.defaults.baseURL}${endpoint}`);
    
    // Use the same pattern as other MediaHub calls (with manual query string)
    const response = await apiClient.get(endpoint);
    
    console.log('[fetchFacilityAssets] Response status:', response.status);
    console.log('[fetchFacilityAssets] Response headers Content-Type:', response.headers['content-type']);
    console.log('[fetchFacilityAssets] Response data type:', typeof response.data);
    
    const data = response.data;
    
    // Normalize the response - handle different API response formats
    const assets = data?.results || data?.items || data || [];
    
    console.log('[fetchFacilityAssets] Found assets:', assets.length);
    
    return assets.map(asset => ({
      id: asset.id,
      thumb_url: asset.thumb_url || asset.thumbnail_url,
      file_url: asset.file_url,
      original_name: asset.original_filename || asset.filename || asset.name || 'Untitled',
      created_at: asset.created_at
    }));
  } catch (error) {
    console.error('[fetchFacilityAssets] Error:', error);
    console.error('[fetchFacilityAssets] Error config URL:', error.config?.url);
    console.error('[fetchFacilityAssets] Error response status:', error.response?.status);
    console.error('[fetchFacilityAssets] Error response data:', error.response?.data);
    
    // Enhanced diagnostic for HTML responses
    if (typeof error.response?.data === 'string' && error.response.data.startsWith('<!doctype')) {
      console.error('[fetchFacilityAssets] Received HTML, likely wrong path—should be /api/mediahub/assets/?facility=...');
    }
    
    // Create structured error for the UI
    const errorInfo = {
      status: error.response?.status || 'unknown',
      url: error.config?.url || 'unknown',
      bodySnippet: typeof error.response?.data === 'string' 
        ? error.response.data.substring(0, 500)
        : JSON.stringify(error.response?.data || {}).substring(0, 500)
    };
    
    throw new Error(JSON.stringify(errorInfo));
  }
}

// List media assets by tags for a facility (keeping for backward compatibility)
export async function listMediaAssetsByTags({ facilityId, tags = [], limit = 1 }) {
  const params = new URLSearchParams();
  params.set('facility', facilityId);
  if (tags.length) params.set('tags', tags.join(','));
  params.set('page_size', String(limit));
  params.set('ordering', '-created_at'); // newest first

  const res = await fetch(`/api/mediahub/assets/?${params.toString()}`, {
    credentials: 'include'
  });
  
  if (!res.ok) {
    const errorText = await res.text();
    console.error('Failed to fetch media assets:', errorText);
    throw new Error('Failed to fetch media assets');
  }
  
  return await res.json();
}

// SITE PAGES API - Public endpoints for documentation content
export const getSitePage = async (slug) => {
  const response = await apiClient.get(`public/site-pages/${slug}/`);
  return response.data;
};

export const getSitePages = async () => {
  const response = await apiClient.get('public/site-pages/');
  return response.data;
};

// Care guides (public read-only)
export async function getPublicSpeciesCareGuide(speciesId) {
  const { data } = await apiClient.get(`/public/species/${speciesId}/care-guide/`);
  return data; // { overview_md, overview_html, care_guide_md, care_guide_html, species_id, updated_at }
}

// #103 zero-inventory degradation — public species list (inventory-
// agnostic; genus/species/locality/common_names + cover urls +
// care_guide_available). Feeds the homepage Species Spotlight and the
// /frogs learn-mode grids. Optional exact-genus filter.
export async function getPublicSpecies({ genus } = {}) {
  const params = { page_size: 200 };
  if (genus) params.genus = genus;
  const { data } = await apiClient.get('public/species/', { params });
  return Array.isArray(data?.results) ? data.results : [];
}

// #103 — anonymous "notify me when new animals arrive" capture.
// Idempotent server-side (repeat email+species returns 200
// already_subscribed). speciesId optional = interest in any arrivals.
export async function subscribeArrivals(email, speciesId = null) {
  const payload = { email };
  if (speciesId) payload.species_id = speciesId;
  const { data } = await apiClient.post('store/arrivals/subscribe/', payload);
  return data; // { subscribed: true, already_subscribed: bool }
}

// Get all species for care guides using public anonymous endpoints
export async function getAllSpeciesForCareGuides() {
  const facilityId = getActiveFacilityId(); // From appBootstrap.js - no ENV reads

  // Try facility ID first (public endpoint, no auth required)
  try {
    if (facilityId) {
      const { data } = await apiClient.get(`facility/${facilityId}/species/public/`);
      const list = Array.isArray(data) ? data : [];
      return list.map(s => ({
        ...s,
        // Use the public serializer fields
        imageUrl: s.cover_display_url || s.cover_thumb_url || null,
      }));
    }
  } catch (e) {
    console.warn('[Care Guides] Failed to fetch by facility ID:', e);
  }

  // Fallback: Try to get facility name from user context
  try {
    const userContext = getUserContext();
    const facilities = userContext?.facilities || [];

    // If user has facilities, try first facility name
    if (facilities.length > 0) {
      const facilityName = facilities[0].name || 'Feeling Froggy';
      const { data } = await apiClient.get(`facility/by-name/${encodeURIComponent(facilityName)}/species/public/`);
      const list = Array.isArray(data) ? data : [];
      return list.map(s => ({
        ...s,
        imageUrl: s.cover_display_url || s.cover_thumb_url || null,
      }));
    }

    // Last resort: default facility name
    const { data } = await apiClient.get(`facility/by-name/Feeling%20Froggy/species/public/`);
    const list = Array.isArray(data) ? data : [];
    return list.map(s => ({
      ...s,
      imageUrl: s.cover_display_url || s.cover_thumb_url || null,
    }));
  } catch (e) {
    console.warn('[Care Guides] Failed to fetch species:', e);
    return [];
  }
}

// Care guides (facility read)
export async function getSpeciesCareGuide(speciesId) {
  const { data } = await apiClient.get(`/facility/species/${speciesId}/care-guide/`);
  return data; // { overview_md, overview_html, care_guide_md, care_guide_html, species_id, updated_at }
}

// Care guides (facility write)
export async function saveSpeciesCareGuide(speciesId, payload) {
  // payload: { overview_md: string, care_guide_md: string }
  const { data } = await apiClient.put(`/facility/species/${speciesId}/care-guide/`, payload);
  return data;
}

// Legacy function name for backward compatibility
export async function patchFacilitySpeciesCareGuide(speciesId, payload) {
  return await saveSpeciesCareGuide(speciesId, payload);
}

// AI Context (facility read)
export async function getSpeciesAIContext(speciesId) {
  try {
    // Use new stable endpoint
    const { data } = await apiClient.get(`/facility/species/${speciesId}/context/`);
    // Map response fields to expected format for backward compatibility
    return {
      ai_context_froggy_md: data.ai_context_froggy_md || '',
      ai_context_frogger_md: data.ai_context_frogger_md || '',
      updated_at: data.updated_at,
      has_froggy: data.has_froggy,
      has_frogger: data.has_frogger
    };
  } catch (error) {
    if (error.response && error.response.status === 404) {
      // Return empty context on 404
      return {
        ai_context_froggy_md: '',
        ai_context_frogger_md: '',
        updated_at: null,
        has_froggy: false,
        has_frogger: false
      };
    }
    throw error;
  }
}

// AI Context (facility write)
export async function saveSpeciesAIContext(speciesId, payload) {
  // Send the fields directly as the endpoint now expects them
  const requestData = {
    ai_context_froggy_md: payload.ai_context_froggy_md || '',
    ai_context_frogger_md: payload.ai_context_frogger_md || ''
  };
  const { data } = await apiClient.patch(`/facility/species/${speciesId}/context/`, requestData);
  // Return fields directly as the serializer now returns the correct field names
  return {
    ai_context_froggy_md: data.ai_context_froggy_md || '',
    ai_context_frogger_md: data.ai_context_frogger_md || '',
    updated_at: data.updated_at,
    has_froggy: data.has_froggy,
    has_frogger: data.has_frogger
  };
}

// Facility AI Context (facility read)
export async function getFacilityAIContext(facilityId) {
  try {
    // Use new stable endpoint
    const { data } = await apiClient.get(`/facility/facility/${facilityId}/context/`);
    // Map response fields to expected format for backward compatibility
    return {
      ai_context_froggy_md: data.ai_context_froggy_md || '',
      ai_context_frogger_md: data.ai_context_frogger_md || '',
      defer_ai_guidelines: data.defer_ai_guidelines || '',
      updated_at: data.updated_at,
      has_froggy: data.has_froggy,
      has_frogger: data.has_frogger,
      facility_name: data.facility_name
    };
  } catch (error) {
    if (error.response && error.response.status === 404) {
      // Return empty context on 404
      return {
        ai_context_froggy_md: '',
        ai_context_frogger_md: '',
        defer_ai_guidelines: '',
        updated_at: null,
        has_froggy: false,
        has_frogger: false,
        facility_name: 'Facility'
      };
    }
    throw error;
  }
}

// Facility AI Context (facility write)
export async function saveFacilityAIContext(facilityId, payload) {
  // Send the fields directly as the endpoint now expects them
  const requestData = {
    ai_context_froggy_md: payload.ai_context_froggy_md || '',
    ai_context_frogger_md: payload.ai_context_frogger_md || '',
  };
  // Only include defer_ai_guidelines if it was provided
  if (payload.defer_ai_guidelines !== undefined) {
    requestData.defer_ai_guidelines = payload.defer_ai_guidelines || '';
  }
  const { data } = await apiClient.put(`/facility/facility/${facilityId}/context/`, requestData);
  // Return fields directly as the serializer now returns the correct field names
  return {
    ai_context_froggy_md: data.ai_context_froggy_md || '',
    ai_context_frogger_md: data.ai_context_frogger_md || '',
    defer_ai_guidelines: data.defer_ai_guidelines || '',
    updated_at: data.updated_at,
    has_froggy: data.has_froggy,
    has_frogger: data.has_frogger
  };
}

// Species AI Bundle (read-only, for Advisor)
export async function getSpeciesAIBundle(facilityId, speciesId) {
  const { data } = await apiClient.get(`/facility/species/${speciesId}/ai-bundle/`);
  return data;
}

// FroggyAI Chat MVP
export async function postFroggyAsk(facilityId, payload) {
  const requestData = {
    question: payload.question.trim(),
    species_id: payload.species_id || null,
    last_user_message: payload.last_user_message || "",
    pending_escalation: payload.pending_escalation,
    // Pass facility_name from payload only (no ENV fallback)
    facility_name: payload.facility_name || null
  };

  // Use the generic endpoint that accepts facility_name in body
  const { data } = await apiClient.post(`/facility/advisor/froggy/ask/`, requestData);

  // Return raw response data for new handling in FroggyChatPage
  // The component will use extractAdvisorMessage to get the text
  return data;
}

// ===========================================
// SUPPORT ESCALATION API
// ===========================================

/**
 * Generate correlation ID for tracking support escalations
 * @returns {string} Short correlation ID
 */
const generateCorrelationId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
};

/**
 * Create support escalation
 * @param {Object} payload - Escalation data
 * @param {string} payload.full_name - Customer full name (required)
 * @param {string} payload.preferred - Contact method 'email' or 'phone' (required)
 * @param {string} payload.email - Email address (required if preferred='email')
 * @param {string} payload.phone - Phone number (required if preferred='phone')
 * @param {string} payload.notes - Additional notes (optional)
 * @param {string} payload.source - Source of escalation (e.g. 'froggy_chat')
 * @param {string} payload.facility_id - Facility ID (optional)
 * @param {Object} opts - Request options
 * @param {AbortSignal} opts.signal - Abort signal for request cancellation
 * @param {number} opts.timeout - Request timeout in milliseconds (default: 30000)
 * @returns {Promise<Object>} Escalation response with correlation ID and success status
 */
export const createSupportEscalation = async (payload, opts = {}) => {
  // Generate correlation ID for tracking
  const correlationId = payload.correlation_id || generateCorrelationId();
  
  // Validate required fields
  if (!payload.full_name?.trim()) {
    throw new Error('Full name is required');
  }
  if (!['email', 'phone'].includes(payload.preferred)) {
    throw new Error('Preferred contact method must be either "email" or "phone"');
  }
  if (payload.preferred === 'email' && !payload.email?.trim()) {
    throw new Error('Email address is required when preferred method is email');
  }
  if (payload.preferred === 'phone' && !payload.phone?.trim()) {
    throw new Error('Phone number is required when preferred method is phone');
  }
  
  const requestPayload = {
    ...payload,
    correlation_id: correlationId,
    full_name: payload.full_name.trim(),
    notes: payload.notes?.trim() || ''
  };
  
  // Ensure contact info is trimmed
  if (payload.email) {
    requestPayload.email = payload.email.trim();
  }
  if (payload.phone) {
    requestPayload.phone = payload.phone.trim();
  }
  
  console.info(`[API] Creating support escalation ${correlationId}`, {
    facility_id: payload.facility_id,
    contact_method: payload.preferred,
    has_notes: (payload.notes?.trim() || '').length > 0
  });
  
  try {
    // Set up timeout and abort handling
    const timeout = opts.timeout || 30000; // 30 second default
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    // Use the signal from opts or create our own
    const signal = opts.signal || controller.signal;
    
    const response = await apiClient.post('support/escalations/', requestPayload, {
      signal,
      timeout
    });
    
    clearTimeout(timeoutId);
    
    console.info(`[API] Escalation ${correlationId} response:`, {
      status: response.status,
      ok: response.status >= 200 && response.status < 300,
      result_ok: response.data?.ok
    });
    
    return {
      ...response.data,
      correlation_id: correlationId
    };
  } catch (error) {
    // Handle timeout/abort errors
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
      console.error(`[API] Escalation ${correlationId} timed out`);
      throw new Error('Request timed out. Please check your connection and try again.');
    }
    
    // Handle API errors with status-specific messages
    if (error.response) {
      const status = error.response.status;
      let errorMsg = 'Failed to submit escalation. Please try again.';
      
      if (status === 429) {
        errorMsg = 'Too many requests. Please wait a moment before trying again.';
      } else if (status === 400 && error.response.data?.error) {
        errorMsg = error.response.data.error;
      } else if (status >= 500) {
        errorMsg = 'Server error. Please try again in a few minutes.';
      } else if (error.response.data?.error) {
        errorMsg = error.response.data.error;
      }
      
      console.error(`[API] Escalation ${correlationId} failed:`, {
        status,
        error: error.response.data
      });
      
      const enhancedError = new Error(errorMsg);
      enhancedError.status = status;
      enhancedError.correlation_id = correlationId;
      throw enhancedError;
    }
    
    // Handle network errors
    console.error(`[API] Network error for escalation ${correlationId}:`, error);
    const networkError = new Error('Network error. Please check your connection and try again.');
    networkError.correlation_id = correlationId;
    throw networkError;
  }
};

/**
 * Check support health endpoint (placeholder for future health checks)
 * @param {Object} opts - Request options
 * @param {AbortSignal} opts.signal - Abort signal for request cancellation
 * @returns {Promise<Object>} Health check response
 */
export const supportHealth = async (opts = {}) => {
  try {
    const response = await apiClient.get('support/health/', {
      signal: opts.signal
    });
    return response.data;
  } catch (error) {
    console.error('[API] Support health check failed:', error);
    throw error;
  }
};

// ============================================================================
// Promotions API (Phase D)
// ============================================================================

/**
 * Get list of promotions for a facility
 * @param {string} facilityId - Facility UUID
 * @param {Object} params - Query parameters (is_active, promotion_type, scope, search, page, page_size)
 * @returns {Promise<Object>} Paginated list of promotions
 */
export const getPromotions = async (facilityId, params = {}) => {
  try {
    const response = await apiClient.get(`store/facilities/${facilityId}/promotions/`, { params });
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch promotions:', error);
    throw error;
  }
};

/**
 * Get single promotion detail
 * @param {string} facilityId - Facility UUID
 * @param {string} promotionId - Promotion UUID
 * @returns {Promise<Object>} Promotion detail
 */
export const getPromotion = async (facilityId, promotionId) => {
  try {
    const response = await apiClient.get(`store/facilities/${facilityId}/promotions/${promotionId}/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch promotion:', error);
    throw error;
  }
};

/**
 * Create new promotion
 * @param {string} facilityId - Facility UUID
 * @param {Object} data - Promotion data
 * @returns {Promise<Object>} Created promotion
 */
export const createPromotion = async (facilityId, data) => {
  try {
    const response = await apiClient.post(`store/facilities/${facilityId}/promotions/`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to create promotion:', error);
    throw error;
  }
};

/**
 * Update promotion (full update)
 * @param {string} facilityId - Facility UUID
 * @param {string} promotionId - Promotion UUID
 * @param {Object} data - Promotion data
 * @returns {Promise<Object>} Updated promotion
 */
export const updatePromotion = async (facilityId, promotionId, data) => {
  try {
    const response = await apiClient.put(`store/facilities/${facilityId}/promotions/${promotionId}/`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to update promotion:', error);
    throw error;
  }
};

/**
 * Partial update promotion
 * @param {string} facilityId - Facility UUID
 * @param {string} promotionId - Promotion UUID
 * @param {Object} data - Partial promotion data
 * @returns {Promise<Object>} Updated promotion
 */
export const patchPromotion = async (facilityId, promotionId, data) => {
  try {
    const response = await apiClient.patch(`store/facilities/${facilityId}/promotions/${promotionId}/`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to patch promotion:', error);
    throw error;
  }
};

/**
 * Delete promotion (soft delete - sets is_active=false)
 * @param {string} facilityId - Facility UUID
 * @param {string} promotionId - Promotion UUID
 * @returns {Promise<void>}
 */
export const deletePromotion = async (facilityId, promotionId) => {
  try {
    const response = await apiClient.delete(`store/facilities/${facilityId}/promotions/${promotionId}/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to delete promotion:', error);
    throw error;
  }
};

/**
 * Clone promotion
 * @param {string} facilityId - Facility UUID
 * @param {string} promotionId - Promotion UUID
 * @returns {Promise<Object>} Cloned promotion
 */
export const clonePromotion = async (facilityId, promotionId) => {
  try {
    const response = await apiClient.post(`store/facilities/${facilityId}/promotions/${promotionId}/clone/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to clone promotion:', error);
    throw error;
  }
};

// ============================================================================
// Task System API Functions (Phase C)
// ============================================================================

/**
 * Get task templates for a facility
 * @param {string} facilityId - Facility UUID
 * @returns {Promise<Array>} List of task templates
 */
export const getTaskTemplates = async (facilityId) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/tasks/templates/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch task templates:', error);
    throw error;
  }
};

/**
 * Create a new task template
 * @param {string} facilityId - Facility UUID
 * @param {Object} templateData - Template data
 * @returns {Promise<Object>} Created template
 */
export const createTaskTemplate = async (facilityId, templateData) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/tasks/templates/`, templateData);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to create task template:', error);
    throw error;
  }
};

/**
 * Update a task template
 * @param {string} facilityId - Facility UUID
 * @param {string} templateId - Template UUID
 * @param {Object} templateData - Template data
 * @returns {Promise<Object>} Updated template
 */
export const updateTaskTemplate = async (facilityId, templateId, templateData) => {
  try {
    const response = await apiClient.patch(`facility/facilities/${facilityId}/tasks/templates/${templateId}/`, templateData);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to update task template:', error);
    throw error;
  }
};

/**
 * Delete a task template
 * @param {string} facilityId - Facility UUID
 * @param {string} templateId - Template UUID
 * @returns {Promise<void>}
 */
export const deleteTaskTemplate = async (facilityId, templateId) => {
  try {
    await apiClient.delete(`facility/facilities/${facilityId}/tasks/templates/${templateId}/`);
  } catch (error) {
    console.error('[API] Failed to delete task template:', error);
    throw error;
  }
};

/**
 * Get task schedules for a facility
 * @param {string} facilityId - Facility UUID
 * @returns {Promise<Array>} List of task schedules
 */
export const getTaskSchedules = async (facilityId) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/tasks/schedules/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch task schedules:', error);
    throw error;
  }
};

/**
 * Create a new task schedule
 * @param {string} facilityId - Facility UUID
 * @param {Object} scheduleData - Schedule data
 * @returns {Promise<Object>} Created schedule
 */
export const createTaskSchedule = async (facilityId, scheduleData) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/tasks/schedules/`, scheduleData);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to create task schedule:', error);
    throw error;
  }
};

/**
 * Update a task schedule
 * @param {string} facilityId - Facility UUID
 * @param {string} scheduleId - Schedule UUID
 * @param {Object} scheduleData - Schedule data
 * @returns {Promise<Object>} Updated schedule
 */
export const updateTaskSchedule = async (facilityId, scheduleId, scheduleData) => {
  try {
    const response = await apiClient.patch(`facility/facilities/${facilityId}/tasks/schedules/${scheduleId}/`, scheduleData);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to update task schedule:', error);
    throw error;
  }
};

/**
 * Delete a task schedule
 * @param {string} facilityId - Facility UUID
 * @param {string} scheduleId - Schedule UUID
 * @returns {Promise<void>}
 */
export const deleteTaskSchedule = async (facilityId, scheduleId) => {
  try {
    await apiClient.delete(`facility/facilities/${facilityId}/tasks/schedules/${scheduleId}/`);
  } catch (error) {
    console.error('[API] Failed to delete task schedule:', error);
    throw error;
  }
};

/**
 * Get task instances for a facility with optional filters
 * @param {string} facilityId - Facility UUID
 * @param {Object} filters - Filter params (status, from_date, to_date, assigned_to, category)
 * @returns {Promise<Array>} List of task instances
 */
export const getTaskInstances = async (facilityId, filters = {}, options = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    const url = `facility/facilities/${facilityId}/tasks/instances/${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get(url);
    // #124: with no date bound the server returns 500 tasks, latest due
    // date first (future-due included), and SAYS so in two headers (the body stays a bare array, so
    // every existing caller is unchanged). A caller that wants to tell
    // the operator the list was cut short passes `onMeta`.
    if (typeof options.onMeta === 'function') {
      const headers = response.headers || {};
      const capped = String(headers['x-ff-result-capped'] || '').toLowerCase() === 'true';
      const total = Number.parseInt(headers['x-ff-result-total'], 10);
      options.onMeta({
        capped,
        total: Number.isFinite(total) ? total : null,
        shown: Array.isArray(response.data) ? response.data.length : null,
      });
    }
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch task instances:', error);
    throw error;
  }
};

/**
 * Get ONE task instance (the row the lists serve, `made_flavour_choice`
 * included). The flavour dialog refetches its card with it after the
 * complete door refused a flavour the card no longer offers.
 * @param {string} facilityId - Facility UUID
 * @param {string} instanceId - Instance UUID
 * @returns {Promise<Object>} The task row
 */
export const getTaskInstance = async (facilityId, instanceId) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/tasks/instances/${instanceId}/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch task instance:', error);
    throw error;
  }
};

/**
 * Get tasks due today
 * @param {string} facilityId - Facility UUID
 * @returns {Promise<Array>} List of tasks due today
 */
export const getTasksToday = async (facilityId) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/tasks/instances/today/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch today tasks:', error);
    throw error;
  }
};

/**
 * Today's tasks GROUPED BY PLACE, in walking order (I-03 R6 · S3-06.3).
 *
 * The same rows `getTasksToday` returns, from the same endpoint, in the
 * shape FroggerAI's `whatsDue` answers in — room, then rack, then the
 * tanks on it — so the page's groups and the FroggerAI-led round can
 * never put the round in two different orders.
 *
 * `includeOverdue` adds the overdue window, because the page's default
 * list is overdue + today's active rows and an overdue task on rack 1
 * gets done when the keeper is at rack 1.
 *
 * `assignedTo: 'me'` asks for the round FroggerAI's `whatsDue` answers
 * for a Staff caller — their own tasks plus the unclaimed ones. The
 * DEFAULT is unchanged and facility-wide; the page sends it only for
 * Staff, so the header and the dock cannot count different sets and
 * both call them "overdue" (see `tasks_places.PAGE_BUCKETS`).
 *
 * @param {string} facilityId
 * @param {{includeOverdue?: boolean, assignedTo?: 'me'}} options
 * @returns {Promise<{when: string, count: number, group_count: number,
 *                    scope: 'facility'|'mine_or_unclaimed',
 *                    counts: {pending: number, in_progress: number,
 *                             overdue: number, open: number},
 *                    groups: Array<{place: object, count: number, tasks: Array}>}>}
 */
export const getTasksTodayByPlace = async (facilityId, options = {}) => {
  const params = { group_by: 'place' };
  if (options.includeOverdue) params.include_overdue = 1;
  if (options.assignedTo) params.assigned_to = options.assignedTo;
  try {
    const response = await apiClient.get(
      `facility/facilities/${facilityId}/tasks/instances/today/`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch today tasks by place:', error);
    throw error;
  }
};

/**
 * Get tasks assigned to current user
 * @param {string} facilityId - Facility UUID
 * @returns {Promise<Array>} List of user's pending/in-progress tasks
 */
export const getMyTasks = async (facilityId) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/tasks/instances/my_tasks/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch my tasks:', error);
    throw error;
  }
};

/**
 * Get overdue tasks
 * @param {string} facilityId - Facility UUID
 * @returns {Promise<Array>} List of overdue tasks
 */
// ⚠️ THE OVERDUE LIST IS PART OF THE ROUND, so it takes the round's
// SCOPE (re-sweep #6, MAJOR 4). The grouped call asks for `assigned_to:
// 'me'` on a Staff round and this one asked for the whole facility, so
// the Overdue counter (server-side, scoped) and the Overdue CARDS
// (unscoped) were two different sets — the "the page says five, the
// voice says four" finding one level down, inside one page. `assignedTo`
// is opt-in, so every existing caller keeps the answer it had.
export const getOverdueTasks = async (facilityId, options = {}) => {
  const params = {};
  if (options.assignedTo) params.assigned_to = options.assignedTo;
  try {
    const response = await apiClient.get(
      `facility/facilities/${facilityId}/tasks/instances/overdue/`,
      Object.keys(params).length ? { params } : undefined
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch overdue tasks:', error);
    throw error;
  }
};

/**
 * Get facility members for task assignment dropdown
 * @param {string} facilityId - Facility UUID
 * @returns {Promise<Array>} List of facility members with id, display_name, name
 *   (the same label as display_name — never an email), role. NO email key
 *   (Cowork run 40, the 2026-09-11 rule).
 */
export const getFacilityMembers = async (facilityId) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/tasks/instances/members/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch facility members:', error);
    throw error;
  }
};

/**
 * Create a new task instance (manual)
 * @param {string} facilityId - Facility UUID
 * @param {Object} instanceData - Instance data
 * @returns {Promise<Object>} Created instance
 */
export const createTaskInstance = async (facilityId, instanceData) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/tasks/instances/`, instanceData);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to create task instance:', error);
    throw error;
  }
};

/**
 * Create a one-off feeding task (manager direct creation)
 */
export const createFeedingOneOff = async (facilityId, data) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/tasks/feeding-oneoff/`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to create feeding one-off:', error);
    throw error;
  }
};

/**
 * Request a one-off feeding task (employee request, pending approval)
 */
export const requestFeedingOneOff = async (facilityId, data) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/tasks/feeding-request/`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to request feeding one-off:', error);
    throw error;
  }
};

/**
 * Start a task (mark as in_progress)
 * @param {string} facilityId - Facility UUID
 * @param {string} instanceId - Instance UUID
 * @returns {Promise<Object>} Updated task
 */
export const startTask = async (facilityId, instanceId) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/tasks/instances/${instanceId}/start/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to start task:', error);
    throw error;
  }
};

/**
 * Unstart a task (move from in_progress back to pending)
 * Staff can unstart tasks assigned to them.
 * Manager/Owner can unstart any task.
 * @param {string} facilityId - Facility UUID
 * @param {string} instanceId - Instance UUID
 * @returns {Promise<Object>} Updated task
 */
export const unstartTask = async (facilityId, instanceId) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/tasks/instances/${instanceId}/unstart/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to unstart task:', error);
    throw error;
  }
};

/**
 * Complete a task
 * @param {string} facilityId - Facility UUID
 * @param {string} instanceId - Instance UUID
 * @param {Object} data - Completion data (duration_minutes, notes, checklist_state)
 * @returns {Promise<Object>} Updated task
 */
export const completeTask = async (facilityId, instanceId, data = {}) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/tasks/instances/${instanceId}/complete/`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to complete task:', error);
    throw error;
  }
};

/**
 * Complete a batch feeding task with per-target results.
 * @param {string} facilityId - Facility UUID
 * @param {string} instanceId - Task instance UUID
 * @param {Object} data - Batch completion data
 * @param {number} [data.duration_minutes] - Time spent
 * @param {string} [data.notes] - Global notes
 * @param {Array} data.per_target - Per-target completion results
 * @returns {Promise<Object>} { task, fed_count, skipped_count, feeding_events_created, status }
 */
export const batchCompleteTask = async (facilityId, instanceId, data) => {
  try {
    const response = await apiClient.post(
      `facility/facilities/${facilityId}/tasks/instances/${instanceId}/batch-complete/`,
      data
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to batch complete task:', error);
    throw error;
  }
};

/**
 * Defer a task to a future date
 * @param {string} facilityId - Facility UUID
 * @param {string} instanceId - Instance UUID
 * @param {Object} data - Defer data (to_date, reason)
 * @returns {Promise<Object>} Updated task
 */
export const deferTask = async (facilityId, instanceId, data) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/tasks/instances/${instanceId}/defer/`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to defer task:', error);
    throw error;
  }
};

/**
 * Skip a task
 * @param {string} facilityId - Facility UUID
 * @param {string} instanceId - Instance UUID
 * @param {Object} data - Skip data (reason)
 * @returns {Promise<Object>} Updated task
 */
export const skipTask = async (facilityId, instanceId, data = {}) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/tasks/instances/${instanceId}/skip/`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to skip task:', error);
    throw error;
  }
};

/**
 * Undo a task deferral (punch #40) — returns a deferred task to pending
 * on its original schedule. Backend viewset action: undo-defer.
 * @param {string} facilityId - Facility UUID
 * @param {string} instanceId - Instance UUID
 * @returns {Promise<Object>} Updated task
 */
export const undoDeferTask = async (facilityId, instanceId) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/tasks/instances/${instanceId}/undo-defer/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to undo defer:', error);
    throw error;
  }
};

/**
 * Bulk update tasks (complete, complete_as_scheduled, skip)
 * @param {string} facilityId - Facility UUID
 * @param {Object} data - { action: 'complete'|'complete_as_scheduled'|'skip', task_ids: [...], notes: '' }
 * @returns {Promise<Object>} { succeeded, failed, skipped_already_done, total_requested }
 */
export const bulkUpdateTasks = async (facilityId, data) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/tasks/instances/bulk-update/`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to bulk update tasks:', error);
    throw error;
  }
};

/**
 * Q-overdue-triage: bulk "Slide to today" / "Skip" over OVERDUE cards
 * (Owner/Manager). Backend viewset action: overdue-triage.
 *
 * Exactly ONE selection goes on the wire: `task_ids` (the cards) OR
 * `overdue_before` (YYYY-MM-DD — every overdue card dated before it, read
 * by the server). `preview: true` only when previewing (reads and writes
 * nothing; the confirm names the server's `count`).
 * @param {string} facilityId - Facility UUID
 * @param {Object} opts - { action: 'slide_to_today'|'skip', taskIds?, overdueBefore?, preview? }
 * @returns {Promise<Object>} preview: { count, task_ids, not_changed };
 *   apply: { requested, written, task_ids, not_changed, receipt }
 */
export const triageOverdueTasks = async (facilityId, {
  action, taskIds, overdueBefore, preview = false,
}) => {
  const body = { action };
  if (taskIds) body.task_ids = taskIds;
  else body.overdue_before = overdueBefore;
  if (preview) body.preview = true;
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/tasks/instances/overdue-triage/`, body);
  return response.data;
};

/**
 * Request to defer a task (staff only - requires manager approval)
 * @param {string} facilityId - Facility UUID
 * @param {string} instanceId - Instance UUID
 * @param {Object} data - Defer request data (to_date, reason)
 * @returns {Promise<Object>} Created defer request
 */
export const requestDeferTask = async (facilityId, instanceId, data) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/tasks/instances/${instanceId}/request_defer/`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to request defer:', error);
    throw error;
  }
};

/**
 * Reopen a completed or skipped task
 * Manager+ only. Moves task back to pending status.
 * @param {string} facilityId - Facility UUID
 * @param {string} instanceId - Instance UUID
 * @returns {Promise<Object>} Updated task
 */
export const reopenTask = async (facilityId, instanceId) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/tasks/instances/${instanceId}/reopen/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to reopen task:', error);
    throw error;
  }
};

/**
 * Assign a task to a user
 * @param {string} facilityId - Facility UUID
 * @param {string} instanceId - Instance UUID
 * @param {string} userId - User UUID to assign to
 * @returns {Promise<Object>} Updated task
 */
export const assignTask = async (facilityId, instanceId, userId) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/tasks/instances/${instanceId}/assign/`, { user_id: userId });
    return response.data;
  } catch (error) {
    console.error('[API] Failed to assign task:', error);
    throw error;
  }
};

/**
 * Claim a task (assign to current user)
 * Any facility staff can claim unassigned tasks.
 * @param {string} facilityId - Facility UUID
 * @param {string} instanceId - Task instance UUID
 * @returns {Promise<Object>} Updated task
 */
export const claimTask = async (facilityId, instanceId) => {
  try {
    // The backend assign endpoint with 'me' value assigns to current user
    const response = await apiClient.post(`facility/facilities/${facilityId}/tasks/instances/${instanceId}/claim/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to claim task:', error);
    throw error;
  }
};

/**
 * Generate tasks for today (or specified date)
 * Manager+ only. Idempotent - won't create duplicates.
 * @param {string} facilityId - Facility UUID
 * @param {string} [date] - Optional date in YYYY-MM-DD format (defaults to today)
 * @returns {Promise<Object>} {created: N, skipped: N, errors: N, date: "..."}
 */
export const generateTasks = async (facilityId, date = null) => {
  try {
    const payload = date ? { date } : {};
    const response = await apiClient.post(`facility/facilities/${facilityId}/tasks/instances/generate/`, payload);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to generate tasks:', error);
    throw error;
  }
};

// ============================================================================
// Task Requests (Employee one-time task requests)
// ============================================================================

/**
 * Create a task request (any facility member)
 * @param {string} facilityId - Facility UUID
 * @param {Object} data - { template_id, requested_date, reason }
 * @returns {Promise<Object>} Created task request
 */
export const createTaskRequest = async (facilityId, data) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/tasks/requests/`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to create task request:', error);
    throw error;
  }
};

/**
 * Get task requests for a facility
 * Members see own; Manager+ sees all (optionally filtered by status)
 * @param {string} facilityId - Facility UUID
 * @param {string} [statusFilter] - Optional status filter (pending, approved, denied)
 * @returns {Promise<Object>} {count: N, results: [...]}
 */
export const getTaskRequests = async (facilityId, statusFilter = null) => {
  try {
    const params = statusFilter ? `?status=${statusFilter}` : '';
    const response = await apiClient.get(`facility/facilities/${facilityId}/tasks/requests/${params}`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch task requests:', error);
    throw error;
  }
};

/**
 * Review (approve/deny) a task request (Manager+ only)
 * @param {string} facilityId - Facility UUID
 * @param {string} requestId - TaskRequest UUID
 * @param {Object} data - { action: 'approve'|'deny', notes?, override_date?, assigned_to? }
 * @returns {Promise<Object>} Updated task request
 */
export const reviewTaskRequest = async (facilityId, requestId, data) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/tasks/requests/${requestId}/review/`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to review task request:', error);
    throw error;
  }
};

// ============================================================================
// Defer Request Management (Manager-only)
// ============================================================================

/**
 * Get all pending defer requests for a facility
 * Manager+ only.
 * @param {string} facilityId - Facility UUID
 * @returns {Promise<Object>} {count: N, results: [...]}
 */
export const getDeferRequests = async (facilityId) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/tasks/instances/defer_requests/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to get defer requests:', error);
    throw error;
  }
};

/**
 * Approve or deny a defer request
 * Manager+ only.
 * @param {string} facilityId - Facility UUID
 * @param {string} taskId - Task instance UUID (for URL routing)
 * @param {string} requestId - Defer request UUID
 * @param {string} action - 'approve' or 'deny'
 * @param {string} [notes] - Optional notes for denial
 * @returns {Promise<Object>} Updated defer request
 */
export const reviewDeferRequest = async (facilityId, taskId, requestId, action, notes = '') => {
  try {
    const response = await apiClient.post(
      `facility/facilities/${facilityId}/tasks/instances/${taskId}/defer-requests/${requestId}/review/`,
      { action, notes }
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to review defer request:', error);
    throw error;
  }
};

/**
 * Retry AI evaluation for a defer request
 * Manager+ only. Clears any previous error and queues a new AI evaluation.
 * @param {string} facilityId - Facility UUID
 * @param {string} taskId - Task instance UUID
 * @param {string} requestId - Defer request UUID
 * @returns {Promise<Object>} Updated defer request
 */
export const retryDeferAI = async (facilityId, taskId, requestId) => {
  try {
    const response = await apiClient.post(
      `facility/facilities/${facilityId}/tasks/instances/${taskId}/defer-requests/${requestId}/retry-ai/`
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to retry AI evaluation:', error);
    throw error;
  }
};

// ============================================================================
// Facility Task Settings
// ============================================================================

/**
 * Get facility task notification settings
 * Manager+ only.
 * @param {string} facilityId - Facility UUID
 * @returns {Promise<Object>} Facility task settings
 */
export const getFacilityTaskSettings = async (facilityId) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/task-settings/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to get facility task settings:', error);
    throw error;
  }
};

/**
 * Update facility task notification settings
 * Manager+ only.
 * @param {string} facilityId - Facility UUID
 * @param {Object} data - Settings to update (defer_request_notify_email, defer_request_notify_name)
 * @returns {Promise<Object>} Updated settings
 */
export const updateFacilityTaskSettings = async (facilityId, data) => {
  try {
    const response = await apiClient.patch(`facility/facilities/${facilityId}/task-settings/`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to update facility task settings:', error);
    throw error;
  }
};

// ============================================================================
// Facility AI Settings (Defer Evaluation)
// ============================================================================

/**
 * Get facility AI settings for defer request evaluation
 * Manager+ only.
 * @param {string} facilityId - Facility UUID
 * @returns {Promise<Object>} AI settings { defer_ai_level, defer_ai_level_display, defer_ai_enabled_at, defer_ai_enabled_by_name }
 */
export const getDeferAISettings = async (facilityId) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/tasks/instances/ai-settings/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to get defer AI settings:', error);
    throw error;
  }
};

/**
 * Update facility AI settings for defer request evaluation
 * Manager+ only.
 * @param {string} facilityId - Facility UUID
 * @param {number|null} deferAILevel - 0=Manual, 1=AI Assist, 2=AI Auto-Approve (optional)
 * @param {Object} [extra] - Additional settings to patch (e.g. defer_ai_confidence_threshold)
 * @returns {Promise<Object>} Updated AI settings
 */
export const updateDeferAISettings = async (facilityId, deferAILevel, extra = {}) => {
  try {
    const payload = { ...extra };
    if (deferAILevel !== null && deferAILevel !== undefined) {
      payload.defer_ai_level = deferAILevel;
    }
    const response = await apiClient.patch(
      `facility/facilities/${facilityId}/tasks/instances/ai-settings/`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to update defer AI settings:', error);
    throw error;
  }
};

/**
 * Set time-boxed auto-approve for a number of hours
 * Manager+ only. When active, AI auto-approve works regardless of defer_ai_level.
 * @param {string} facilityId - Facility UUID
 * @param {number} hours - Hours to enable (0 to disable, 1, 4, 24, etc.)
 * @returns {Promise<Object>} Updated AI settings with defer_ai_auto_until and is_auto_approve_active
 */
export const setAutoApproveTimer = async (facilityId, hours) => {
  try {
    const response = await apiClient.patch(
      `facility/facilities/${facilityId}/tasks/instances/ai-settings/`,
      { defer_ai_auto_until_hours: hours }
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to set auto-approve timer:', error);
    throw error;
  }
};

/**
 * Update defer AI guidelines for this facility
 * Manager+ only. Guidelines are appended to the default AI evaluation prompt.
 * @param {string} facilityId - Facility UUID
 * @param {string} guidelines - Custom guidelines text (max 2000 chars)
 * @returns {Promise<Object>} { ok: true, defer_ai_guidelines: "..." }
 */
export const updateDeferAIGuidelines = async (facilityId, guidelines) => {
  try {
    const response = await apiClient.patch(
      `facility/facilities/${facilityId}/tasks/instances/ai-guidelines/`,
      { defer_ai_guidelines: guidelines }
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to update defer AI guidelines:', error);
    throw error;
  }
};

// ============================================================================
// AI Task Suggestions (Phase C1b)
// ============================================================================

/**
 * Generate AI task suggestions for a facility
 * Manager+ only. Calls LLM to analyze facility context and suggest tasks.
 * @param {string} facilityId - Facility UUID
 * @param {Object} options - Optional parameters
 * @param {string} [options.focus] - Category focus: 'all', 'feeding', 'cleaning', 'health', 'breeding', 'maintenance'
 * @param {string} [options.user_prompt] - Optional specific request
 * @param {boolean} [options.include_species_context] - Include species care info (default true)
 * @returns {Promise<Object>} { ok: true, suggestions: [...], context_summary: {...} }
 */
export const suggestTasks = async (facilityId, options = {}) => {
  try {
    const response = await apiClient.post(
      `facility/facilities/${facilityId}/tasks/instances/suggest-plan/`,
      options
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to generate task suggestions:', error);
    throw error;
  }
};

/**
 * Get all task plan suggestions for a facility
 * Manager+ only.
 * @param {string} facilityId - Facility UUID
 * @param {string} [status] - Optional status filter: 'pending', 'accepted', 'rejected', 'expired'
 * @returns {Promise<Object>} { count: N, results: [...] }
 */
export const getTaskSuggestions = async (facilityId, status = null) => {
  try {
    const params = status ? { status } : {};
    const response = await apiClient.get(
      `facility/facilities/${facilityId}/tasks/instances/plan-suggestions/`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to get task suggestions:', error);
    throw error;
  }
};

/**
 * Accept a task plan suggestion (creates TaskTemplate + optional Schedule)
 * Manager+ only.
 * @param {string} facilityId - Facility UUID
 * @param {string} suggestionId - Suggestion UUID
 * @param {Object} [modifications] - Optional overrides { name, priority, cadence, etc. }
 * @returns {Promise<Object>} { ok: true, suggestion: {...}, template: {...} }
 */
export const acceptTaskSuggestion = async (facilityId, suggestionId, modifications = {}) => {
  try {
    const response = await apiClient.post(
      `facility/facilities/${facilityId}/tasks/instances/plan-suggestions/${suggestionId}/accept/`,
      modifications
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to accept task suggestion:', error);
    throw error;
  }
};

/**
 * Reject a task plan suggestion
 * Manager+ only.
 * @param {string} facilityId - Facility UUID
 * @param {string} suggestionId - Suggestion UUID
 * @param {string} [reason] - Optional rejection reason
 * @returns {Promise<Object>} { ok: true, suggestion: {...} }
 */
export const rejectTaskSuggestion = async (facilityId, suggestionId, reason = '', notes = '') => {
  try {
    const response = await apiClient.post(
      `facility/facilities/${facilityId}/tasks/instances/plan-suggestions/${suggestionId}/reject/`,
      { reason, notes }
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to reject task suggestion:', error);
    throw error;
  }
};

// =============================================================================
// AI OPS INSIGHTS (Phase C6)
// =============================================================================

/**
 * Get AI ops insights (operational suggestions) for a facility.
 * These are nightly-generated suggestions about task patterns:
 * - ops_defer_pattern: Task frequently deferred
 * - ops_skip_pattern: Task frequently skipped
 * - ops_workload_imbalance: One user doing most work
 * Manager+ only.
 *
 * @param {string} facilityId - Facility UUID
 * @param {string} [status='proposed'] - Filter by status
 * @returns {Promise<Object>} { insights: [...], count: N }
 */
export const getOpsInsights = async (facilityId, status = 'proposed') => {
  try {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    const response = await apiClient.get(
      `facility/facilities/${facilityId}/ai/ops-insights/?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch ops insights:', error);
    throw error;
  }
};

/**
 * Dismiss an AI suggestion (ops insight or other).
 * Manager+ only.
 * @param {string} facilityId - Facility UUID
 * @param {string} suggestionId - Suggestion UUID
 * @param {string} [reason] - Structured reason enum (e.g. 'not_relevant')
 * @param {string} [notes] - Optional free-text dismissal notes
 * @returns {Promise<Object>} Updated suggestion
 */
export const dismissAISuggestion = async (facilityId, suggestionId, reason = '', notes = '') => {
  try {
    const response = await apiClient.post(
      `facility/facilities/${facilityId}/ai/suggestions/${suggestionId}/review/`,
      { action: 'dismiss', reason, notes }
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to dismiss suggestion:', error);
    throw error;
  }
};

/**
 * Approve an AI suggestion.
 * Manager+ only.
 * @param {string} facilityId - Facility UUID
 * @param {string} suggestionId - Suggestion UUID
 * @param {string} [reason] - Structured reason enum (e.g. 'looks_correct')
 * @param {string} [notes] - Optional free-text approval notes
 * @returns {Promise<Object>} Updated suggestion
 */
export const approveAISuggestion = async (facilityId, suggestionId, reason = '', notes = '') => {
  try {
    const response = await apiClient.post(
      `facility/facilities/${facilityId}/ai/suggestions/${suggestionId}/review/`,
      { action: 'approve', reason, notes }
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to approve suggestion:', error);
    throw error;
  }
};

/**
 * Apply an approved AI suggestion (e.g., profile change).
 * Manager+ only.
 * @param {string} facilityId - Facility UUID
 * @param {string} suggestionId - Suggestion UUID
 * @returns {Promise<Object>} Applied suggestion result
 */
export const applyAISuggestion = async (facilityId, suggestionId) => {
  try {
    const response = await apiClient.post(
      `facility/facilities/${facilityId}/ai/suggestions/${suggestionId}/apply/`
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to apply suggestion:', error);
    throw error;
  }
};

/**
 * Generate ops insights NOW (manual trigger).
 * Same analysis as nightly job but returns results immediately.
 * Manager+ only.
 * @param {string} facilityId - Facility UUID
 * @returns {Promise<Object>} { ok, insights_created, insights, count }
 */
export const generateOpsInsightsNow = async (facilityId) => {
  try {
    const response = await apiClient.post(
      `facility/facilities/${facilityId}/ai/ops-insights/`
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to generate ops insights:', error);
    throw error;
  }
};

/**
 * Run Plan Health Check - drift analysis for feeding profiles.
 * Manager+ only.
 * @param {string} facilityId - Facility UUID
 * @param {Object} options - { persist: false, lookback_days: 90, use_llm: false }
 * @returns {Promise<Object>} Analysis results (does NOT persist by default)
 */
export const runPlanHealthCheck = async (facilityId, options = {}) => {
  try {
    const response = await apiClient.post(
      `facility/facilities/${facilityId}/ai/feeding/plan-health-check/`,
      {
        // IMPORTANT: Health Check should NOT persist by default
        // Use generateProfileSuggestions() to persist suggestions
        persist: options.persist ?? false,
        lookback_days: options.lookback_days ?? 90,
        use_llm: options.use_llm ?? false,
        ...options,
      }
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to run plan health check:', error);
    throw error;
  }
};

/**
 * Generate Profile Suggestions - feeding profile change proposals.
 * Manager+ only.
 * @param {string} facilityId - Facility UUID
 * @param {Object} options - { lookback_days: 90, use_llm: false }
 * @returns {Promise<Object>} Generated suggestions
 */
export const generateProfileSuggestions = async (facilityId, options = {}) => {
  try {
    const response = await apiClient.post(
      `facility/facilities/${facilityId}/ai/feeding/profile-suggestions/`,
      {
        lookback_days: options.lookback_days ?? 90,
        use_llm: options.use_llm ?? false,
        ...options,
      }
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to generate profile suggestions:', error);
    throw error;
  }
};

/**
 * Get all AI suggestions for a facility (all types).
 * Manager+ only.
 * @param {string} facilityId - Facility UUID
 * @param {Object} options - { type, status, limit }
 * @returns {Promise<Object>} { suggestions: [...] }
 */
export const getAISuggestions = async (facilityId, options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.type) params.append('type', options.type);
    if (options.status) params.append('status', options.status);
    if (options.limit) params.append('limit', options.limit);
    const response = await apiClient.get(
      `facility/facilities/${facilityId}/ai/suggestions/?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch AI suggestions:', error);
    throw error;
  }
};

// =============================================================================
// AI SCHEDULE PLANNER (Phase C3)
// =============================================================================

/**
 * Generate an AI schedule plan for a facility.
 * This plans actual work for day/week/month (different from Task Planner which suggests templates).
 * Manager+ only.
 *
 * ASYNC-FIRST ARCHITECTURE (avoids Cloudflare 520 timeouts):
 * - For horizon='day': Tries sync first (15s fast-path). If LLM completes quickly, returns immediately.
 *   If timeout, falls back to async job (returns { async: true, job_id }).
 * - For horizon='week'|'month': Always returns { async: true, job_id }.
 *
 * Frontend should ALWAYS be prepared to handle async response from any horizon.
 * Use pollSchedulePlanJob() to wait for completion when async=true.
 *
 * @param {string} facilityId - Facility UUID
 * @param {Object} options - { horizon: 'day'|'week'|'month', date_anchor?: 'YYYY-MM-DD' }
 * @returns {Promise<Object>} { ok: true, plan: {...} } or { ok: true, async: true, job_id }
 */
export const generateSchedulePlan = async (facilityId, options = {}) => {
  try {
    // Server is pure-async as of 2026-06-30 — POST queues a Celery
    // task and returns 202 + job_id in <100ms regardless of LLM
    // latency. Default axios timeout is fine. Client then polls via
    // pollSchedulePlanJob(). The earlier 25s per-call override (and
    // the server-side 15s fast-path it was working around) were
    // removed together; see facility/views_tasks.py:schedule_plan
    // for the architecture note.
    const response = await apiClient.post(
      `facility/facilities/${facilityId}/tasks/schedule_plan/`,
      options
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to generate schedule plan:', error);
    throw error;
  }
};

/**
 * Get status of an async schedule plan job.
 * @param {string} facilityId - Facility UUID
 * @param {string} jobId - Job UUID
 * @returns {Promise<Object>} { job_id, status, progress, result?, error? }
 */
export const getSchedulePlanJobStatus = async (facilityId, jobId) => {
  try {
    const response = await apiClient.get(
      `facility/facilities/${facilityId}/tasks/schedule_plan_status/${jobId}/`
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to get schedule plan job status:', error);
    throw error;
  }
};

/**
 * Poll for async schedule plan job completion.
 * Calls onProgress callback with status updates.
 * Returns the final result when job completes.
 *
 * @param {string} facilityId - Facility UUID
 * @param {string} jobId - Job UUID
 * @param {Object} options - { intervalMs?: 2000, maxAttempts?: 150, onProgress?: (status) => void }
 * @returns {Promise<Object>} The result payload when job succeeds
 * @throws {Error} If job fails or times out
 */
export const pollSchedulePlanJob = async (facilityId, jobId, options = {}) => {
  const {
    intervalMs = 2000,
    maxAttempts = 150, // 5 minutes at 2s interval
    onProgress = () => {},
  } = options;

  let attempts = 0;

  while (attempts < maxAttempts) {
    attempts++;
    const status = await getSchedulePlanJobStatus(facilityId, jobId);
    onProgress(status);

    if (status.status === 'succeeded') {
      return status.result;
    }

    if (status.status === 'failed') {
      throw new Error(status.error || 'Schedule plan generation failed');
    }

    // Wait before next poll
    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }

  throw new Error('Schedule plan generation timed out');
};

/**
 * Apply an AI schedule plan (assign/create tasks).
 * Manager+ only.
 * @param {string} facilityId - Facility UUID
 * @param {string} planId - Schedule plan UUID
 * @param {Object} options - { items_to_apply?: string[], apply_mode?: 'assign'|'create_and_assign' }
 * @returns {Promise<Object>} { ok: true, applied_count, created_count, assigned_count, errors: [] }
 */
export const applySchedulePlan = async (facilityId, planId, options = {}) => {
  try {
    const response = await apiClient.post(
      `facility/facilities/${facilityId}/tasks/schedule_plan/${planId}/apply/`,
      options
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to apply schedule plan:', error);
    throw error;
  }
};

/**
 * Submit feedback for a schedule plan (accept/reject items).
 * Creates FroggerAIMemory entries for learning.
 * Manager+ only.
 * @param {string} facilityId - Facility UUID
 * @param {string} planId - Schedule plan UUID
 * @param {Object} feedback - { item_feedbacks: [{item_id, action:'accept'|'reject', reason?}], plan_feedback?: {...} }
 * @returns {Promise<Object>} { ok: true, recorded_count }
 */
export const submitSchedulePlanFeedback = async (facilityId, planId, feedback = {}) => {
  try {
    const response = await apiClient.post(
      `facility/facilities/${facilityId}/tasks/schedule_plan/${planId}/feedback/`,
      feedback
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to submit schedule plan feedback:', error);
    throw error;
  }
};

/**
 * Get schedule plan history for a facility.
 * Manager+ only.
 * @param {string} facilityId - Facility UUID
 * @param {Object} options - { horizon?: 'day'|'week'|'month', limit?: number }
 * @returns {Promise<Array>} Array of past plans
 */
export const getSchedulePlanHistory = async (facilityId, options = {}) => {
  try {
    const response = await apiClient.get(
      `facility/facilities/${facilityId}/tasks/schedule_plan/history/`,
      { params: options }
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to get schedule plan history:', error);
    throw error;
  }
};

/**
 * Get a specific schedule plan by ID.
 * Used for reloading a saved plan when modal reopens.
 * @param {string} facilityId - Facility UUID
 * @param {string} planId - Plan UUID
 * @returns {Promise<Object>} Plan with items
 */
export const getSchedulePlan = async (facilityId, planId) => {
  try {
    const response = await apiClient.get(
      `facility/facilities/${facilityId}/tasks/schedule_plan/${planId}/`
    );
    return response.data;
  } catch (error) {
    console.error('[API] Failed to get schedule plan:', error);
    throw error;
  }
};

// =============================================================================
// FEEDING PROFILES (Phase C4)
// =============================================================================

/**
 * Get feeding profiles for a facility
 * @param {string} facilityId - Facility UUID
 * @returns {Promise<Array>} List of feeding profiles
 */
export const getFeedingProfiles = async (facilityId) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/feeding-profiles/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to get feeding profiles:', error);
    throw error;
  }
};

/**
 * Create a feeding profile
 * @param {string} facilityId - Facility UUID
 * @param {Object} data - Profile data { species, life_stage, feedings_per_week_target, ... }
 * @returns {Promise<Object>} Created profile
 */
export const createFeedingProfile = async (facilityId, data) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/feeding-profiles/`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to create feeding profile:', error);
    throw error;
  }
};

/**
 * Update a feeding profile
 * @param {string} facilityId - Facility UUID
 * @param {string} profileId - Profile UUID
 * @param {Object} data - Updated profile data
 * @returns {Promise<Object>} Updated profile
 */
export const updateFeedingProfile = async (facilityId, profileId, data) => {
  try {
    const response = await apiClient.patch(`facility/facilities/${facilityId}/feeding-profiles/${profileId}/`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to update feeding profile:', error);
    throw error;
  }
};

/**
 * Delete a feeding profile
 * @param {string} facilityId - Facility UUID
 * @param {string} profileId - Profile UUID
 * @returns {Promise<void>}
 */
export const deleteFeedingProfile = async (facilityId, profileId) => {
  try {
    await apiClient.delete(`facility/facilities/${facilityId}/feeding-profiles/${profileId}/`);
  } catch (error) {
    console.error('[API] Failed to delete feeding profile:', error);
    throw error;
  }
};

// =============================================================================
// FOODS WE KEEP (I-03 S3-07.1) — the Feeding page's Foods tab
// =============================================================================

/**
 * The foods this facility keeps: the catalogue grouped by category, each
 * food's Kept flag, its kept variants (EMPTY = every variant) and the stock
 * of each kept variant (`high | in_stock | low | out`). `derived: true`
 * means the facility has saved nothing yet and the answer is "the foods its
 * feeding profiles name". Any facility role.
 * @param {string} facilityId - Facility UUID
 * @returns {Promise<{derived, categories, counts}>}
 */
export const getFoods = async (facilityId) => {
  const response = await apiClient.get(`facility/facilities/${facilityId}/foods/`);
  return response.data;
};

/** The keys one Foods PATCH may carry, besides `feeder_type`. */
export const FOOD_PATCH_KEYS = ['is_kept', 'kept_variants', 'variant', 'stock_status', 'note'];

/**
 * Change ONE food (Owner/Manager). `changes` is any of `is_kept` (bool),
 * `kept_variants` (string[]; `[]` = every variant), and — together with
 * `variant` (the size / flavour key, `""` for a food with no variants) —
 * `stock_status` and/or `note`. Only those keys reach the wire; an absent
 * key is not sent (the server reads a missing key as "not changing it").
 * The FIRST write for a facility saves its whole derived list first.
 * @returns {Promise<Object>} the food as the server now has it
 */
export const patchFood = async (facilityId, feederType, changes = {}) => {
  const body = { feeder_type: feederType };
  FOOD_PATCH_KEYS.forEach((key) => {
    if (changes[key] !== undefined) body[key] = changes[key];
  });
  const response = await apiClient.patch(`facility/facilities/${facilityId}/foods/`, body);
  return response.data;
};

// =============================================================================
// MADE FOODS (I-03 S3-07.2) — the Foods tab's "Made here" section
// =============================================================================

/**
 * The foods this facility MAKES (a gecko diet mix, fruit fly cultures):
 * each recipe with its rotation, the next suggested flavour, its OPEN batch
 * (`open_batch`, `expected_empty`, `days_left` against the facility's own
 * `today`), its latest NON-voided batches (`recent_batches`) with how many
 * voided ones were left out (`recent_voided_not_shown`) and its open "Mix
 * the next batch" task (`open_make_task`). Any
 * facility role; read-only here — a batch is recorded by completing the
 * make task, never from this list.
 * @param {string} facilityId - Facility UUID
 * @returns {Promise<Array>} the made foods (active first)
 */
export const getMadeFoods = async (facilityId) => {
  const response = await apiClient.get(`facility/facilities/${facilityId}/made-foods/`);
  const data = response.data || {};
  return Array.isArray(data.results) ? data.results : [];
};

/**
 * The keys a recipe POST / PATCH may carry — the server's own
 * `views_foods.MADE_FOOD_FIELDS` (the recipe editor, Fable design
 * 2026-10-20). Every value is a KEY (a variant key, a media recipe's id),
 * never a label.
 */
export const MADE_FOOD_KEYS = [
  'name', 'kind', 'feeds_as', 'base_ingredient', 'rotation',
  'rotation_amount', 'rotation_unit', 'other_ingredients', 'yield_value',
  'yield_unit', 'shelf_life_days', 'lead_time_min_days',
  'lead_time_max_days', 'make_quantity', 'cadence_days', 'notes',
  'is_active', 'consumes', 'draw_per_unit', 'size_yields', 'default_size',
  'retire_after_days',
  // 2b R3 (the leftovers batch, item 5): a culture's seed day.
  'seed_offset_days',
];

/** Only MADE_FOOD_KEYS reach the wire, and an `undefined` is not sent (a
 * `null` is: it clears `consumes`, `retire_after_days`, a shelf life). */
const madeFoodBody = (fields = {}) => {
  const body = {};
  MADE_FOOD_KEYS.forEach((key) => {
    if (fields[key] !== undefined) body[key] = fields[key];
  });
  return body;
};

/**
 * Add a recipe (Owner/Manager). `fields` must name `kind`, `name` and
 * `feeds_as`; the server refuses an unknown key (`unknown_field`) and says
 * what is wrong with a value in a sentence, with `fields: {name: sentence}`
 * when it names one.
 * @returns {Promise<Object>} the recipe as the Foods tab lists it
 */
export const createMadeFood = async (facilityId, fields = {}) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/made-foods/`, madeFoodBody(fields));
  return response.data;
};

/**
 * Change a recipe (Owner/Manager) — the body carries ONLY the keys the
 * user changed (the server reads a missing key as "not changing it").
 * `{is_active: false}` retires it (the answer names any open card a keeper
 * still holds: `open_cards_kept`); `{is_active: true}` restores it.
 * @returns {Promise<Object>} the recipe as the Foods tab lists it
 */
export const patchMadeFood = async (facilityId, madeFoodId, changes = {}) => {
  const response = await apiClient.patch(
    `facility/facilities/${facilityId}/made-foods/${madeFoodId}/`,
    madeFoodBody(changes));
  return response.data;
};

/**
 * THE FINISH DOOR (any facility role): a culture batch retired, a mix
 * batch that ran out — `finished` true sends `{finished: true}` and the
 * SERVER stamps its own facility day (never a day this page held since it
 * loaded — a tab left open overnight would record yesterday); false sends
 * `{finished: false}` and clears it (the row's Undo). Nothing else on a
 * batch is written here.
 * @returns {Promise<{batch: Object, made_food: Object}>}
 */
export const finishMadeBatch = async (facilityId, madeFoodId, batchId, finished) => {
  const response = await apiClient.patch(
    `facility/facilities/${facilityId}/made-foods/${madeFoodId}/batches/${batchId}/`,
    { finished: finished === true });
  return response.data;
};

/**
 * The keys "Record a past batch" may send — the server's own
 * `made_foods.PAST_BATCH_FIELDS` (adoption batch A1).
 */
export const PAST_BATCH_KEYS = ['made_on', 'quantity', 'variant', 'size', 'left', 'note'];

/**
 * RECORD A PAST BATCH (Owner/Manager; adoption batch A1): a batch made on
 * an earlier day (or today), through the SAME server writer a make card's
 * completion uses — a culture draws from the media mix, a mix's flavour
 * steps the rotation as a completion on that day would. `fields`:
 * `made_on` (an ISO day), and per kind `quantity` (a culture), `variant`
 * (a mix's flavour key) or `size` + `left` (a media mix: the cultures'
 * worth left in the tub RIGHT NOW — FABLE_QUEUE R-PAST-MEDIA; 0 is a
 * value and is sent), plus an optional `note`.
 * Only PAST_BATCH_KEYS reach the wire; an `undefined`, `null` or blank one
 * is not sent. A refusal is a sentence (`fields: {made_on: …}` when it
 * names a field); nothing is written then. The answer's `undo_token`
 * takes the batch back (`undoPastBatch`).
 * @returns {Promise<{batch: Object, message: string, undo_token: ?string, made_food: Object}>}
 */
export const recordPastBatch = async (facilityId, madeFoodId, fields = {}) => {
  const body = {};
  PAST_BATCH_KEYS.forEach((key) => {
    const v = fields[key];
    if (v !== undefined && v !== null && v !== '') body[key] = v;
  });
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/made-foods/${madeFoodId}/batches/`, body);
  return response.data;
};

/**
 * "VOID BATCH…", READ FIRST (Owner/Manager; FABLE_QUEUE R-VOID): what
 * voiding the batch would do, read off the rows by the server — writes
 * nothing. The confirm shows `sentence` (or, when the void may not go
 * ahead, `refusal`) and the real call echoes `key`.
 * @returns {Promise<{sentence: string, key: string, refusal: ?string, already_voided: boolean}>}
 */
export const previewVoidMadeBatch = async (facilityId, madeFoodId, batchId) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/made-foods/${madeFoodId}/batches/${batchId}/void/?dry_run=1`,
    {});
  return response.data;
};

/**
 * "VOID BATCH…" (Owner/Manager): the soft void, sending exactly `{key}`
 * (and `reason` when one is given) — the key the dry run answered. A 409
 * `void_stale` carries the fresh `sentence` and `key`; a 409
 * `void_refused` says what is in the way. Nothing is ever deleted.
 * @returns {Promise<{message: string, voided: boolean, already_voided: boolean, made_food: Object}>}
 */
export const voidMadeBatch = async (facilityId, madeFoodId, batchId, key, reason) => {
  const body = { key };
  if (typeof reason === 'string' && reason.trim()) body.reason = reason.trim();
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/made-foods/${madeFoodId}/batches/${batchId}/void/`,
    body);
  return response.data;
};

/**
 * THE PAST-BATCH RECEIPT'S UNDO (the keeper who recorded it; R-VOID door
 * 1): sends exactly `{token}`. The server refuses, naming what changed,
 * when the batch or anything that drew from it changed since (409
 * `changed_since`).
 * @returns {Promise<{message: string, made_food: Object}>}
 */
export const undoPastBatch = async (facilityId, madeFoodId, batchId, token) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/made-foods/${madeFoodId}/batches/${batchId}/undo/`,
    { token });
  return response.data;
};

/**
 * Get canonical feeder catalog from backend
 * @param {string} facilityId - Facility UUID
 * @returns {Promise<Array>} List of feeder types with size buckets and categories
 */
export const getFeedersCatalog = async (facilityId) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/feeders-catalog/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to get feeders catalog:', error);
    throw error;
  }
};

/**
 * Get life stage definitions with age ranges
 * @param {string} facilityId - Facility UUID
 * @returns {Promise<Array>} List of life stage definitions
 */
export const getLifeStages = async (facilityId) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/life-stages/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to get life stages:', error);
    throw error;
  }
};

/**
 * Get canonical supplement catalog from backend
 * @param {string} facilityId - Facility UUID
 * @returns {Promise<Array>} List of supplement types with categories and cadence defaults
 */
export const getSupplementsCatalog = async (facilityId) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/supplements-catalog/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to get supplements catalog:', error);
    throw error;
  }
};

// =============================================================================
// Feeding Profile Templates
// =============================================================================

/**
 * Get all feeding profile templates for a facility
 * @param {string} facilityId - Facility UUID
 * @returns {Promise<Array>} List of templates
 */
export const getFeedingProfileTemplates = async (facilityId) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/feeding-profile-templates/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to get feeding profile templates:', error);
    throw error;
  }
};

/**
 * Create a new feeding profile template
 * @param {string} facilityId - Facility UUID
 * @param {Object} data - Template data
 * @returns {Promise<Object>} Created template
 */
export const createFeedingProfileTemplate = async (facilityId, data) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/feeding-profile-templates/`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to create feeding profile template:', error);
    throw error;
  }
};

/**
 * Update a feeding profile template
 * @param {string} facilityId - Facility UUID
 * @param {string} templateId - Template UUID
 * @param {Object} data - Updated template data
 * @returns {Promise<Object>} Updated template
 */
export const updateFeedingProfileTemplate = async (facilityId, templateId, data) => {
  try {
    const response = await apiClient.patch(`facility/facilities/${facilityId}/feeding-profile-templates/${templateId}/`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to update feeding profile template:', error);
    throw error;
  }
};

/**
 * Delete (archive) a feeding profile template
 * @param {string} facilityId - Facility UUID
 * @param {string} templateId - Template UUID
 * @returns {Promise<void>}
 */
export const deleteFeedingProfileTemplate = async (facilityId, templateId) => {
  try {
    await apiClient.delete(`facility/facilities/${facilityId}/feeding-profile-templates/${templateId}/`);
  } catch (error) {
    console.error('[API] Failed to delete feeding profile template:', error);
    throw error;
  }
};

/**
 * Apply a feeding profile template to multiple species + life stages
 * @param {string} facilityId - Facility UUID
 * @param {string} templateId - Template UUID
 * @param {Object} data - { species_ids: [], life_stages: [], mode: 'create_only' | 'upsert' }
 * @returns {Promise<Object>} Result summary
 */
export const applyFeedingProfileTemplate = async (facilityId, templateId, data) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/feeding-profile-templates/${templateId}/apply/`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to apply feeding profile template:', error);
    throw error;
  }
};

// =============================================================================
// Feeding Plan (Phase 2 + 2.1+)
// =============================================================================

/**
 * Get computed feeding plan for a date range
 * @param {string} facilityId - Facility UUID
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {number} days - Number of days (1-30, default 7)
 * @param {string} groupBy - Grouping mode: "enclosure" (default), "clutch", or "aggregate"
 * @returns {Promise<Object>} Plan with entries, summary
 */
export const getFeedingPlan = async (facilityId, startDate, days = 7, groupBy = 'enclosure', config = {}) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/feeding-plan/`, {
      params: { start_date: startDate, days, group_by: groupBy },
      ...config,
    });
    return response.data;
  } catch (error) {
    console.error('[API] Failed to get feeding plan:', error);
    throw error;
  }
};

/**
 * Apply feeding plan by creating TaskTemplates and TaskInstances
 * @param {string} facilityId - Facility UUID
 * @param {Object} data - { start_date, days, group_by, options: { create_templates, assign_to } }
 * @returns {Promise<Object>} Result with created_tasks, created_templates, skipped_existing, task_ids
 */
export const applyFeedingPlan = async (facilityId, data) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/feeding-plan/apply/`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to apply feeding plan:', error);
    throw error;
  }
};

/**
 * Get list of past feeding plan runs
 * @param {string} facilityId - Facility UUID
 * @param {Object} [params] - Optional filters: start_date, end_date, days, group_by, limit
 * @returns {Promise<Object>} { runs: [...], count: number }
 */
export const getFeedingPlanRuns = async (facilityId, params = {}) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/feeding-plan/runs/`, { params });
    return response.data;
  } catch (error) {
    console.error('[API] Failed to get feeding plan runs:', error);
    throw error;
  }
};

/**
 * Get a specific feeding plan run by ID
 * @param {string} facilityId - Facility UUID
 * @param {string} runId - Plan run UUID
 * @returns {Promise<Object>} Full plan run with snapshot_json
 */
export const getFeedingPlanRun = async (facilityId, runId) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/feeding-plan/runs/${runId}/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to get feeding plan run:', error);
    throw error;
  }
};

/**
 * Get the latest feeding plan run matching parameters
 * @param {string} facilityId - Facility UUID
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {number} days - Number of days
 * @param {string} [groupBy] - Grouping mode (default: 'batch')
 * @returns {Promise<Object>} Full plan run with snapshot_json, or null if none exists
 */
export const getLatestFeedingPlanRun = async (facilityId, startDate, days, groupBy = 'batch', config = {}) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/feeding-plan/runs/latest/`, {
      params: { start_date: startDate, days, group_by: groupBy },
      ...config,
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null; // No matching run found
    }
    console.error('[API] Failed to get latest feeding plan run:', error);
    throw error;
  }
};

/**
 * Get feeding event history for a facility
 * @param {string} facilityId - Facility UUID
 * @param {Object} [params] - Query params (species_id, life_stage, start_date,
 *   end_date, limit, offset, include_voided)
 *
 * `include_voided: 1` is the ONE reader that may see a soft-deleted row
 * (Cowork run 9, C2): the page owns the Void control, the confirm dialog
 * promises the row "stays in the database", and without it that promise
 * was unverifiable and the reason unreadable. Default off.
 * @returns {Promise<Array>} List of feeding events
 */
export const getFeedingHistory = async (facilityId, params = {}) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/feeding-events/`, { params });
    return response.data;
  } catch (error) {
    console.error('[API] Failed to fetch feeding history:', error);
    throw error;
  }
};

/**
 * Void ONE feeding row — the Feeding History page's own Undo.
 *
 * Cowork run 7, B2: the spoken Undo is conversation-scoped and the row
 * that turns out to be wrong an hour later had no control anywhere.
 * Owner/Manager only (the server enforces it); nothing is hard-deleted.
 *
 * @param {string} facilityId - Facility UUID
 * @param {string} eventId - FeedingEvent UUID
 * @param {string} [reason] - Why it was voided
 * @returns {Promise<Object>} The voided row
 */
export const voidFeedingEvent = async (facilityId, eventId, reason = '') => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/feeding-events/${eventId}/void/`,
    { reason },
  );
  return response.data;
};

/**
 * Get supplement status for facility
 * @param {string} facilityId - Facility UUID
 * @param {string} [speciesId] - Optional species filter
 * @param {string} [lifeStage] - Optional life stage filter
 * @returns {Promise<Object>} { statuses: [...] }
 */
export const getSupplementStatus = async (facilityId, speciesId = null, lifeStage = null) => {
  try {
    const params = {};
    if (speciesId) params.species_id = speciesId;
    if (lifeStage) params.life_stage = lifeStage;
    const response = await apiClient.get(`facility/facilities/${facilityId}/supplement-status/`, { params });
    return response.data;
  } catch (error) {
    console.error('[API] Failed to get supplement status:', error);
    throw error;
  }
};

// =============================================================================
// Employee Shifts (MVP Scheduling)
// =============================================================================

/**
 * Get employee shifts for a facility
 * @param {string} facilityId - Facility UUID
 * @param {Object} params - Optional filters: { start_date, end_date, employee_id, shift_type }
 * @returns {Promise<Array>} List of employee shift records
 */
export const getEmployeeShifts = async (facilityId, params = {}) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/employee-shifts/`, { params });
    return response.data;
  } catch (error) {
    console.error('[API] Failed to get employee shifts:', error);
    throw error;
  }
};

/**
 * Create a new employee shift
 * @param {string} facilityId - Facility UUID
 * @param {Object} data - { employee, start_dt, end_dt, shift_type, notes }
 * @returns {Promise<Object>} Created shift
 */
export const createEmployeeShift = async (facilityId, data) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/employee-shifts/`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to create employee shift:', error);
    throw error;
  }
};

/**
 * Update an employee shift
 * @param {string} facilityId - Facility UUID
 * @param {string} shiftId - Shift UUID
 * @param {Object} data - Updated shift data
 * @returns {Promise<Object>} Updated shift
 */
export const updateEmployeeShift = async (facilityId, shiftId, data) => {
  try {
    const response = await apiClient.patch(`facility/facilities/${facilityId}/employee-shifts/${shiftId}/`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to update employee shift:', error);
    throw error;
  }
};

/**
 * Delete (soft-delete) an employee shift
 * @param {string} facilityId - Facility UUID
 * @param {string} shiftId - Shift UUID
 * @returns {Promise<void>}
 */
export const deleteEmployeeShift = async (facilityId, shiftId) => {
  try {
    await apiClient.delete(`facility/facilities/${facilityId}/employee-shifts/${shiftId}/`);
  } catch (error) {
    console.error('[API] Failed to delete employee shift:', error);
    throw error;
  }
};

/**
 * Get today's shifts for a facility
 * @param {string} facilityId - Facility UUID
 * @returns {Promise<Array>} List of shifts for today
 */
export const getEmployeeShiftsToday = async (facilityId) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/employee-shifts/today/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to get today\'s shifts:', error);
    throw error;
  }
};

/**
 * Get shifts for the current week
 * @param {string} facilityId - Facility UUID
 * @returns {Promise<Array>} List of shifts for this week
 */
export const getEmployeeShiftsWeek = async (facilityId) => {
  try {
    const response = await apiClient.get(`facility/facilities/${facilityId}/employee-shifts/week/`);
    return response.data;
  } catch (error) {
    console.error('[API] Failed to get week\'s shifts:', error);
    throw error;
  }
};

/**
 * Copy shifts from one week to another
 * @param {string} facilityId - Facility UUID
 * @param {string} sourceWeekStart - Source week Monday (YYYY-MM-DD)
 * @param {string} targetWeekStart - Target week Monday (YYYY-MM-DD)
 * @param {boolean} overwrite - If true, delete existing shifts in target week
 * @returns {Promise<Object>} { created, skipped, deleted, message }
 */
export const copyEmployeeShiftsWeek = async (facilityId, sourceWeekStart, targetWeekStart, overwrite = false) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/employee-shifts/copy-week/`, {
      source_week_start: sourceWeekStart,
      target_week_start: targetWeekStart,
      overwrite,
    });
    return response.data;
  } catch (error) {
    console.error('[API] Failed to copy shifts:', error);
    throw error;
  }
};

/**
 * Bulk create multiple shifts at once
 * @param {string} facilityId - Facility UUID
 * @param {Array} shifts - Array of shift objects
 * @returns {Promise<Object>} { created, errors }
 */
export const bulkCreateEmployeeShifts = async (facilityId, shifts) => {
  try {
    const response = await apiClient.post(`facility/facilities/${facilityId}/employee-shifts/bulk/`, {
      shifts,
    });
    return response.data;
  } catch (error) {
    console.error('[API] Failed to bulk create shifts:', error);
    throw error;
  }
};


// ============================================================================
// Marketing Dashboard (P-12)
// ============================================================================
// Three facility-scoped GETs that power /my-store/<facilityId>/marketing.
// Backend lives in store/views_marketing_api.py and is gated by
// IsOwnerOrManagerOrAdminOfFacility — Staff users will get 403.

export const getMarketingTopProducts = async (facilityId, range = '7d') => {
  const response = await apiClient.get(
    `store/facilities/${facilityId}/marketing/top-products/`,
    { params: { range } }
  );
  return response.data;
};

export const getMarketingAbandonedCarts = async (facilityId, range = '7d') => {
  const response = await apiClient.get(
    `store/facilities/${facilityId}/marketing/abandoned-carts/`,
    { params: { range } }
  );
  return response.data;
};

export const getMarketingFunnel = async (facilityId, range = '7d') => {
  const response = await apiClient.get(
    `store/facilities/${facilityId}/marketing/funnel/`,
    { params: { range } }
  );
  return response.data;
};

// ============================================================================
// P-11 MarketingGraph Suite (S-17)
// ============================================================================

export const getMarketingContext = async (facilityId) => {
  const response = await apiClient.get(
    `store/facilities/${facilityId}/marketing/context/`
  );
  return response.data;
};

export const saveMarketingContext = async (facilityId, fields) => {
  // `fields` is any subset of: brand_voice_md, do_dont_md, audience_md,
  // margin_targets_md, seasonality_md. Empty strings clear a section.
  const response = await apiClient.patch(
    `store/facilities/${facilityId}/marketing/context/`,
    fields
  );
  return response.data;
};

export const runMarketingGraph = async (facilityId, {
  intent, userMessage, conversationId, parameters,
  // S-23.8 — dispatch async by default for non-chat intents so the
  // request thread doesn't block on a 4-6 minute web-research-enabled
  // generation. Caller can opt out by passing `async: false`. Chat
  // intent is always sync because the inline reply is the whole UX.
  async: useAsync,
  allowWebResearch = false,
} = {}) => {
  const resolvedIntent = intent || 'chat';
  const asyncFlag = (typeof useAsync === 'boolean')
    ? useAsync
    : (resolvedIntent !== 'chat');
  const response = await apiClient.post(
    `store/facilities/${facilityId}/marketing/run/`,
    {
      intent: resolvedIntent,
      user_message: userMessage || '',
      conversation_id: conversationId || undefined,
      parameters: parameters || {},
      async: asyncFlag,
      allow_web_research: !!allowWebResearch,
    }
  );
  return response.data;
};

// S-23.8 — poll the async draft-generation task. Returns the same
// payload shape the backend returns: `{state, draft_id, error,
// error_node, duration_ms}`. State values: PENDING / STARTED / SUCCESS
// / FAILURE / REVOKED.
export const getMarketingTaskStatus = async (facilityId, taskId) => {
  const response = await apiClient.get(
    `store/facilities/${facilityId}/marketing/task-status/${taskId}/`,
  );
  return response.data;
};

// S-23.8 — high-level helper: polls every `intervalMs` (default 3s)
// until the task reaches a terminal state, then resolves with the
// final payload. Caller can pass `signal` (AbortController.signal) to
// stop polling early (e.g. component unmount). Default timeout is
// 10 minutes — more than enough for a web-research generation but
// guards against a stuck task polling forever.
export const pollMarketingTaskUntilDone = async (
  facilityId,
  taskId,
  {
    intervalMs = 3000,
    timeoutMs = 600000,
    signal,
    onProgress,
  } = {},
) => {
  const startedAt = Date.now();
  const TERMINAL = new Set(['SUCCESS', 'FAILURE', 'REVOKED']);
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (signal && signal.aborted) {
      throw new DOMException('Polling aborted', 'AbortError');
    }
    if (Date.now() - startedAt > timeoutMs) {
      throw new Error('Marketing task polling timed out');
    }
    const status = await getMarketingTaskStatus(facilityId, taskId);
    if (typeof onProgress === 'function') {
      try { onProgress(status); } catch (_e) { /* swallow */ }
    }
    if (TERMINAL.has(status.state)) {
      return status;
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
};

// ============================================================================
// P-11 S-17 milestone 3 — Marketing chat streaming (SSE)
// ============================================================================
// Async generator that POSTs the chat message and yields parsed JSON events
// from the SSE stream. Reuses the shared `readSSE` parser from
// services/streamingAPI.js. The fetch carries the same Authorization header
// apiClient uses so the backend's IsOwnerOrManagerOrAdminOfFacility gate
// receives the right credentials.

export async function* streamMarketingChat(facilityId, {
  userMessage, conversationId, allowWebResearch = false, signal,
} = {}) {
  const { readSSE } = await import('./streamingAPI');
  // apiClient.defaults.baseURL ends without a trailing slash (e.g.
  // "https://api-beta.feelingfroggy.llc/api"), so we must add one
  // before joining. Run 55 caught a `/apistore/` 404 from naive concat.
  const base = (apiClient.defaults.baseURL || '').replace(/\/$/, '');
  const url = `${base}/store/facilities/${facilityId}/marketing/chat/stream/`;
  const authHeader = apiClient.defaults.headers.common['Authorization'];

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      ...(authHeader ? { Authorization: authHeader } : {}),
    },
    body: JSON.stringify({
      user_message: userMessage || '',
      conversation_id: conversationId || undefined,
      // S-20 m3 — operator opt-in to Anthropic web_search / web_fetch
      // for this turn. Backend reads strict `is True` so JSON boolean
      // is required (not string).
      allow_web_research: allowWebResearch === true,
    }),
    signal,
  });

  if (!response.ok) {
    let body = '';
    try { body = await response.text(); } catch (_) {}
    const err = new Error(`HTTP ${response.status}: ${response.statusText}`);
    err.status = response.status;
    err.body = body;
    throw err;
  }

  for await (const dataStr of readSSE(response)) {
    try {
      yield JSON.parse(dataStr);
    } catch (e) {
      console.warn('[marketing-stream] failed to parse SSE event:', dataStr);
    }
  }
}

export const getMarketingChatHistory = async (facilityId, conversationId) => {
  const response = await apiClient.get(
    `store/facilities/${facilityId}/marketing/chat/history/`,
    { params: { conversation_id: conversationId } }
  );
  return response.data;
};

export const clearMarketingChat = async (facilityId, conversationId) => {
  const response = await apiClient.post(
    `store/facilities/${facilityId}/marketing/chat/clear/`,
    { conversation_id: conversationId }
  );
  return response.data;
};

// ============================================================================
// Concierge Phase 2 — write-proposal confirmation handshake.
// The chat AI can STAGE promotion changes; these endpoints are the human
// half: inspect the pending proposal, confirm it (executes), or cancel.
// ============================================================================

export const getConciergePending = async (facilityId, conversationId) => {
  const response = await apiClient.get(
    `store/facilities/${facilityId}/marketing/concierge/pending/`,
    { params: { conversation_id: conversationId } }
  );
  return response.data;
};

export const confirmConciergeMutation = async (facilityId, conversationId, proposalId) => {
  const response = await apiClient.post(
    `store/facilities/${facilityId}/marketing/concierge/confirm/`,
    { conversation_id: conversationId, proposal_id: proposalId }
  );
  return response.data;
};

export const cancelConciergeMutation = async (facilityId, conversationId) => {
  const response = await apiClient.post(
    `store/facilities/${facilityId}/marketing/concierge/cancel/`,
    { conversation_id: conversationId }
  );
  return response.data;
};

// ============================================================================
// P-11 S-18 m4 — Marketing Drafts
// ============================================================================

export const listMarketingDrafts = async (facilityId, { status, contentType, page = 1, pageSize = 25 } = {}) => {
  const params = { page, page_size: pageSize };
  if (status) params.status = status;
  if (contentType) params.content_type = contentType;
  const response = await apiClient.get(
    `store/facilities/${facilityId}/marketing/drafts/`,
    { params }
  );
  return response.data;
};

export const getMarketingDraft = async (facilityId, draftId) => {
  const response = await apiClient.get(
    `store/facilities/${facilityId}/marketing/drafts/${draftId}/`
  );
  return response.data;
};

// S-25.6e — optimistic locking. When `ifUnmodifiedSince` is supplied
// (an ISO-8601 timestamp the caller read off the draft when it loaded),
// the backend returns 409 if `updated_at` has bumped in the interim
// (concurrent tab, admin edit, background task). Callers must catch
// 409 and prompt the operator to reload.
export const approveMarketingDraft = async (facilityId, draftId, edits = null, ifUnmodifiedSince = null) => {
  const body = edits ? { edits } : {};
  if (ifUnmodifiedSince) body.if_unmodified_since = ifUnmodifiedSince;
  const response = await apiClient.post(
    `store/facilities/${facilityId}/marketing/drafts/${draftId}/approve/`,
    body
  );
  return response.data;
};

// S-31.3 — eager-save for the section-aware newsletter editor.
// Posts a normalized sections array to the draft and returns the
// updated draft (with media_asset_details refreshed if section
// images were just attached/swapped).
export const updateDraftSections = async (facilityId, draftId, sections) => {
  const response = await apiClient.post(
    `store/facilities/${facilityId}/marketing/drafts/${draftId}/update-sections/`,
    { sections },
  );
  return response.data;
};


// S-30.1 — eager-save for hero + supplementary image attachments.
// Persists `media_asset_ids` without touching draft status. Lets the
// picker save selections immediately so they appear in Send Test
// without waiting for Approve. The list semantics match
// approveMarketingDraft exactly: index 0 = hero slot (null = "no
// override, use species cover"), index 1..N = supplementary images.
export const attachDraftMedia = async (facilityId, draftId, mediaAssetIds) => {
  const response = await apiClient.post(
    `store/facilities/${facilityId}/marketing/drafts/${draftId}/attach-media/`,
    { media_asset_ids: mediaAssetIds },
  );
  return response.data;
};

export const rejectMarketingDraft = async (facilityId, draftId, reason = '') => {
  const response = await apiClient.post(
    `store/facilities/${facilityId}/marketing/drafts/${draftId}/reject/`,
    { reason }
  );
  return response.data;
};

// S-22 m3 — Request Changes. Re-runs the marketing graph with the same
// intent as the original draft, plus the operator's feedback woven in.
// Returns { previous_draft, new_draft } — the original is NOT mutated;
// the operator can compare and pick.
//
// Timeout: 90s (overrides the apiClient default of 15s). The revision
// path runs a full Sonnet 4.6 generation pass — same cost profile as
// Generate Newsletter — which legitimately takes 30-60s. Per CLAUDE.md
// beta-timeout policy, AI-backed operations get a 90s floor. Run 69
// caught the 15s default firing before the backend finished, leaving
// the modal showing a red error even though the revision had persisted.
export const requestMarketingDraftChanges = async (
  facilityId,
  draftId,
  { feedback, allowWebResearch = false } = {}
) => {
  const response = await apiClient.post(
    `store/facilities/${facilityId}/marketing/drafts/${draftId}/request-changes/`,
    {
      feedback: String(feedback || ''),
      allow_web_research: allowWebResearch === true,
    },
    { timeout: 240000 }  // 4 min — Run 71 fix; matches the Generate Draft path.
  );
  return response.data;
};

// S-21 m3 + m4 — Publish an APPROVED draft. The endpoint dispatches by
// content_type: email-shaped (email/promo_blast/newsletter) goes to the
// SES task; social-shaped (social_post/story/reel) goes to the Meta
// Graph API task (DRY-RUN until META_INTEGRATION_ENABLED is set).
//
// Email options: `testRecipient` (single address preview, draft stays
//   approved).
// Social options: `platforms` (subset of ['instagram', 'facebook'],
//   defaults to both).
export const publishMarketingDraft = async (
  facilityId,
  draftId,
  { testRecipient = '', platforms = null } = {}
) => {
  const body = {};
  if (testRecipient) body.test_recipient = testRecipient;
  if (platforms) body.platforms = platforms;
  const response = await apiClient.post(
    `store/facilities/${facilityId}/marketing/drafts/${draftId}/publish/`,
    body,
  );
  return response.data;
};

// ─────────────────────────────────────────────────────────────────────────────
// P-11 S-21 m5 — AutomationProfile editor + AuditLog browser
// ─────────────────────────────────────────────────────────────────────────────

// GET the per-facility AutomationProfile. Backend auto-creates a
// MANUAL-defaults row on first read so the editor never sees a 404.
export const getMarketingAutomationProfile = async (facilityId) => {
  const response = await apiClient.get(
    `store/facilities/${facilityId}/marketing/automation/`,
  );
  return response.data;
};

// PATCH any subset of the AutomationProfile fields. Empty-object PATCH
// is a no-op on the backend (no `changed` fields → no save).
export const updateMarketingAutomationProfile = async (facilityId, partial) => {
  const response = await apiClient.patch(
    `store/facilities/${facilityId}/marketing/automation/`,
    partial,
  );
  return response.data;
};

// S-23.6 — fetch the MediaAssets linked to a given object (species,
// product, etc.). Used by the marketing photo picker to list the
// available hero photos for a featured species.
//
// S-24.11 — when `targetModel === 'species'` the response also includes
// a `candidates` array of auto-matched MediaAssetSpecies rows (iNat /
// Smithsonian photos linked by binomial/common name, not yet operator-
// confirmed). The picker renders these in the LinkedPhotosStrip with
// ✓ confirm / ✗ deny actions.
export const getObjectAssets = async (targetModel, targetId) => {
  const response = await apiClient.get('mediahub/objects/assets/', {
    params: { target_model: targetModel, target_id: targetId },
  });
  return response.data;
};

// S-24.16 — operator flags/un-flags an asset as unusable. Backend
// refuses (409 with `error_type='asset_has_links'` + a `linked_to`
// payload) if there are any MediaLinks referencing the asset; caller
// surfaces those links so the operator knows where to un-link first.
export const toggleAssetUnusable = async (assetId, isUnusable, facilityId) => {
  const response = await apiClient.post('mediahub/assets/toggle-unusable/', {
    asset_id: assetId,
    is_unusable: isUnusable,
    facility_id: facilityId,
  });
  return response.data;
};

// S-24.11 — operator denies an auto-matched MediaAssetSpecies link
// (e.g., a Bakhuis-looking iNat photo that got auto-linked to Azureus).
// Marks the row `operator_denied` so the reconcile signal won't re-add
// it on the next Species save, and the picker won't surface it as a
// candidate. Idempotent — re-denying a denied row is a no-op.
export const denyAssetSpeciesLink = async (assetId, speciesId) => {
  const response = await apiClient.post('mediahub/asset-species/deny/', {
    asset_id: assetId,
    species_id: speciesId,
  });
  return response.data;
};

// S-24.8 — update a MediaLink row's position field (for gallery reorder).
// The backend MediaLinkDetailView accepts PATCH with any subset of
// editable fields; we send only `position` to avoid surprise writes.
export const updateMediaLinkPosition = async (linkId, position) => {
  const response = await apiClient.patch(`mediahub/links/${linkId}/`, { position });
  return response.data;
};

// S-24.8 — set the cover photo for a Product. New endpoint added in
// S-24.8 because Product was the only level of the cascade
// (Species/Morph/Product/Variation) that didn't have a MediaAsset
// cover-set endpoint.
export const setProductCover = async (productId, assetId) => {
  const response = await apiClient.post(
    `store/products/${productId}/set-cover/`,
    { image_id: assetId },
  );
  return response.data;
};

// S-24.9 — unified cover-set helper. Dispatches to the right backend
// endpoint based on target type (each level has its own historical
// endpoint shape — some use `asset_id`, some use `image_id`, some
// PATCH facility-scoped, some POST store-scoped). Centralizing here
// so the ImagePickerModal can do "promote to cover" on any of the 5
// entity types without each caller having to know endpoint trivia.
//
// targetType: 'species' | 'morph' | 'animal' | 'productvariation' | 'product'
//             | 'sellablelot'
// ids: { facilityId, targetId }
// assetId: the MediaAsset UUID to promote to cover
export const setEntityCover = async (targetType, ids, assetId) => {
  const { facilityId, targetId } = ids;
  if (!facilityId || !targetId || !assetId) {
    throw new Error('setEntityCover: facilityId, targetId, and assetId are required');
  }
  // 45s, NO retry — see HEAVY_WRITE_TIMEOUT_MS. Cowork QA on beta saw
  // six client-side timeouts on the species branch in a single run.
  //
  // r-review MEDIUM-2 (2026-09-07): a retry here is NOT safe the way it
  // is on delete. A client-side timeout does not cancel the request the
  // server is still running, so the retry can overlap it — and two
  // overlapping cover sets both find no existing link and both insert,
  // violating MediaLink's unique_together. That surfaced as a 500 on a
  // cover that HAD been set. The server side is now tolerant of the
  // race (`sync_cover_medialink` uses get_or_create), but the retry
  // bought nothing here anyway: the reconcile that made this slow now
  // runs on a worker, so 45s is already enormous headroom.
  const cfg = { timeout: HEAVY_WRITE_TIMEOUT_MS };
  let response;
  switch (targetType) {
    case 'species':
      response = await apiClient.patch(
        `facility/facilities/${facilityId}/species/${targetId}/cover/`,
        { asset_id: assetId },
        cfg,
      );
      break;
    case 'morph':
      response = await apiClient.patch(
        `facility/facilities/${facilityId}/morphs/${targetId}/image/`,
        { asset_id: assetId },
        cfg,
      );
      break;
    case 'animal':
      response = await apiClient.patch(
        `facility/facilities/${facilityId}/animals/${targetId}/cover/`,
        { asset_id: assetId },
        cfg,
      );
      break;
    case 'productvariation':
      response = await apiClient.post(
        `store/variations/${targetId}/set-cover/`,
        { image_id: assetId },
        cfg,
      );
      break;
    case 'product':
      response = await apiClient.post(
        `store/products/${targetId}/set-cover/`,
        { image_id: assetId },
        cfg,
      );
      break;
    case 'sellablelot':
      // A pinned pair's / group's cover (review of the 2026-10-07 lot
      // gallery order): the store-side lot_cover door, which writes
      // through the same sync_cover_medialink every other entity uses.
      response = await apiClient.patch(
        `store/facilities/${facilityId}/lots/${targetId}/cover/`,
        { asset_id: assetId },
        cfg,
      );
      break;
    case 'pairing':
      // A breeding pairing's cover (Q-trios-and-pairing-photos,
      // 2026-10-14) — facility-side only, the same one cover writer.
      response = await apiClient.patch(
        `facility/facilities/${facilityId}/pairings/${targetId}/cover/`,
        { asset_id: assetId },
        cfg,
      );
      break;
    default:
      throw new Error(`setEntityCover: unknown target type "${targetType}"`);
  }
  return response.data;
};

// #144 (operator-approved 2026-08-24): clear a cover ENTIRELY — the
// missing exit when a cover was set by mistake (Run 16 obs 2: every UI
// control refused, correctly, and only a DB edit could undo it). The
// backend DELETE unsets cover_asset and removes the synced role='cover'
// MediaLink atomically. Animals and lots (a lot's cover is ONLY its
// role='cover' link, demoted back into its gallery on clear); the other
// entity types keep swap-only until asked.
const CLEAR_COVER_PATHS = {
  animal: (facilityId, targetId) =>
    `facility/facilities/${facilityId}/animals/${targetId}/cover/`,
  sellablelot: (facilityId, targetId) =>
    `store/facilities/${facilityId}/lots/${targetId}/cover/`,
  pairing: (facilityId, targetId) =>
    `facility/facilities/${facilityId}/pairings/${targetId}/cover/`,
};

export const clearEntityCover = async (targetType, ids) => {
  const { facilityId, targetId } = ids;
  if (!facilityId || !targetId) {
    throw new Error('clearEntityCover: facilityId and targetId are required');
  }
  const path = CLEAR_COVER_PATHS[targetType];
  if (!path) {
    throw new Error(`clearEntityCover: not supported for "${targetType}" yet`);
  }
  const response = await apiClient.delete(path(facilityId, targetId));
  return response.data;
};

// GET paginated MarketingAuditLog rows for a facility.
// Optional filters: action (one of the Action choices), draftId.
export const getMarketingAuditLog = async (
  facilityId,
  { action = '', draftId = '', page = 1, pageSize = 25 } = {}
) => {
  const params = { page, page_size: pageSize };
  if (action) params.action = action;
  if (draftId) params.draft_id = draftId;
  const response = await apiClient.get(
    `store/facilities/${facilityId}/marketing/audit-log/`,
    { params },
  );
  return response.data;
};

// ============================================================================
// P-12b S-32.4 — Operator-Admin Telemetry (facility-scoped, read-only)
// Backed by facility/views_operator_admin.py. All endpoints return:
//   { count, next, previous, limit, offset, results: [...] }
// Filters accept comma-separated strings for multi-select chips.
// ============================================================================

const _buildOpAdminParams = ({
  limit = 50,
  offset = 0,
  startDate = '',
  endDate = '',
  ...rest
} = {}) => {
  const params = { limit, offset };
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  Object.entries(rest).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '' && v !== false) {
      params[k] = v;
    }
  });
  return params;
};

// ─────────────────────────────────────────────────────────────
// Owner Telemetry Surface — Phase A (2026-07-18)
// Owner + Manager only, no Staff. Backend permission enforces.
// ─────────────────────────────────────────────────────────────

// ─── Homepage photo (operator ruling 2026-10-01) ─────────────────────
// StoreConfig.hero_image_url is the storefront homepage's hero banner.
// The client sends a MediaAsset id of THIS facility (or null to remove
// it) — never a URL; the server resolves the photo's display URL.
// GET any facility role; PATCH Owner/Manager.
export const getStoreConfig = async (facilityId) => {
  const response = await apiClient.get(
    `store/facilities/${facilityId}/store-config/`,
  );
  return response.data;
};

export const setStoreHeroAsset = async (facilityId, assetId) => {
  const response = await apiClient.patch(
    `store/facilities/${facilityId}/store-config/`,
    { hero_asset_id: assetId || null },
  );
  return response.data;
};

// ─── Plant Cuttings counter (I-03 S3-01.3) ───────────────────────────
export const getCuttings = async (facilityId) => {
  const response = await apiClient.get(`store/facilities/${facilityId}/cuttings/`);
  return response.data;
};

export const saveCuttings = async (facilityId, rows) => {
  const response = await apiClient.post(
    `store/facilities/${facilityId}/cuttings/`,
    { rows },
  );
  return response.data;
};

// I-03 S3-01.5 — plant packages (combo boxes). Availability is DERIVED
// server-side from the component counts; these calls never send it.
export const getPlantPackages = async (facilityId) => {
  const response = await apiClient.get(
    `store/facilities/${facilityId}/plant-packages/`,
  );
  return response.data;
};

export const createPlantPackage = async (facilityId, payload) => {
  const response = await apiClient.post(
    `store/facilities/${facilityId}/plant-packages/`,
    payload,
  );
  return response.data;
};

export const updatePlantPackage = async (facilityId, productId, payload) => {
  const response = await apiClient.patch(
    `store/facilities/${facilityId}/plant-packages/${productId}/`,
    payload,
  );
  return response.data;
};

export const archivePlantPackage = async (facilityId, productId) => {
  const response = await apiClient.delete(
    `store/facilities/${facilityId}/plant-packages/${productId}/`,
  );
  return response.data;
};

export const getBusinessHealthOverview = async (facilityId) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/telemetry/overview/`,
  );
  return response.data;
};

export const getRecentVisitors = async (facilityId, { hours = 24 } = {}) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/telemetry/visitors/`,
    { params: { hours } },
  );
  return response.data;
};

export const getClientJourney = async (facilityId, { clientUuid, days = 7 } = {}) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/telemetry/journey/`,
    { params: { client_uuid: clientUuid, days } },
  );
  return response.data;
};

export const getPipelineStatus = async (facilityId) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/telemetry/pipeline/`,
  );
  return response.data;
};

export const getOperatorAdminSummary = async (
  facilityId,
  { startDate = '', endDate = '' } = {}
) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/operator-admin/summary/`,
    { params },
  );
  return response.data;
};

export const getOperatorAdminAIRuns = async (
  facilityId,
  {
    subsystem = '',
    conversationId = '',
    errored = false,
    startDate = '',
    endDate = '',
    limit = 50,
    offset = 0,
  } = {}
) => {
  const params = _buildOpAdminParams({
    limit,
    offset,
    startDate,
    endDate,
    subsystem,
    conversation_id: conversationId,
    errored: errored ? 'true' : '',
  });
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/operator-admin/ai-runs/`,
    { params },
  );
  return response.data;
};

export const getOperatorAdminAIRunDetail = async (facilityId, runId) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/operator-admin/ai-runs/${runId}/`,
  );
  return response.data;
};

export const getOperatorAdminMarketingAudit = async (
  facilityId,
  {
    action = '',
    draftId = '',
    actorId = '',
    startDate = '',
    endDate = '',
    limit = 50,
    offset = 0,
  } = {}
) => {
  const params = _buildOpAdminParams({
    limit,
    offset,
    startDate,
    endDate,
    action,
    draft_id: draftId,
    actor_id: actorId,
  });
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/operator-admin/marketing-audit/`,
    { params },
  );
  return response.data;
};

export const getOperatorAdminMarketingResearch = async (
  facilityId,
  {
    intent = '',
    draftId = '',
    conversationId = '',
    startDate = '',
    endDate = '',
    limit = 50,
    offset = 0,
  } = {}
) => {
  const params = _buildOpAdminParams({
    limit,
    offset,
    startDate,
    endDate,
    intent,
    draft_id: draftId,
    conversation_id: conversationId,
  });
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/operator-admin/marketing-research/`,
    { params },
  );
  return response.data;
};

export const getOperatorAdminStuckOrders = async (facilityId) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/operator-admin/stuck-orders/`,
  );
  return response.data;
};

export const forceProcessStuckOrder = async (facilityId, orderId) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/operator-admin/stuck-orders/${orderId}/force-process/`,
  );
  return response.data;
};

export const getOperatorAdminJourney = async (
  facilityId,
  { q = '', days = 7 } = {}
) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/operator-admin/journey/`,
    { params: { q, days } },
  );
  return response.data;
};

export const getOperatorAdminRecentClients = async (
  facilityId,
  { days = 7, limit = 50 } = {}
) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/operator-admin/recent-clients/`,
    { params: { days, limit } },
  );
  return response.data;
};

export const getOperatorAdminTaskDecisions = async (
  facilityId,
  {
    action = '',
    actorType = '',
    taskRequestId = '',
    deferRequestId = '',
    startDate = '',
    endDate = '',
    limit = 50,
    offset = 0,
  } = {}
) => {
  const params = _buildOpAdminParams({
    limit,
    offset,
    startDate,
    endDate,
    action,
    actor_type: actorType,
    task_request_id: taskRequestId,
    defer_request_id: deferRequestId,
  });
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/operator-admin/task-decisions/`,
    { params },
  );
  return response.data;
};

// =============================================================================
// SUPPLIES (R7-INVENTORY slice 1, spec §7) — Facility → Supplies
// =============================================================================
//
// Every route is under `facility/facilities/<fid>/`; every refusal is
// `{code, error, detail}` (the page shows `detail` on the row). The role
// matrix is the SERVER's: a Staff GET carries no price keys at all, and the
// purchase routes answer 403 for Staff. Each wrapper below puts EXACTLY the
// documented body on the wire — `stockApiWire.test.js` is the contract.

const stockBase = (facilityId) => `facility/facilities/${facilityId}/stock/`;

/** Every key a supply POST may carry (the server's `ITEM_FIELDS`). */
export const STOCK_CREATE_KEYS = [
  'thing_id', 'name', 'room', 'rack', 'enclosure', 'spot',
  'category', 'unit', 'package_word', 'package_size',
  'reorder_point', 'lead_days', 'food_feeder_type',
  'food_variant', 'draw_made_food_id', 'draw_amount',
  'draw_variant', 'rotation_note',
];

/** Every key a supply PATCH may carry (`PATCH_FIELDS`: never the Thing,
 * never `on_hand` — a number is written only by a count). */
export const STOCK_PATCH_KEYS = [
  'category', 'unit', 'package_word', 'package_size',
  'reorder_point', 'lead_days', 'food_feeder_type',
  'food_variant', 'draw_made_food_id', 'draw_amount',
  'draw_variant', 'rotation_note',
];

const pickKeys = (source, keys) => {
  const body = {};
  keys.forEach((key) => {
    if (source && source[key] !== undefined) body[key] = source[key];
  });
  return body;
};

/** The supplies (`{count, results}`); `includeRetired` adds the retired. */
export const getStock = async (facilityId, { includeRetired = false } = {}) => {
  const response = await apiClient.get(
    `${stockBase(facilityId)}${includeRetired ? '?include_retired=1' : ''}`);
  return response.data;
};

export const getStockItem = async (facilityId, itemId) => {
  const response = await apiClient.get(`${stockBase(facilityId)}${itemId}/`);
  return response.data;
};

/** Owner/Manager. `fields`: a `thing_id`, or a `name` (+ place) for a new
 * Thing, and the stock fields. Only `STOCK_CREATE_KEYS` reach the wire. */
export const createStockItem = async (facilityId, fields = {}) => {
  const response = await apiClient.post(
    stockBase(facilityId), pickKeys(fields, STOCK_CREATE_KEYS));
  return response.data;
};

/** Owner/Manager. Only `STOCK_PATCH_KEYS` reach the wire — `on_hand` is
 * dropped here (the server would answer 400 `use_count`). */
export const updateStockItem = async (facilityId, itemId, changes = {}) => {
  const response = await apiClient.patch(
    `${stockBase(facilityId)}${itemId}/`, pickKeys(changes, STOCK_PATCH_KEYS));
  return response.data;
};

/**
 * Any role. A count in the supply's unit (`{onHand}`) OR in packages
 * (`{packages}`) — exactly ONE key on the wire, `{on_hand}` or
 * `{packages}`. Answers `{item, adjustment, message, undo_token}`.
 */
export const countStock = async (facilityId, itemId, { onHand, packages } = {}) => {
  const body = (packages !== undefined && packages !== null && packages !== '')
    ? { packages } : { on_hand: onHand };
  const response = await apiClient.post(`${stockBase(facilityId)}${itemId}/count/`, body);
  return response.data;
};

/** Any role. `flag`: `low | out | clear` — a STATUS, never a number. */
export const flagStock = async (facilityId, itemId, flag) => {
  const response = await apiClient.post(`${stockBase(facilityId)}${itemId}/flag/`, { flag });
  return response.data;
};

/** Owner/Manager. Soft: `is_active` only, never a delete. */
export const retireStockItem = async (facilityId, itemId) => {
  const response = await apiClient.post(`${stockBase(facilityId)}${itemId}/retire/`, {});
  return response.data;
};

export const restoreStockItem = async (facilityId, itemId) => {
  const response = await apiClient.post(`${stockBase(facilityId)}${itemId}/restore/`, {});
  return response.data;
};

/** The page's Undo of one count / flag: `{token}` → `{item, message}`. */
export const reverseStockAdjustment = async (facilityId, adjustmentId, token) => {
  const response = await apiClient.post(
    `${stockBase(facilityId)}adjustments/${adjustmentId}/reverse/`, { token });
  return response.data;
};

/** The out / low list: `{results, count, unknown_count}`. Any role. */
export const getShoppingList = async (facilityId) => {
  const response = await apiClient.get(`${stockBase(facilityId)}shopping-list/`);
  return response.data;
};

const SUPPLIER_KEYS = ['name', 'url', 'note', 'is_active'];

/** `{results}`; `includeRetired` adds the retired suppliers. Any role. */
export const getSuppliers = async (facilityId, { includeRetired = false } = {}) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/suppliers/${includeRetired ? '?include_retired=1' : ''}`);
  return response.data;
};

/** Owner/Manager: `{name, url?, note?}` → the supplier + `warning`. */
export const createSupplier = async (facilityId, fields = {}) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/suppliers/`, pickKeys(fields, SUPPLIER_KEYS));
  return response.data;
};

/** Owner/Manager: any of `{name, url, note, is_active}` (retire = false). */
export const updateSupplier = async (facilityId, supplierId, changes = {}) => {
  const response = await apiClient.patch(
    `facility/facilities/${facilityId}/suppliers/${supplierId}/`,
    pickKeys(changes, SUPPLIER_KEYS));
  return response.data;
};

export const PURCHASE_KEYS = [
  'stock_item', 'supplier', 'purchased_on', 'quantity', 'unit_price',
  'total', 'url', 'note', 'received_on',
];

/** Owner/Manager ONLY (Staff 403). `{results}`, newest first. */
export const getPurchases = async (facilityId, { stockItem } = {}) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/purchases/${stockItem ? `?stock_item=${encodeURIComponent(stockItem)}` : ''}`);
  return response.data;
};

/** Owner/Manager. Only `PURCHASE_KEYS` reach the wire. Answers
 * `{purchase, message, item}`. */
export const createPurchase = async (facilityId, fields = {}) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/purchases/`, pickKeys(fields, PURCHASE_KEYS));
  return response.data;
};

/** Owner/Manager. `receivedOn` (YYYY-MM-DD) is sent only when given — the
 * server's own today otherwise. */
export const receivePurchase = async (facilityId, purchaseId, { receivedOn } = {}) => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/purchases/${purchaseId}/receive/`,
    receivedOn ? { received_on: receivedOn } : {});
  return response.data;
};

/** Owner/Manager. A void, never a delete: `{reason}`. */
export const voidPurchase = async (facilityId, purchaseId, reason = '') => {
  const response = await apiClient.post(
    `facility/facilities/${facilityId}/purchases/${purchaseId}/void/`, { reason });
  return response.data;
};

// =============================================================================
// NOTIFICATIONS (R-NOTIFY, spec §5.5) — the bell, its list, Settings
// =============================================================================

const notifyBase = (facilityId) => `facility/facilities/${facilityId}/notifications/`;

/** The caller's OWN notifications, newest first:
 * `{count, page, page_size, num_pages, next, unread, results}`. */
export const getNotifications = async (facilityId, { page, pageSize } = {}) => {
  const search = new URLSearchParams();
  if (page) search.append('page', page);
  if (pageSize) search.append('page_size', pageSize);
  const query = search.toString();
  const response = await apiClient.get(`${notifyBase(facilityId)}${query ? `?${query}` : ''}`);
  return response.data;
};

/** `{unread}` — the bell's badge. */
export const getUnreadNotificationCount = async (facilityId) => {
  const response = await apiClient.get(`${notifyBase(facilityId)}unread-count/`);
  return response.data;
};

export const markNotificationRead = async (facilityId, notificationId) => {
  const response = await apiClient.post(`${notifyBase(facilityId)}${notificationId}/read/`, {});
  return response.data;
};

export const markAllNotificationsRead = async (facilityId) => {
  const response = await apiClient.post(`${notifyBase(facilityId)}read-all/`, {});
  return response.data;
};

/** `{preferences: [{kind, label, channel, enabled, default}], line}`. */
export const getNotificationPreferences = async (facilityId) => {
  const response = await apiClient.get(`${notifyBase(facilityId)}preferences/`);
  return response.data;
};

/** One switch: `{kind, channel, enabled}` (email is the only channel). */
export const setNotificationPreference = async (facilityId, { kind, channel = 'email', enabled }) => {
  const response = await apiClient.patch(
    `${notifyBase(facilityId)}preferences/`, { kind, channel, enabled });
  return response.data;
};

/** `{digest_hour, can_edit}` — the facility's morning email hour. */
export const getDigestHour = async (facilityId) => {
  const response = await apiClient.get(`${notifyBase(facilityId)}digest-hour/`);
  return response.data;
};

/** Owner/Manager: a whole hour 0–23, sent as a NUMBER. */
export const setDigestHour = async (facilityId, hour) => {
  const response = await apiClient.patch(
    `${notifyBase(facilityId)}digest-hour/`, { digest_hour: Number(hour) });
  return response.data;
};

/** R-TRAINING T6 — `{review_hour, can_edit}`: the afternoon review email's
 * hour (the twin of the digest hour; any role reads). */
export const getReviewHour = async (facilityId) => {
  const response = await apiClient.get(`${notifyBase(facilityId)}review-hour/`);
  return response.data;
};

/** Owner/Manager: a whole hour 0–23, sent as a NUMBER. */
export const setReviewHour = async (facilityId, hour) => {
  const response = await apiClient.patch(
    `${notifyBase(facilityId)}review-hour/`, { review_hour: Number(hour) });
  return response.data;
};

// =============================================================================
// TRAINING (R-TRAINING slice 1, spec §8) — the register, sign-offs, the
// assignment check, the review, the membership details and Froggy U.
// Every refusal is the server's sentence (`detail` / `error`).
// =============================================================================

const trainingBase = (facilityId) => `facility/facilities/${facilityId}/training/`;

/** A query string from the keys that HAVE a value ('' when none). */
const queryOf = (pairs) => {
  const search = new URLSearchParams();
  pairs.forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') search.append(key, value);
  });
  const query = search.toString();
  return query ? `?${query}` : '';
};

/** A register target key (`template:<id>` | `routine:<id>` |
 * `category:feeding`) → the ONE body key the server reads. */
export const trainingTargetBody = (targetKey) => {
  const [kind, ...rest] = String(targetKey || '').split(':');
  const id = rest.join(':');
  if (kind === 'template' || kind === 'routine' || kind === 'category') {
    return { [kind]: id };
  }
  return {};
};

/** `{targets, keepers, cells, can_void, can_sign_everything}` — the grid,
 * computed server-side (a member sees their own row; Owner/Manager all; a
 * trainer the keepers they trained). */
export const getTrainingRegister = async (facilityId) => {
  const response = await apiClient.get(trainingBase(facilityId));
  return response.data;
};

/** Sign a keeper off on ONE target, at most one scope:
 * `{keeper, template|routine|category, scope_kind, scope_id, note}`. No
 * scope sends `scope_kind: 'none'` and no id. → `{signoff, already,
 * sentence, cell}`. */
export const signOffTraining = async (facilityId, {
  keeper, target, scopeKind = 'none', scopeId = null, note = '',
}) => {
  const none = !scopeKind || scopeKind === 'none';
  const body = {
    keeper,
    ...trainingTargetBody(target),
    scope_kind: none ? 'none' : scopeKind,
    scope_id: none ? null : (scopeId || null),
    note: note || '',
  };
  const response = await apiClient.post(`${trainingBase(facilityId)}signoffs/`, body);
  return response.data;
};

/** Owner only: one no-scope sign-off per target in use, each voidable.
 * → `{signoffs, written, sentence}`. */
export const signOffTrainingEverything = async (facilityId, { keeper, note = '' }) => {
  const response = await apiClient.post(
    `${trainingBase(facilityId)}signoffs/everything/`, { keeper, note: note || '' });
  return response.data;
};

/** Owner/Manager: a void, never a delete — `{reason}` is required. */
export const voidTrainingSignoff = async (facilityId, signoffId, reason) => {
  const response = await apiClient.post(
    `${trainingBase(facilityId)}signoffs/${signoffId}/void/`, { reason: reason || '' });
  return response.data;
};

/** Owner/Manager, read-only: `{covered, sentence}` for a keeper and a card
 * (`task`) or a target (`template` | `routine` | `category`). */
export const checkTraining = async (facilityId, {
  keeper, task, template, routine, category, at,
} = {}) => {
  const response = await apiClient.get(`${trainingBase(facilityId)}check/${queryOf([
    ['keeper', keeper], ['task', task], ['template', template],
    ['routine', routine], ['category', category], ['at', at],
  ])}`);
  return response.data;
};

/** Owner/Manager: `{since, items: [{at, kind, keeper_id, text, link}]}` —
 * today's review (`days` 1–14 widens it; `keeper` narrows it). */
export const getTrainingReview = async (facilityId, { days, keeper } = {}) => {
  const response = await apiClient.get(
    `${trainingBase(facilityId)}review/${queryOf([['days', days], ['keeper', keeper]])}`);
  return response.data;
};

const membersBase = (facilityId) => `facility/facilities/${facilityId}/members/`;

/** Owner/Manager: every member's details row — `[{user_id, name, role,
 * is_junior, birth_date, age, gender, trainer_note, name_pronunciation}]`.
 * Never on a customer surface. */
export const getMembersDetails = async (facilityId) => {
  const response = await apiClient.get(`${membersBase(facilityId)}details/`);
  return response.data;
};

/** Owner/Manager: one member's details row. */
export const getMemberDetails = async (facilityId, userId) => {
  const response = await apiClient.get(`${membersBase(facilityId)}${userId}/details/`);
  return response.data;
};

const MEMBER_DETAIL_KEYS = ['is_junior', 'birth_date', 'gender', 'trainer_note', 'name_pronunciation'];

/** Owner only: PATCH exactly the keys given, of the five the server
 * reads (`is_junior`, `birth_date`, `gender`, `trainer_note`,
 * `name_pronunciation`). */
export const updateMemberDetails = async (facilityId, userId, changes = {}) => {
  const body = {};
  MEMBER_DETAIL_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(changes, key)) body[key] = changes[key];
  });
  const response = await apiClient.patch(`${membersBase(facilityId)}${userId}/details/`, body);
  return response.data;
};

/** Owner only (Q-add-a-keeper-page): add a person who has signed in once,
 * by email, with a role (Owner | Manager | Staff) and the details. POSTs
 * `email`, `role` and only the detail keys given; answers 201 with the
 * member as `getMembersDetails` lists them, plus `sentence`. */
export const addFacilityMember = async (facilityId, body = {}) => {
  const wire = { email: body.email, role: body.role };
  MEMBER_DETAIL_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(body, key)) wire[key] = body[key];
  });
  const response = await apiClient.post(membersBase(facilityId), wire);
  return response.data;
};

/** Froggy U: `{landing, read_only, my_day, my_day_line, my_training,
 * exempt, trainer_today}` for the caller — or, with `as`, for that keeper
 * (Owner/Manager only, read-only). */
export const getFroggyU = async (facilityId, { as } = {}) => {
  const response = await apiClient.get(
    `facility/facilities/${facilityId}/froggy-u/${queryOf([['as', as]])}`);
  return response.data;
};
